import { useState, useEffect, useCallback, useRef, lazy, Suspense } from "react";
import { supabase } from "./supabase";
import { DemoModeBanner } from "./DemoModeBanner";
import { computeKeywordScore } from "./AIMatchEngine";
import { sendWelcomeEmail, sendApplicationReceived } from "./EmailNotifications";
import { initPushNotifications } from "./PushNotifications";
import { useInactivityTimeout } from "./useInactivityTimeout";

// Eagerly loaded — needed on first render
import AIJobMatchFeed from "./AIJobMatchFeed";
import StudentHome from "./StudentHome";
import OnboardingFlow from "./OnboardingFlow";

// Used within MainApp components (kept static — infrequently rendered)
import { MatchBadge, MatchBreakdown } from "./MatchScoreUI";
import { CompanyReviewsDisplay, WriteReviewModal } from "./CompanyReviews";
import { InterviewSchedulerStudent } from "./InterviewScheduler";
import { PremiumGate, PremiumBlur } from "./PremiumGate";
import MobileStudentApp from "./MobileStudentApp";

// Lazy loaded — only downloaded when student navigates to that section
const ProfilePage            = lazy(() => import("./ProfilePage"));
const OJTLogbook             = lazy(() => import("./OJTLogbook"));
const OJTProgress            = lazy(() => import("./OJTProgress"));
const MessagingPanel         = lazy(() => import("./MessagingPanel"));
const ReferralSystem         = lazy(() => import("./ReferralSystem"));

// ── In-app notification handler for push events ──────────────────────────────
// Listens for push notification events fired from PushNotifications.js
// and shows them as toasts when the app is open

// Inline spinner for lazy section loads
function SectionLoader() {
  return (
    <div style={{ padding: 60, textAlign: "center" }}>
      <div style={{ width: 28, height: 28, border: "3px solid #e2e8e5",
        borderTopColor: "#0a6e4e", borderRadius: "50%",
        animation: "spin .7s linear infinite", display: "inline-block" }} />
      <style>{"@keyframes spin{to{transform:rotate(360deg)}}"}</style>
    </div>
  );
}



// ── Google Analytics GA4 ──────────────────────────────────────────────────
const GA4_ID = "G-XXXXXXXXXX"; // Replace with your actual GA4 Measurement ID
function initGA4() {
  if (window.gtag || !GA4_ID || GA4_ID === "G-XXXXXXXXXX") return;
  const script = document.createElement("script");
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
  script.async = true;
  document.head.appendChild(script);
  window.dataLayer = window.dataLayer || [];
  window.gtag = function(){ window.dataLayer.push(arguments); };
  window.gtag("js", new Date());
  window.gtag("config", GA4_ID, { page_path: window.location.pathname });
}

export function trackEvent(action, category, label, value) {
  if (window.gtag) {
    window.gtag("event", action, {
      event_category: category,
      event_label:    label,
      value:          value,
    });
  }
}



// ─── THEME ────────────────────────────────────────────────────────────────────

// ── Inline EmptyState (shared pattern) ───────────────────────────────────────
const SkeletonCard = () => (
  <>
  <style>{`@keyframes skeletonShimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} } .skeleton{background:linear-gradient(90deg,#e8ede9 25%,#f4f6f5 50%,#e8ede9 75%);background-size:200% 100%;animation:skeletonShimmer 1.4s infinite;border-radius:6px;}`}</style>
  <div style={{ background:"#fff", borderRadius:14, padding:"18px 20px", border:"1px solid #e2e8e5" }}>
    <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:14 }}>
      <div className="skeleton" style={{ width:40, height:40, borderRadius:10 }} />
      <div style={{ flex:1 }}>
        <div className="skeleton" style={{ width:"60%", height:14, marginBottom:6 }} />
        <div className="skeleton" style={{ width:"40%", height:11 }} />
      </div>
    </div>
    <div className="skeleton" style={{ width:"100%", height:11, marginBottom:8 }} />
    <div className="skeleton" style={{ width:"100%", height:11, marginBottom:8 }} />
    <div className="skeleton" style={{ width:"50%", height:11 }} />
  </div>
  </>
);
const SkeletonList = ({ count=4 }) => (
  <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
    {Array.from({length:count}).map((_,i)=><SkeletonCard key={i} />)}
  </div>
);

const EmptyState = ({ icon="📭", title, desc, action }) => (
  <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"52px 24px", textAlign:"center" }}>
    <div style={{ fontSize:48, marginBottom:14, lineHeight:1 }}>{icon}</div>
    <div style={{ fontFamily:"'Fraunces',serif", fontSize:18, fontWeight:800, color:"#0f1923", marginBottom:6 }}>{title}</div>
    {desc && <div style={{ fontSize:13, color:"#6b7a8d", maxWidth:320, lineHeight:1.7, marginBottom:action?18:0 }}>{desc}</div>}
    {action && <button onClick={action.onClick} style={{ background:"#0a6e4e", color:"#fff", border:"none", borderRadius:10, padding:"9px 20px", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>{action.label}</button>}
  </div>
);

const T = {
  primary: "#0a6e4e",      // deep forest green — PH flag green feel
  primaryD: "#084f38",
  primaryL: "#e8f5f0",
  accent: "#f5a623",       // golden amber — PH sun
  accentD: "#d4891a",
  accentL: "#fef9ed",
  ink: "#0f1923",
  inkM: "#2d3d4f",
  inkL: "#6b7a8d",
  surface: "#f7f9f8",
  white: "#ffffff",
  border: "#e2e8e5",
  green: "#16a34a", greenL: "#dcfce7",
  blue: "#1d6fcc", blueL: "#dbeafe",
  red: "#dc2626", redL: "#fee2e2",
  amber: "#d97706", amberL: "#fef3c7",
  purple: "#7c3aed", purpleL: "#f5f3ff",
  shadow: "0 2px 16px rgba(10,110,78,.08)",
  shadowLg: "0 8px 40px rgba(10,110,78,.13)",
};

const GLOBAL_CSS = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'DM Sans', sans-serif; background: ${T.surface}; color: ${T.ink}; }
  ::-webkit-scrollbar { width: 8px; height: 8px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: #c8d5cf; border-radius: 8px; border: 2px solid transparent; background-clip: padding-box; }
  ::-webkit-scrollbar-thumb:hover { background: #9ab5a8; border: 2px solid transparent; background-clip: padding-box; }
  * { scrollbar-width: thin; scrollbar-color: #c8d5cf transparent; }
  input, select, textarea, button { font-family: inherit; }

  @keyframes fadeUp { from { opacity:0; transform:translateY(18px); } to { opacity:1; transform:translateY(0); } }
  @keyframes fadeIn { from { opacity:0; } to { opacity:1; } }
  @keyframes slideIn { from { opacity:0; transform:translateX(20px); } to { opacity:1; transform:translateX(0); } }
  @keyframes spin { to { transform:rotate(360deg); } }
  @keyframes ticker { 0% { transform:translateX(0); } 100% { transform:translateX(-50%); } }
  @keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:.35; } }
  @keyframes shimmer { 0% { background-position:-400px 0; } 100% { background-position:400px 0; } }
  @keyframes floatA { 0%,100% { transform:translateY(0px); } 50% { transform:translateY(-10px); } }
  @keyframes floatB { 0%,100% { transform:translateY(0px) rotate(0deg); } 50% { transform:translateY(-7px) rotate(2deg); } }

  .fade-up { animation: fadeUp .5s cubic-bezier(.22,1,.36,1) both; }
  .fade-in { animation: fadeIn .3s ease both; }
  .slide-in { animation: slideIn .4s cubic-bezier(.22,1,.36,1) both; }
  .spin { animation: spin .75s linear infinite; }
  .pulse-dot { animation: pulse 2s ease infinite; }
  .float-a { animation: floatA 4s ease-in-out infinite; }
  .float-b { animation: floatB 5s ease-in-out infinite 1.2s; }
  .ticker-inner { display:inline-flex; animation: ticker 34s linear infinite; white-space:nowrap; }

  .btn { border:none; cursor:pointer; font-weight:600; transition:all .18s; display:inline-flex; align-items:center; justify-content:center; gap:6px; letter-spacing:-.01em; }
  .btn-primary { background:${T.primary}; color:#fff; border-radius:10px; padding:11px 24px; font-size:14px; }
  .btn-primary:hover { background:${T.primaryD}; transform:translateY(-1px); box-shadow:0 6px 20px rgba(10,110,78,.22); }
  .btn-accent { background:${T.accent}; color:${T.ink}; border-radius:10px; padding:11px 24px; font-size:14px; font-weight:700; }
  .btn-accent:hover { background:${T.accentD}; transform:translateY(-1px); box-shadow:0 6px 20px rgba(245,166,35,.3); }
  .btn-ghost { background:transparent; color:${T.inkM}; border:1.5px solid ${T.border}; border-radius:10px; padding:10px 22px; font-size:14px; }
  .btn-ghost:hover { border-color:${T.primary}; color:${T.primary}; }
  .btn-outline { background:transparent; color:${T.primary}; border:1.5px solid ${T.primary}; border-radius:10px; padding:10px 22px; font-size:14px; }
  .btn-outline:hover { background:${T.primary}; color:#fff; }
  .btn-sm { padding:7px 16px; font-size:12px; border-radius:8px; }
  .btn-xs { padding:5px 12px; font-size:11px; border-radius:6px; }

  .input { width:100%; border:1.5px solid ${T.border}; border-radius:10px; padding:11px 14px; font-size:14px; color:${T.ink}; background:#fff; outline:none; transition:border-color .2s, box-shadow .2s; }
  .input:focus { border-color:${T.primary}; box-shadow:0 0 0 3px rgba(10,110,78,.1); }
  select.input { cursor:pointer; }
  label { font-size:13px; font-weight:600; color:${T.inkM}; display:block; margin-bottom:6px; }

  .card { background:#fff; border:1.5px solid ${T.border}; border-radius:14px; }
  .tag { background:${T.surface}; color:${T.inkL}; border:1px solid ${T.border}; font-size:11px; font-weight:600; padding:3px 10px; border-radius:100px; }
  .badge { font-size:11px; font-weight:700; padding:3px 10px; border-radius:100px; display:inline-flex; align-items:center; gap:5px; }

  .modal-overlay { position:fixed; inset:0; background:rgba(10,30,20,.5); backdrop-filter:blur(8px); z-index:200; display:flex; align-items:center; justify-content:center; padding:20px; animation:fadeIn .2s ease; }
  .modal { background:#fff; border-radius:20px; padding:32px; max-width:520px; width:100%; max-height:90vh; overflow-y:auto; animation:fadeUp .3s cubic-bezier(.22,1,.36,1); }

  .nav-item { color:${T.inkL}; font-size:14px; font-weight:500; cursor:pointer; padding:6px 0; text-decoration:none; transition:color .18s; position:relative; }
  .nav-item::after { content:''; position:absolute; bottom:-2px; left:0; width:0; height:2px; background:${T.accent}; border-radius:2px; transition:width .2s; }
  .nav-item:hover, .nav-item.active { color:${T.ink}; }
  .nav-item:hover::after, .nav-item.active::after { width:100%; }

  .sidebar-item { display:flex; align-items:center; gap:10px; padding:10px 14px; border-radius:10px; cursor:pointer; color:${T.inkM}; font-size:14px; font-weight:500; transition:all .18s; }
  .sidebar-item:hover { background:${T.surface}; color:${T.ink}; }
  .sidebar-item.active { background:${T.primaryL}; color:${T.primary}; font-weight:700; }

  .job-card { background:#fff; border:1.5px solid ${T.border}; border-radius:16px; padding:20px; cursor:pointer; transition:all .22s; position:relative; overflow:hidden; }
  .job-card::before { content:''; position:absolute; top:0; left:0; right:0; height:3px; background:var(--ac); transform:scaleX(0); transform-origin:left; transition:transform .28s; }
  .job-card:hover { border-color:var(--ac); transform:translateY(-3px); box-shadow:0 10px 32px rgba(0,0,0,.08); }
  .job-card:hover::before { transform:scaleX(1); }

  .stat-box { background:#fff; border:1.5px solid ${T.border}; border-radius:14px; padding:20px; transition:transform .2s, box-shadow .2s; }
  .stat-box:hover { transform:translateY(-2px); box-shadow:${T.shadow}; }
  .shimmer { background:linear-gradient(90deg,#eef1ef 25%,#e4eae7 50%,#eef1ef 75%); background-size:400px 100%; animation:shimmer 1.4s infinite; border-radius:8px; }
  .divider { border:none; border-top:1px solid ${T.border}; margin:18px 0; }
  .plan-card { border:2px solid ${T.border}; border-radius:16px; padding:26px; cursor:pointer; transition:all .22s; background:#fff; }
  .plan-card:hover { border-color:${T.primary}; box-shadow:${T.shadowLg}; }
  .plan-card.featured { border-color:${T.primary}; background:${T.primaryL}; }

  /* ═══ MOBILE RESPONSIVE ═══════════════════════════════════════════════════ */
  .hide-mobile     { }
  .show-mobile-flex { display: none !important; }
  @media (max-width: 768px) {
    .hide-mobile        { display: none !important; }
    .show-mobile-flex   { display: flex !important; }
    .desktop-nav-links  { display: none !important; }
    .modal              { padding: 20px 16px 90px !important; border-radius: 20px 20px 0 0 !important; max-height:92vh !important; }
    .modal-overlay      { padding: 8px !important; align-items: flex-end !important; }
  }
`;

// ─── MOCK DATA ────────────────────────────────────────────────────────────────
const STATUS_META = {
  new:         { label:"New",         bg:T.blueL,   color:T.blue,    dot:T.blue },
  reviewing:   { label:"Reviewing",   bg:T.amberL,  color:T.amber,   dot:T.amber },
  shortlisted: { label:"Shortlisted", bg:T.purpleL, color:T.purple,  dot:T.purple },
  hired:       { label:"Hired",       bg:T.greenL,  color:T.green,   dot:T.green },
  rejected:    { label:"Rejected",    bg:T.redL,    color:T.red,     dot:T.red },
};

// ─── CLAUDE API ────────────────────────────────────────────────────────────────
async function claudeAPI(system, user) {
  // ✅ Routes through Supabase Edge Function proxy — API key never exposed to browser
  const res = await fetch("https://sghmpmjszcuvfdinsbxq.supabase.co/functions/v1/anthropic-proxy", {
    method:"POST",
    headers:{ "Content-Type":"application/json" },
    body: JSON.stringify({ system, messages:[{role:"user",content:user}], max_tokens:1000 }),
  });
  const data = await res.json();
  return data.content?.[0]?.text || "";
}

async function fetchRealJobs(country, query) {
  try {
    const searchQuery = query
      ? `${query} internship ${country === "PH" ? "philippines" : "usa"}`
      : `OJT internship ${country === "PH" ? "philippines" : "usa"}`;

    // ✅ Routes through Supabase proxy — RapidAPI key never exposed to browser
    const url = `https://sghmpmjszcuvfdinsbxq.supabase.co/functions/v1/jsearch-proxy?query=${encodeURIComponent(searchQuery)}&country=${country}`;
    const response = await fetch(url, { method:"GET" });

    if (!response.ok) {
      return [];
    }

    const data = await response.json();

    const jobs = data.data || data.results || data.jobs || [];
    if (jobs.length === 0) {
      return [];
    }

    return jobs.map((job, i) => ({
      id: job.job_id || `job-${i}`,
      title: job.job_title || "Internship Role",
      company: job.employer_name || "Company",
      location: [job.job_city, job.job_state, job.job_country].filter(Boolean).join(", "),
      country: country,
      salary: job.job_min_salary
        ? `$${Math.round(job.job_min_salary)}–$${Math.round(job.job_max_salary)}/yr`
        : country === "PH" ? "Salary negotiable" : "Salary not listed",
      tags: (job.job_required_skills || []).slice(0, 3).length > 0
        ? (job.job_required_skills || []).slice(0, 3)
        : job.job_title.split(" ").slice(0, 2).concat(["Internship"]),
      source: job.job_publisher || "JSearch",
      sourceColor: "#e8523a",
      flag: country === "PH" ? "🇵🇭" : "🇺🇸",
      accentColor: i % 3 === 0 ? T.primary : i % 3 === 1 ? T.blue : T.accent,
      posted: job.job_posted_at_datetime_utc
        ? new Date(job.job_posted_at_datetime_utc).toLocaleDateString()
        : "Recently",
      type: job.job_employment_type || "Full-time",
      duration: "3 months",
      applicants: Math.floor(Math.random() * 80) + 10,
      desc: (job.job_description || "").slice(0, 300) + "...",
      applyLink: job.job_apply_link || "#",
    }));
  } catch (err) {
    console.error("fetchRealJobs error:", err);
    return [];
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// ROOT APP
// ═══════════════════════════════════════════════════════════════════════════════
export default function MainApp({ initialPage }) {
  const [page,          setPage]          = useState(initialPage || "home");
  const [sessionChecked, setSessionChecked] = useState(false);
  const [user,          setUser]          = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [scrollTarget,  setScrollTarget]  = useState(null);
  const [profileTab,    setProfileTab]    = useState(null);
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const [showResetModal, setShowResetModal] = useState(false);
  const [studentProfile, setStudentProfile] = useState(null);
  const [checklistDismissed, setChecklistDismissed] = useState(false);
  const [showPremium, setShowPremium] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const [isMobile,  setIsMobile]  = useState(window.innerWidth <= 768);
  const [applyJob, setApplyJob] = useState(null);
  const [showAlerts, setShowAlerts] = useState(false);
  const [applications, setApplications] = useState([]);
  const [cvProfile, setCvProfile] = useState(null);
  const [savedJobs, setSavedJobs] = useState([]);
  const [notification, setNotification] = useState(null);
  const [showInactivityWarning, setShowInactivityWarning] = useState(false);

  const notify = (msg, type="success") => {
    setNotification({ msg, type });
    setTimeout(() => setNotification(null), 3200);
  };

  // 1-hour inactivity timeout for students and university staff
  useInactivityTimeout({
    user: user && ["student","coordinator","department_head","university_admin"].includes(user.role) ? user : null,
    onWarning: () => setShowInactivityWarning(true),
    onLogout: () => {
      setUser(null);
      window.location.replace("/");
    },
  });

  // Restore session on page load
  useEffect(() => {
    let authTimeout; // defined but not used for redirect anymore

    // Handle OAuth callback — clean URL hash (onAuthStateChange handles the session)
    if (window.location.hash.includes("access_token")) {
      window.history.replaceState({}, "", window.location.pathname);
    }

    // Detect password reset redirect (Supabase lands user back with session)
    const params = new URLSearchParams(window.location.search);
    if (params.get("reset") === "1") {
      setShowResetModal(true);
      // Clean URL
      window.history.replaceState({}, "", window.location.pathname);
    }
    // Auto-open auth modal from landing page Sign In / Sign Up
    const authParam = params.get("auth");
    if (authParam === "login") {
      setAuthMode("login"); setShowAuth(true);
      window.history.replaceState({}, "", window.location.pathname);
    } else if (authParam === "signup") {
      setAuthMode("signup"); setShowAuth(true);
      window.history.replaceState({}, "", window.location.pathname);
    }
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === "TOKEN_REFRESHED") return;

      if (event === "INITIAL_SESSION") {
        if (session?.user) {
          const u = session.user;
          const fallbackRole = u.user_metadata?.role || "student";
          const fallbackName = u.user_metadata?.full_name || u.email.split("@")[0];
          setUser({ id: u.id, name: fallbackName, email: u.email, role: fallbackRole });
          setPage("home");
          setSessionChecked(true);
          supabase.from("profiles").select("role, full_name, is_premium").eq("id", u.id).maybeSingle().then(({ data: prof }) => {
            if (prof) {
              const role = prof.role || fallbackRole;
              setUser({ id: u.id, name: prof.full_name || fallbackName, email: u.email, role });
              if (prof.is_premium) setIsPremium(true);
              if (role === "student") loadStudentProfile(u.id, prof);
              if (role === "employer") { window.location.href = "/employer"; return; }
              if (["coordinator","department_head","university_admin"].includes(role)) { window.location.href = "/university"; return; }
            } else {
              if (fallbackRole === "student") loadStudentProfile(u.id);
            }
            initPushNotifications(supabase, u.id);
          });
        } else {
          setSessionChecked(true);
          window.location.replace("/");
        }
        return;
      }

      if (session?.user) {
        const u = session.user;
        const { data: prof } = await supabase.from("profiles").select("role, full_name").eq("id", u.id).single();

        // If new Google user — create profile and record consent
        if (!prof && u.app_metadata?.provider === "google") {
          await supabase.from("profiles").upsert({
            id:        u.id,
            email:     u.email,
            full_name: u.user_metadata?.full_name || u.email.split("@")[0],
            role:      "student",
            terms_agreed_at:   new Date().toISOString(),
            privacy_agreed_at: new Date().toISOString(),
          });
        }

        const role = prof?.role || u.user_metadata?.role || "student";
        setUser({
          id:   u.id,
          name: prof?.full_name || u.user_metadata?.full_name || u.email.split("@")[0],
          email: u.email,
          role
        });

        // On sign-in, update page state (only if not already logged in)
        if (event === "SIGNED_IN") {
          if (role === "employer")         { window.location.href = "/employer"; return; }
          if (role === "coordinator" ||
              role === "department_head" ||
              role === "university_admin") { window.location.href = "/university"; return; }
          // Don't reset page if already on dashboard
          setPage(p => p === "home" ? p : "home");
        }
      } else if (event === "SIGNED_OUT") {
        setUser(null);
        window.location.replace("/");
      }
    });
    // Init GA4
    initGA4();
    // Mobile resize detection
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    // Page navigation events from child components
    const handleSetPage = (e) => { setPage(e.detail); if(window.gtag) window.gtag("event","page_view",{page_title:e.detail}); };
    window.addEventListener("ojthub:setPage", handleSetPage);
    return () => {
      subscription.unsubscribe();
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("ojthub:setPage", handleSetPage);
    };
  }, []);

  const loadStudentProfile = async (userId, existingProfile = null) => {
  const [appRes, docRes, endRes, logRes] = await Promise.all([
    supabase.from("applications").select("id").eq("student_id", userId).limit(1),
    supabase.from("student_documents").select("id").eq("student_id", userId).limit(1),
    supabase.from("endorsements").select("id, status").eq("student_id", userId).limit(1),
    supabase.from("logbook_entries").select("id").eq("student_id", userId).limit(1),
  ]);

  let p = existingProfile;
  if (!p) {
    const { data } = await supabase.from("profiles").select("*").eq("id", userId).single();
    p = data || {};
  }

  if (p.is_premium) setIsPremium(true);
  setStudentProfile({
    ...p,
    has_avatar:      !!p.avatar_url,
    has_bio:         !!(p.bio && p.bio.trim()),
    has_skills:      !!(p.skills && p.skills.length > 0),
    has_education:   !!(p.education && p.education.trim()),
    has_university:  !!p.university_id,
    has_applied:     (appRes.data||[]).length > 0,
    has_document:    (docRes.data||[]).length > 0,
    has_endorsement: (endRes.data||[]).length > 0,
    has_logbook:     (logRes.data||[]).length > 0,
  });
};

  const login = (u) => {
    setUser(u);
    // Show onboarding for brand new student accounts
    if (u.isNew && u.role === "student") setShowOnboarding(true);
    setShowAuth(false);
    if (u.role === "student") loadStudentProfile(u.id, null);
    // Register push notification token
    initPushNotifications(supabase, u.id);
    if (u.role === "employer")          { window.location.href = "/employer";   return; }
    if (u.role === "coordinator")       { window.location.href = "/university"; return; }
    if (u.role === "department_head")   { window.location.href = "/university"; return; }
    if (u.role === "university_admin")  { window.location.href = "/university"; return; }
    // student — stay on current page if already on a valid tab, else go home
    // page is always dashboard in /app
    notify(`Maligayang pagdating, ${u.name.split(" ")[0]}! 🇵🇭`);
  };
  const logout = async () => {
  sessionStorage.setItem("ojthub_signout", "1");
  await supabase.auth.signOut();
  window.location.replace("/");
};

  const openApply = (job) => {
    if (!user) { setShowAuth(true); setAuthMode("login"); return; }
    setApplyJob(job);
  };

  const applyToJob = async (job, coverNote = "") => {
    if (!user) { setShowAuth(true); setAuthMode("login"); return; }
    // Always save to DB — both direct and external jobs
    // Store job_title + company_name directly so they show even for external jobs
    const appPayload = {
      student_id:   user.id,
      job_id:       job.id || ("ext_" + Date.now()),
      status:       "pending",
      cover_note:   coverNote,
      job_title:    job.title || job.job_title || "",
      company_name: job.company || job.company_name || "",
      job_location: job.location || "",
      job_source:   job.source || "external",
    };
    const { error } = await supabase.from("applications").insert(appPayload);
    if (error) {
      if (error.code === "23505") { notify("You already applied to this job."); return; }
      // If column doesn't exist yet, try without extra columns
      if (error.code === "42703") {
        const { error: err2 } = await supabase.from("applications").insert({
          student_id: user.id,
          job_id:     appPayload.job_id,
          status:     "pending",
          cover_note: coverNote,
        });
        if (err2) { notify("Failed to apply: " + err2.message); return; }
      } else {
        notify("Failed to apply: " + error.message); return;
      }
    }
    if (applications.find(a => a.jobId===job.id)) { notify("You already applied to this job."); return; }
    setApplications(prev => [...prev, { id:"a"+Date.now(), name:user.name, email:user.email, jobId:job.id, jobTitle:job.title, company:job.company, status:"pending", appliedAt:new Date().toISOString().split("T")[0] }]);
    notify(`Applied to ${job.title}! ✓`);
    // Notify employer of new application
    if (job.employer_email) {
      sendApplicationReceived(supabase, {
        employerEmail: job.employer_email,
        employerName:  job.company || job.company_name,
        studentName:   user.name,
        jobTitle:      job.title,
      });
    }
    if(window.gtag) window.gtag("event","apply_job",{event_category:"engagement",event_label:job.title});
  };

  // Show spinner only while waiting for INITIAL_SESSION
  if (!sessionChecked) return (
    <div style={{ minHeight:"100vh", display:"flex", alignItems:"center",
      justifyContent:"center", background:"#f7f9f8" }}>
      <div style={{ textAlign:"center" }}>
        <img src="/OJT_logo.png" alt="OJT Hub"
          style={{ height:56, objectFit:"contain", opacity:.7, marginBottom:16 }}/>
        <div style={{ width:32, height:32, margin:"0 auto",
          border:"3px solid #e2e8e5", borderTopColor:"#0a6e4e",
          borderRadius:"50%", animation:"spin .7s linear infinite" }}/>
        <style>{"@keyframes spin{to{transform:rotate(360deg)}}"}</style>
      </div>
    </div>
  );

  return (
    <>
      <style>{GLOBAL_CSS}</style>
      {notification && (
        <div style={{ position:"fixed",top:20,left:"50%",transform:"translateX(-50%)",zIndex:999, background:notification.type==="success"?T.primary:notification.type==="warn"?T.amber:T.red, color:"#fff",padding:"11px 24px",borderRadius:100,fontSize:14,fontWeight:700,boxShadow:T.shadowLg,animation:"fadeUp .3s ease",whiteSpace:"nowrap" }}>
          {notification.msg}
        </div>
      )}
      {showResetModal && <ResetPasswordModal onClose={()=>setShowResetModal(false)} onDone={()=>{ setShowResetModal(false); setShowAuth(true); setAuthMode("login"); }} />}
      {showInactivityWarning && (
        <div style={{ position:"fixed", bottom:24, right:24, zIndex:999,
          background:"#fff", border:"2px solid #f5a623", borderRadius:14,
          padding:"18px 22px", maxWidth:320,
          boxShadow:"0 8px 32px rgba(0,0,0,.15)" }}>
          <div style={{ fontSize:18, marginBottom:6 }}>⏱ Session Expiring</div>
          <div style={{ fontSize:13, color:"#2d3d4f", marginBottom:14, lineHeight:1.5 }}>
            You'll be logged out in <strong>5 minutes</strong> due to inactivity.
          </div>
          <button onClick={() => setShowInactivityWarning(false)}
            style={{ background:"#0a6e4e", color:"#fff", border:"none",
              borderRadius:8, padding:"8px 18px", fontWeight:700,
              fontSize:13, cursor:"pointer", fontFamily:"inherit" }}>
            Keep me logged in
          </button>
        </div>
      )}
      {showAuth && <AuthModal mode={authMode} setMode={setAuthMode} onLogin={login} onClose={()=>setShowAuth(false)} />}
      <DemoModeBanner role={studentProfile?.role || "student"} userEmail={user?.email} />
      <TopNav user={user} page={page} setPage={setPage} onLogin={()=>{setAuthMode("login");setShowAuth(true);}} onSignup={()=>{setAuthMode("signup");setShowAuth(true);}} onLogout={logout} isPremium={isPremium} onUpgrade={()=>setShowPremium(true)} notify={notify} />

      {/* Wrong-portal banner for university staff */}
      {user && ["coordinator","department_head","university_admin"].includes(user.role) && (
        <div style={{ background:"linear-gradient(135deg,#063d2b,#0a6e4e)", color:"#fff", padding:"14px clamp(16px,5vw,56px)", display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:12, marginTop:64, position:"sticky", top:64, zIndex:90, boxShadow:"0 2px 12px rgba(10,110,78,.2)" }}>
          <div style={{ display:"flex", alignItems:"center", gap:14 }}>
            <span style={{ fontSize:28 }}>{user.role === "university_admin" ? "🏫" : user.role === "coordinator" ? "🎓" : "🏢"}</span>
            <div>
              <div style={{ fontWeight:800, fontSize:14 }}>
                You're signed in as a {user.role === "university_admin" ? "University Admin" : user.role === "coordinator" ? "OJT Coordinator" : "Department Head"}
              </div>
              <div style={{ fontSize:12, opacity:.75 }}>Your portal has student management, endorsements, logbooks, and reports.</div>
            </div>
          </div>
          <button
            onClick={() => window.location.href = "/university"}
            style={{ background:"#fff", color:"#0a6e4e", border:"none", borderRadius:10, padding:"10px 22px", fontWeight:800, fontSize:14, cursor:"pointer", fontFamily:"inherit", flexShrink:0, boxShadow:"0 2px 8px rgba(0,0,0,.1)" }}>
            Go to My Portal →
          </button>
        </div>
      )}

      {/* OnboardingChecklist removed — StudentHome replaces it */}
      {showPremium && <StudentPremiumModal user={user} notify={notify} onClose={()=>setShowPremium(false)} onSuccess={()=>setIsPremium(true)} />}
      {showOnboarding && (
        <OnboardingFlow
          user={user}
          supabase={supabase}
          onComplete={() => {
            setShowOnboarding(false);
            loadStudentProfile(user.id);
            setPage("jobs");
            // Queue welcome email
            supabase.from("email_queue").insert({
              to_email: user.email,
              template: "welcome",
              data: { name: user.name, role: "student" },
              status: "pending",
            }).then(() => {});
          }}
        />
      )}
      {applyJob && <ApplyModal job={applyJob} user={user} cvProfile={cvProfile} onClose={()=>setApplyJob(null)} onApply={applyToJob} />}
      {showAlerts && <JobAlertsModal user={user} notify={notify} onClose={()=>setShowAlerts(false)} />}
      {/* Progress banner for logged-in students */}
      {user?.role === "student" && page === "jobs" && cvProfile && (() => {
        const req = cvProfile.required_hours || 486;
        const approved = cvProfile?.total_approved || cvProfile?.total_hours || 0;
        const pct = Math.min(100, Math.round((approved / req) * 100));
        return (
          <div onClick={() => window.location.href = "/progress"}
            style={{ margin:"68px 16px 0", padding:"12px 20px", background:`linear-gradient(135deg,#0a6e4e,#0d8f66)`,
              borderRadius:12, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"space-between",
              gap:12, transition:"opacity .2s" }}
            onMouseEnter={e=>e.currentTarget.style.opacity=".9"} onMouseLeave={e=>e.currentTarget.style.opacity="1"}>
            <div>
              <div style={{ fontSize:11, fontWeight:700, color:"rgba(255,255,255,.7)", marginBottom:1, textTransform:"uppercase", letterSpacing:".06em" }}>My OJT Progress</div>
              <div style={{ fontFamily:"'Fraunces',serif", fontSize:18, fontWeight:900, color:"#fff" }}>{approved}/{req} hrs approved · {pct}%</div>
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:12 }}>
              <div style={{ background:"rgba(255,255,255,.2)", borderRadius:100, width:80, height:7, overflow:"hidden" }}>
                <div style={{ height:"100%", borderRadius:100, background:"#fff", width:`${pct}%` }} />
              </div>
              <span style={{ fontSize:12, color:"rgba(255,255,255,.8)", fontWeight:700, whiteSpace:"nowrap" }}>View →</span>
            </div>
          </div>
        );
      })()}
      {isMobile && user?.role === "student" ? (
        <MobileStudentApp user={user} page={page} setPage={setPage} notify={notify}>
          <Suspense fallback={<SectionLoader />}>
          {page==="home"         && <StudentHome user={user} studentProfile={studentProfile} cvProfile={cvProfile} setPage={setPage} notify={notify} isPremium={isPremium} onUpgrade={()=>setShowPremium(true)} />}
          {page==="jobs"         && <AIJobMatchFeed user={user} supabase={supabase} notify={notify} onApply={openApply} onLoginPrompt={()=>{setAuthMode("login");setShowAuth(true);}} />}
          {page==="cv"           && (() => { setPage("profile"); setProfileTab("resume"); return null; })()}
          {page==="applications" && <MyApplications user={user} isPremium={isPremium} onUpgrade={()=>setShowPremium(true)} onLoginPrompt={()=>{setAuthMode("login");setShowAuth(true);}} />}
          {page==="referral"     && <ReferralSystem user={user} supabase={supabase} notify={notify} isPremium={isPremium} />}
          {page==="profile"      && <ProfilePage user={user} notify={notify} isPremium={isPremium} onUpgrade={()=>setShowPremium(true)} supabase={supabase} initialTab={profileTab} onTabLoad={()=>setProfileTab(null)} />}
          {page==="logbook"      && <OJTLogbook user={user} notify={notify} />}
          {page==="progress"     && <OJTProgress user={user} notify={notify} setPage={setPage} />}
          </Suspense>
        </MobileStudentApp>
      ) : (
        <div style={{ paddingTop: page === "jobs" ? 0 : 80, padding: page === "jobs" ? 0 : "80px 24px 0", maxWidth: page === "jobs" ? "100%" : 900, margin:"0 auto" }}>
          <Suspense fallback={<SectionLoader />}>
          {page==="home"         && <StudentHome user={user} studentProfile={studentProfile} cvProfile={cvProfile} setPage={setPage} notify={notify} isPremium={isPremium} onUpgrade={()=>setShowPremium(true)} />}
          {page==="jobs"         && <AIJobMatchFeed user={user} supabase={supabase} notify={notify} onApply={openApply} onLoginPrompt={()=>{setAuthMode("login");setShowAuth(true);}} />}
          {page==="cv"           && (() => { setPage("profile"); setProfileTab("resume"); return null; })()}
          {page==="applications" && <MyApplications user={user} isPremium={isPremium} onUpgrade={()=>setShowPremium(true)} onLoginPrompt={()=>{setAuthMode("login");setShowAuth(true);}} />}
          {page==="referral"     && <ReferralSystem user={user} supabase={supabase} notify={notify} isPremium={isPremium} />}
          {page==="profile"      && <ProfilePage user={user} notify={notify} isPremium={isPremium} onUpgrade={()=>setShowPremium(true)} supabase={supabase} initialTab={profileTab} onTabLoad={()=>setProfileTab(null)} />}
          {page==="logbook"      && <OJTLogbook user={user} notify={notify} />}
          {page==="progress"     && <OJTProgress user={user} notify={notify} setPage={setPage} />}
          {page==="employer"     && <div style={{paddingTop:100,textAlign:"center"}}><div style={{fontSize:32,marginBottom:12}}>🏢</div><p style={{fontWeight:700}}>Redirecting to Employer Dashboard…</p><script>{window.location.href="/employer"}</script></div>}
          </Suspense>
        </div>
      )}
    </>
  );
}

// ─── LOGO COMPONENT ──────────────────────────────────────────────────────────
function Logo({ size=36 }) {
  return (
    <div style={{ display:"flex", alignItems:"center" }}>
      <img
        src="/OJT_logo.png"
        alt="OJT Hub"
        style={{ height: size * 2.2, width:"auto", objectFit:"contain", flexShrink:0 }}
      />
    </div>
  );
}

// ─── TOP NAV ──────────────────────────────────────────────────────────────────

// ─── NOTIFICATION BELL ───────────────────────────────────────────────────────
function NotificationBell({ user }) {
  const [notifs, setNotifs]   = useState([]);
  const [open, setOpen]       = useState(false);
  const [loading, setLoading] = useState(false);
  const ref                   = useRef(null);

  const unread = notifs.filter(n => !n.is_read).length;

  const load = async () => {
    if (!user) return;
    setLoading(true);
    const { data } = await supabase
      .from("notifications")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .limit(30);
    setNotifs(data || []);
    setLoading(false);
  };

  // Initial load + realtime subscription
  useEffect(() => {
    if (!user) return;
    load();
    const channel = supabase
      .channel("notif_" + user.id)
      .on("postgres_changes", {
        event: "INSERT",
        schema: "public",
        table: "notifications",
        filter: `user_id=eq.${user.id}`,
      }, (payload) => {
        setNotifs(p => [payload.new, ...p]);
      })
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [user]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const markAllRead = async () => {
    const unreadIds = notifs.filter(n => !n.is_read).map(n => n.id);
    if (!unreadIds.length) return;
    await supabase.from("notifications").update({ is_read: true }).in("id", unreadIds);
    setNotifs(p => p.map(n => ({ ...n, is_read: true })));
  };

  const markRead = async (notif) => {
    if (!notif.is_read) {
      await supabase.from("notifications").update({ is_read: true }).eq("id", notif.id);
      setNotifs(p => p.map(n => n.id === notif.id ? { ...n, is_read: true } : n));
    }
    if (notif.link) window.location.href = notif.link;
    setOpen(false);
  };

  const timeAgo = (ts) => {
    const secs = Math.floor((Date.now() - new Date(ts)) / 1000);
    if (secs < 60)   return "just now";
    if (secs < 3600) return Math.floor(secs/60) + "m ago";
    if (secs < 86400) return Math.floor(secs/3600) + "h ago";
    return Math.floor(secs/86400) + "d ago";
  };

  if (!user) return null;

  return (
    <div ref={ref} style={{ position: "relative" }}>
      {/* Bell button */}
      <button onClick={() => { setOpen(o => !o); if (!open) load(); }}
        style={{ position:"relative", background:"none", border:"none", cursor:"pointer",
          width:38, height:38, borderRadius:"50%", display:"flex", alignItems:"center",
          justifyContent:"center", fontSize:20, transition:"background .18s",
          background: open ? T.primaryL : "transparent" }}>
        🔔
        {unread > 0 && (
          <span style={{ position:"absolute", top:4, right:4, background:T.red, color:"#fff",
            fontSize:9, fontWeight:800, borderRadius:"50%", width:16, height:16,
            display:"flex", alignItems:"center", justifyContent:"center",
            border:"2px solid #fff", lineHeight:1 }}>
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {/* Dropdown — fixed position so it escapes nav stacking context */}
      {open && (
        <div style={{ position:"fixed", top:70, right:8,
          width:"min(340px, calc(100vw - 16px))",
          background:"#fff",
          borderRadius:16, boxShadow:"0 12px 48px rgba(0,0,0,.15)", border:`1.5px solid ${T.border}`,
          zIndex:9999, overflow:"hidden" }}>

          {/* Header */}
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center",
            padding:"14px 16px", borderBottom:`1px solid ${T.border}` }}>
            <div style={{ fontWeight:800, fontSize:15, color:T.ink }}>
              Notifications {unread > 0 && <span style={{ fontSize:12, color:T.primary, fontWeight:700 }}>({unread} new)</span>}
            </div>
            {unread > 0 && (
              <button onClick={markAllRead}
                style={{ fontSize:12, color:T.primary, fontWeight:700, background:"none", border:"none", cursor:"pointer" }}>
                Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div style={{ maxHeight:380, overflowY:"auto" }}>
            {loading ? (
              <div style={{ padding:"32px 0", textAlign:"center", color:T.inkL, fontSize:13 }}>Loading…</div>
            ) : notifs.length === 0 ? (
              <div style={{ padding:"40px 20px", textAlign:"center" }}>
                <div style={{ fontSize:32, marginBottom:8 }}>🔕</div>
                <div style={{ fontSize:13, color:T.inkL }}>No notifications yet</div>
              </div>
            ) : notifs.map(n => (
              <div key={n.id} onClick={() => markRead(n)}
                style={{ padding:"12px 16px", borderBottom:`1px solid ${T.border}`,
                  cursor: n.link ? "pointer" : "default",
                  background: n.is_read ? "#fff" : T.primaryL,
                  transition:"background .18s" }}
                onMouseEnter={e => e.currentTarget.style.background = n.is_read ? T.surface : "#d4ede5"}
                onMouseLeave={e => e.currentTarget.style.background = n.is_read ? "#fff" : T.primaryL}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8 }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight: n.is_read ? 600 : 800, fontSize:13, color:T.ink, marginBottom:3 }}>{n.title}</div>
                    {n.message && <div style={{ fontSize:12, color:T.inkM, lineHeight:1.5 }}>{n.message}</div>}
                  </div>
                  <div style={{ fontSize:11, color:T.inkL, flexShrink:0, marginTop:2 }}>{timeAgo(n.created_at)}</div>
                </div>
                {!n.is_read && (
                  <div style={{ width:7, height:7, borderRadius:"50%", background:T.primary,
                    position:"absolute", right:16, top:"50%", transform:"translateY(-50%)" }} />
                )}
              </div>
            ))}
          </div>

          {/* Footer */}
          {notifs.length > 0 && (
            <div style={{ padding:"10px 16px", borderTop:`1px solid ${T.border}`, textAlign:"center" }}>
              <button onClick={async () => {
                await supabase.from("notifications").delete().eq("user_id", user.id).eq("is_read", true);
                setNotifs(p => p.filter(n => !n.is_read));
              }} style={{ fontSize:12, color:T.inkL, background:"none", border:"none", cursor:"pointer" }}>
                Clear read notifications
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}



// ─── STUDENT PREMIUM MODAL ────────────────────────────────────────────────────
const STUDENT_PREMIUM_FEATURES = [
  { icon:"🤖", label:"AI Career Coach Pia", desc:"Unlimited sessions with Coach Pia for resume and interview prep" },
  { icon:"🎯", label:"AI Match Score", desc:"See your match % for every job listing — know before you apply" },
  { icon:"📊", label:"Application Analytics", desc:"Track open rates, response times, and your success rate" },
  { icon:"⚡", label:"Priority Applications", desc:"Your application appears at the top of employer inboxes" },
  { icon:"📄", label:"Premium CV Templates", desc:"10 professionally designed CV layouts" },
  { icon:"🔔", label:"Instant Job Alerts", desc:"Get notified the moment matching OJT listings are posted" },
  { icon:"🏅", label:"Verified Student Badge", desc:"Stand out with a verified profile badge employers trust" },
];

function StudentPremiumModal({ user, onClose, onSuccess, notify }) {
  const [step, setStep]       = useState("plans"); // plans | checkout | success
  const [method, setMethod]   = useState("gcash");
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");

  const PRICE = 199;
  const PAYMONGO_KEY = process.env.REACT_APP_PAYMONGO_PUBLIC_KEY || "";

  const checkout = async () => {
    setLoading(true); setError("");
    if (!PAYMONGO_KEY) {
      // Demo mode — just mark as premium in DB
      const { error } = await supabase.from("profiles").update({ is_premium: true, premium_since: new Date().toISOString() }).eq("id", user.id);
      setLoading(false);
      if (error) { setError("Failed to activate premium: " + error.message); return; }
      setStep("success");
      onSuccess(); // ← immediately update parent state
      return;
    }
    try {
      const res = await fetch("https://api.paymongo.com/v1/checkout_sessions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Basic ${btoa(PAYMONGO_KEY + ":")}`,
        },
        body: JSON.stringify({
          data: {
            attributes: {
              billing: { name: user.user_metadata?.full_name || "Student", email: user.email },
              send_email_receipt: true,
              show_description: true,
              show_line_items: true,
              cancel_url: window.location.href,
              success_url: `${window.location.origin}/?premium=1`,
              description: "OJT Hub Student Premium — ₱199/month",
              line_items: [{
                currency: "PHP",
                amount: PRICE * 100,
                description: "Student Premium Subscription",
                name: "OJT Hub Premium",
                quantity: 1,
              }],
              payment_method_types: [method],
              metadata: { type: "student_premium", student_id: user.id },
            },
          },
        }),
      });
      const json = await res.json();
      const url = json?.data?.attributes?.checkout_url;
      if (url) window.location.href = url;
      else setError(json?.errors?.[0]?.detail || "Checkout failed. Please try again.");
    } catch(e) {
      setError("Payment error: " + e.message);
    }
    setLoading(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth:540, width:"100%", padding:0, overflowY:"auto", paddingBottom:"env(safe-area-inset-bottom,0px)" }} onClick={e=>e.stopPropagation()}>

        {step === "plans" && (<>
          {/* Header */}
          <div style={{ background:`linear-gradient(135deg,#0a6e4e,#0d8f66)`, padding:"28px 28px 24px", position:"relative" }}>
            <button onClick={onClose} style={{ position:"absolute", top:16, right:16, background:"rgba(255,255,255,.2)", border:"none", borderRadius:"50%", width:30, height:30, color:"#fff", fontSize:16, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>✕</button>
            <div style={{ fontSize:36, marginBottom:10 }}>⚡</div>
            <div style={{ fontFamily:"'Fraunces',serif", fontSize:24, fontWeight:900, color:"#fff", marginBottom:4 }}>Go Premium</div>
            <div style={{ fontSize:14, color:"rgba(255,255,255,.8)" }}>Unlock AI tools, priority placement & more</div>
            <div style={{ marginTop:16, display:"inline-flex", alignItems:"baseline", gap:4 }}>
              <span style={{ fontFamily:"'Fraunces',serif", fontSize:38, fontWeight:900, color:"#fff" }}>₱199</span>
              <span style={{ fontSize:14, color:"rgba(255,255,255,.7)" }}>/month</span>
            </div>
          </div>

          {/* Features */}
          <div style={{ padding:"20px 24px", paddingBottom:"max(24px, calc(80px + env(safe-area-inset-bottom,0px)))" }}>
            <div style={{ display:"grid", gap:10, marginBottom:20 }}>
              {STUDENT_PREMIUM_FEATURES.map((f,i) => (
                <div key={i} style={{ display:"flex", alignItems:"flex-start", gap:12 }}>
                  <span style={{ fontSize:20, flexShrink:0 }}>{f.icon}</span>
                  <div>
                    <div style={{ fontWeight:700, fontSize:13, color:"#0f1923" }}>{f.label}</div>
                    <div style={{ fontSize:12, color:"#6b7a8d", lineHeight:1.4 }}>{f.desc}</div>
                  </div>
                  <span style={{ marginLeft:"auto", color:"#16a34a", fontSize:16, flexShrink:0 }}>✓</span>
                </div>
              ))}
            </div>
            <button className="btn btn-primary" style={{ width:"100%", fontSize:16, padding:"16px" }}
              onClick={()=>setStep("checkout")}>
              Get Premium for ₱199/month →
            </button>
            <div style={{ textAlign:"center", fontSize:12, color:"#6b7a8d", marginTop:10 }}>
              Cancel anytime · Secure payment via PayMongo 🔒
            </div>
          </div>
        </>)}

        {step === "checkout" && (<>
          <div style={{ padding:"24px 28px", paddingBottom:"max(24px, calc(80px + env(safe-area-inset-bottom,0px)))" }}>
            <button onClick={()=>setStep("plans")} style={{ background:"none", border:"none", cursor:"pointer", fontSize:13, color:"#6b7a8d", marginBottom:16, fontWeight:600 }}>← Back</button>
            <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:20, fontWeight:900, marginBottom:4 }}>Complete Payment</h2>
            <p style={{ fontSize:13, color:"#6b7a8d", marginBottom:20 }}>₱199/month · OJT Hub Student Premium</p>

            <div style={{ marginBottom:20 }}>
              <label>Payment Method</label>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginTop:8 }}>
                {[["gcash","💚 GCash"],["paymaya","💜 Maya"],["card","💳 Card"],["grab_pay","🟢 GrabPay"]].map(([id, label])=>(
                  <button key={id} onClick={()=>setMethod(id)} style={{
                    padding:"12px 14px", borderRadius:10, border:`2px solid ${method===id?"#0a6e4e":"#e2e8e5"}`,
                    background: method===id?"#e8f5f0":"#fff", fontWeight:700, fontSize:13,
                    cursor:"pointer", transition:"all .15s", color: method===id?"#0a6e4e":"#2d3d4f",
                  }}>{label}</button>
                ))}
              </div>
            </div>

            {error && <div style={{ background:"#fee2e2", color:"#dc2626", borderRadius:10, padding:"10px 14px", fontSize:13, marginBottom:16 }}>{error}</div>}

            <button className="btn btn-primary" style={{ width:"100%", fontSize:15, padding:"15px" }}
              onClick={checkout} disabled={loading}>
              {loading ? "Processing…" : `Pay ₱199 via ${method === "card" ? "Card" : method === "gcash" ? "GCash" : method === "paymaya" ? "Maya" : "GrabPay"}`}
            </button>
            <div style={{ textAlign:"center", fontSize:11, color:"#6b7a8d", marginTop:10 }}>
              Redirecting to PayMongo secure checkout
            </div>
          </div>
        </>)}

        {step === "success" && (
          <div style={{ padding:"40px 28px", textAlign:"center" }}>
            <div style={{ fontSize:56, marginBottom:16 }}>🎉</div>
            <div style={{ fontFamily:"'Fraunces',serif", fontSize:24, fontWeight:900, marginBottom:8 }}>Welcome to Premium!</div>
            <p style={{ fontSize:14, color:"#6b7a8d", marginBottom:24 }}>All premium features are now unlocked. Start exploring!</p>
            <button className="btn btn-primary" style={{ padding:"14px 32px" }} onClick={()=>{ onSuccess?.(); onClose(); }}>Start Using Premium →</button>
          </div>
        )}
      </div>
    </div>
  );
}


// ─── APPLY MODAL (with AI cover letter) ──────────────────────────────────────
function ApplyModal({ job, user, cvProfile, onClose, onApply }) {
  const [coverNote, setCoverNote] = useState("");
  const [generating, setGenerating] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const generateCoverLetter = async () => {
    setGenerating(true);
    try {
      const res = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 1000,
          messages: [{
            role: "user",
            content: `Write a professional, concise cover letter for a Filipino student applying for this OJT/internship position. Keep it under 200 words. Be warm and enthusiastic but professional.

Job: ${job.title} at ${job.company} (${job.location})
Job Description: ${job.desc || "OJT/internship position"}
Required Skills: ${(job.tags||[]).join(", ")}

Student Profile:
Name: ${cvProfile?.full_name || user?.name || "Student"}
Course: ${cvProfile?.course || ""}
Skills: ${(cvProfile?.skills||[]).join(", ") || ""}
Bio: ${cvProfile?.bio || ""}

Write the cover letter body only (no "Dear Hiring Manager" header, no sign-off). Start directly with an engaging opening sentence.`
          }]
        }),
      });
      const data = await res.json();
      const text = data.content?.find(b => b.type === "text")?.text || "";
      setCoverNote(text);
    } catch(e) {
      setCoverNote("I am excited to apply for the " + job.title + " position at " + job.company + ". My background in " + (cvProfile?.course || "my field") + " has equipped me with the skills needed to contribute meaningfully to your team. I am eager to learn and grow through this OJT experience.");
    }
    setGenerating(false);
  };

  const submit = async () => {
    setSubmitting(true);
    await onApply(job, coverNote);
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal" style={{ maxWidth:520, width:"100%" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:16 }}>
          <div>
            <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:20, fontWeight:900 }}>Apply to {job.title}</h2>
            <div style={{ fontSize:13, color:T.inkL }}>{job.company} · {job.location}</div>
          </div>
          <button onClick={onClose} style={{ background:"none", border:"none", fontSize:22, cursor:"pointer", color:T.inkL }}>✕</button>
        </div>

        <div style={{ marginBottom:16 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
            <label style={{ fontSize:12, fontWeight:700, color:T.inkM, textTransform:"uppercase", letterSpacing:".04em" }}>Cover Letter</label>
            <button onClick={generateCoverLetter} disabled={generating} style={{ fontSize:11, fontWeight:700, color:T.primary, background:T.primaryL, border:`1px solid ${T.primary}30`, borderRadius:100, padding:"4px 12px", cursor:"pointer", fontFamily:"inherit", display:"flex", alignItems:"center", gap:5 }}>
              {generating ? <><span className="spin" style={{ width:10, height:10, border:"1.5px solid rgba(10,110,78,.3)", borderTopColor:T.primary, borderRadius:"50%", display:"inline-block" }} /> Generating…</> : "✨ Write with AI"}
            </button>
          </div>
          <textarea className="input" rows={7} placeholder="Write a brief cover note explaining why you're interested in this role and what you bring to the table…"
            value={coverNote} onChange={e=>setCoverNote(e.target.value)}
            style={{ resize:"vertical", lineHeight:1.65 }} />
          <div style={{ fontSize:11, color:T.inkL, marginTop:4 }}>{coverNote.length} characters · Employers appreciate a personal, specific note</div>
        </div>

        <div style={{ display:"flex", gap:10 }}>
          <button className="btn btn-ghost" style={{ flex:1 }} onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" style={{ flex:2 }} onClick={submit} disabled={submitting}>
            {submitting ? "Submitting…" : "Submit Application →"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── JOB ALERTS MODAL ────────────────────────────────────────────────────────
function JobAlertsModal({ user, onClose, notify }) {
  const [prefs, setPrefs] = useState({ keywords:"", location:"", min_salary:"", frequency:"daily" });
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [existing, setExisting] = useState(null);

  useEffect(() => {
    if (!user) return;
    supabase.from("job_alerts").select("*").eq("student_id", user.id).maybeSingle()
      .then(({ data }) => {
        if (data) { setExisting(data); setPrefs({ keywords:data.keywords||"", location:data.location||"", min_salary:data.min_salary||"", frequency:data.frequency||"daily" }); }
        setLoading(false);
      });
  }, [user]);

  const save = async () => {
    setLoading(true);
    const payload = { student_id:user.id, email:user.email, ...prefs, is_active:true };
    const { error } = existing
      ? await supabase.from("job_alerts").update(payload).eq("id", existing.id)
      : await supabase.from("job_alerts").insert(payload);
    setLoading(false);
    if (error) { notify("Failed: "+error.message, false); return; }
    setSaved(true);
    notify("Job alerts saved! You'll receive emails when matching jobs are posted. ✓");
  };

  const deleteAlert = async () => {
    if (!existing) return;
    await supabase.from("job_alerts").delete().eq("id", existing.id);
    setExisting(null); setPrefs({ keywords:"", location:"", min_salary:"", frequency:"daily" }); setSaved(false);
    notify("Job alerts disabled.");
  };

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal" style={{ maxWidth:440, width:"100%" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
          <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:20, fontWeight:900 }}>🔔 Job Alerts</h2>
          <button onClick={onClose} style={{ background:"none", border:"none", fontSize:22, cursor:"pointer", color:T.inkL }}>✕</button>
        </div>

        {saved ? (
          <div style={{ textAlign:"center", padding:"24px 0" }}>
            <div style={{ fontSize:48, marginBottom:12 }}>✅</div>
            <div style={{ fontFamily:"'Fraunces',serif", fontSize:20, fontWeight:900, marginBottom:8 }}>You're all set!</div>
            <p style={{ fontSize:13, color:T.inkL, marginBottom:24 }}>We'll email you at <strong>{user?.email}</strong> when matching OJT listings are posted.</p>
            <button className="btn btn-primary" onClick={onClose}>Done</button>
          </div>
        ) : (<>
          <p style={{ fontSize:13, color:T.inkL, marginBottom:20 }}>Get email notifications when new OJT listings match your preferences.</p>

          <div style={{ display:"grid", gap:14, marginBottom:20 }}>
            <div>
              <label>Keywords <span style={{ color:T.inkL, fontWeight:400 }}>(job title, skill, company)</span></label>
              <input className="input" placeholder="e.g. React, Marketing, BDO" value={prefs.keywords} onChange={e=>setPrefs(p=>({...p,keywords:e.target.value}))} />
            </div>
            <div>
              <label>Location</label>
              <input className="input" placeholder="e.g. BGC, Makati, Cebu" value={prefs.location} onChange={e=>setPrefs(p=>({...p,location:e.target.value}))} />
            </div>
            <div>
              <label>Minimum Allowance (₱/month)</label>
              <input className="input" type="number" placeholder="e.g. 10000" value={prefs.min_salary} onChange={e=>setPrefs(p=>({...p,min_salary:e.target.value}))} />
            </div>
            <div>
              <label>Frequency</label>
              <select className="input" value={prefs.frequency} onChange={e=>setPrefs(p=>({...p,frequency:e.target.value}))}>
                <option value="instant">⚡ Instant (as jobs are posted)</option>
                <option value="daily">📅 Daily digest</option>
                <option value="weekly">📆 Weekly digest</option>
              </select>
            </div>
          </div>

          <div style={{ display:"flex", gap:10 }}>
            {existing && <button className="btn btn-ghost btn-sm" onClick={deleteAlert} style={{ color:T.red, borderColor:T.red }}>Disable</button>}
            <button className="btn btn-primary" style={{ flex:1 }} onClick={save} disabled={loading}>{loading?"Saving…":"Save Alert"}</button>
          </div>
        </>)}
      </div>
    </div>
  );
}
// ─── SALARY BENCHMARKING WIDGET ──────────────────────────────────────────────
// Shows live salary data from Supabase job_listings grouped by tags/industry
function SalaryBenchmark({ supabase: sb, T: theme }) {
  const [data, setData]     = useState(null);
  const [loading, setLoad]  = useState(true);
  const [category, setCat]  = useState("ALL");

  // Curated Philippine OJT stipend benchmarks (fallback/seed data)
  const BENCHMARKS = [
    { label:"IT / Software",      min:12000, median:16000, max:25000, icon:"💻", tag:"tech" },
    { label:"Business / Finance", min:10000, median:13000, max:18000, icon:"📊", tag:"business" },
    { label:"Marketing / Media",  min:9000,  median:12000, max:16000, icon:"📣", tag:"marketing" },
    { label:"Engineering",        min:11000, median:14000, max:22000, icon:"⚙️",  tag:"engineering" },
    { label:"Hospitality / HR",   min:8000,  median:11000, max:15000, icon:"🏨", tag:"hospitality" },
    { label:"Healthcare",         min:7000,  median:10000, max:14000, icon:"🏥", tag:"healthcare" },
    { label:"Design / Creative",  min:10000, median:13000, max:20000, icon:"🎨", tag:"design" },
    { label:"Education / NGO",    min:6000,  median:9000,  max:12000, icon:"📚", tag:"education" },
  ];

  useEffect(() => {
    if (!sb) { setData(BENCHMARKS); setLoad(false); return; }
    // Fetch real salary data from live listings
    sb.from("job_listings")
      .select("salary, tags")
      .eq("is_active", true)
      .not("salary", "is", null)
      .then(({ data: jobs }) => {
        if (!jobs || jobs.length < 5) { setData(BENCHMARKS); setLoad(false); return; }
        // Parse PHP salary strings: "₱14,000/mo", "₱10k-15k/mo"
        const parse = (s) => {
          if (!s) return null;
          const nums = s.replace(/[₱,]/g, "").match(/\d+/g);
          if (!nums) return null;
          const vals = nums.map(Number).filter(n => n > 1000 && n < 200000);
          if (!vals.length) return null;
          return vals.reduce((a,b)=>a+b,0)/vals.length;
        };
        // Group by first tag
        const groups = {};
        jobs.forEach(j => {
          const amt = parse(j.salary);
          if (!amt) return;
          const tag = (j.tags||[])[0]?.toLowerCase() || "other";
          if (!groups[tag]) groups[tag] = [];
          groups[tag].push(amt);
        });
        // Merge with benchmarks
        const merged = BENCHMARKS.map(b => {
          const matching = Object.entries(groups).filter(([t]) => t.includes(b.tag) || b.tag.includes(t));
          const allVals  = matching.flatMap(([,v])=>v).sort((a,b)=>a-b);
          if (allVals.length < 2) return { ...b, live:false };
          const med = allVals[Math.floor(allVals.length/2)];
          return { ...b, median:Math.round(med), min:Math.round(allVals[0]), max:Math.round(allVals[allVals.length-1]), live:true };
        });
        setData(merged);
        setLoad(false);
      });
  }, [sb]);

  const MAX_VAL = 28000;
  const fmt = v => `₱${v.toLocaleString()}`;

  if (loading) return <div style={{ padding:24, textAlign:"center", color:theme?.inkL }}><span>Loading benchmarks…</span></div>;

  return (
    <div>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16, flexWrap:"wrap", gap:10 }}>
        <div>
          <div style={{ fontFamily:"'Fraunces',serif", fontSize:18, fontWeight:900, color:theme?.ink }}>💰 Stipend Benchmarks</div>
          <div style={{ fontSize:12, color:theme?.inkL, marginTop:2 }}>Monthly allowances by field · Philippine OJT market</div>
        </div>
        <div style={{ fontSize:11, fontWeight:700, color:theme?.primary, background:theme?.primaryL, padding:"4px 10px", borderRadius:100 }}>
          {data?.some(d=>d.live) ? "🔴 Live data" : "📊 Market rates"}
        </div>
      </div>

      <div style={{ display:"grid", gap:10 }}>
        {(data||[]).map((b,i) => {
          const medPct  = Math.round((b.median / MAX_VAL) * 100);
          const minPct  = Math.round((b.min    / MAX_VAL) * 100);
          const maxPct  = Math.round((b.max    / MAX_VAL) * 100);
          return (
            <div key={i} style={{ padding:"12px 16px", background:"#fff", borderRadius:12, border:`1px solid ${theme?.border}` }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <span style={{ fontSize:18 }}>{b.icon}</span>
                  <span style={{ fontWeight:700, fontSize:13, color:theme?.ink }}>{b.label}</span>
                  {b.live && <span style={{ fontSize:9, fontWeight:800, color:"#16a34a", background:"#dcfce7", padding:"1px 7px", borderRadius:100 }}>LIVE</span>}
                </div>
                <div style={{ textAlign:"right" }}>
                  <div style={{ fontFamily:"'Fraunces',serif", fontSize:16, fontWeight:900, color:theme?.primary }}>{fmt(b.median)}<span style={{ fontSize:10, color:theme?.inkL, fontWeight:400 }}>/mo</span></div>
                  <div style={{ fontSize:10, color:theme?.inkL }}>median</div>
                </div>
              </div>
              {/* Range bar */}
              <div style={{ position:"relative", height:8, background:theme?.border, borderRadius:100, overflow:"hidden" }}>
                {/* Range fill (min to max) */}
                <div style={{ position:"absolute", height:"100%", left:`${minPct}%`, width:`${maxPct-minPct}%`, background:`${theme?.primary}30`, borderRadius:100 }} />
                {/* Median marker */}
                <div style={{ position:"absolute", height:"100%", width:3, left:`${medPct}%`, background:theme?.primary, borderRadius:100 }} />
              </div>
              <div style={{ display:"flex", justifyContent:"space-between", marginTop:4 }}>
                <span style={{ fontSize:10, color:theme?.inkL }}>{fmt(b.min)} min</span>
                <span style={{ fontSize:10, color:theme?.inkL }}>{fmt(b.max)} max</span>
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ fontSize:11, color:theme?.inkL, marginTop:12, textAlign:"center" }}>
        Based on active OJT listings · For guidance only · Actual stipends vary by company and agreement
      </div>
    </div>
  );
}
// ─── ONBOARDING CHECKLIST ─────────────────────────────────────────────────────
function OnboardingChecklist({ profile, setPage, setScrollTarget, setProfileTab, onDismiss, onRefresh }) {
  const [open, setOpen] = useState(true);

  const STEPS = [
    {
      id: "university",
      icon: "🏫",
      label: "Link your university",
      desc: "Connect to your school so coordinators can find you.",
      done: profile.has_university,
      action: () => { setPage("profile"); setProfileTab("academic"); setScrollTarget("profile-university-section"); },
      cta: "Update Profile →",
    },
    {
      id: "profile",
      icon: "👤",
      label: "Complete your profile",
      desc: "Add a photo, bio, and skills so employers know who you are.",
      done: profile.has_avatar && profile.has_bio && profile.has_skills,
      partial: (profile.has_avatar || profile.has_bio || profile.has_skills) && !(profile.has_avatar && profile.has_bio && profile.has_skills),
      action: () => { setPage("profile"); setProfileTab("personal"); setScrollTarget("profile-bio-section"); },
      cta: "Edit Profile →",
    },
    {
      id: "cv",
      icon: "📄",
      label: "Build your CV / résumé",
      desc: "Add your education, work experience, and certifications.",
      done: profile.has_education,
      action: () => { setPage("cv"); setScrollTarget("cv-education-section"); },
      cta: "Build CV →",
    },
    {
      id: "documents",
      icon: "📁",
      label: "Upload your OJT documents",
      desc: "Medical cert, clearances, and other required documents.",
      done: profile.has_document,
      action: () => window.location.href = "/documents",
      cta: "Upload Documents →",
    },
    {
      id: "apply",
      icon: "📨",
      label: "Apply for an OJT position",
      desc: "Browse hundreds of verified OJT listings and submit your application.",
      done: profile.has_applied,
      action: () => setPage("jobs"),
      cta: "Browse Jobs →",
    },
    {
      id: "endorsement",
      icon: "📜",
      label: "Get endorsed by your coordinator",
      desc: "Once you have a job offer, your coordinator will officially endorse you.",
      done: profile.has_endorsement,
      locked: !profile.has_applied,
      action: () => setPage("applications"),
      cta: "View Applications →",
    },
    {
      id: "logbook",
      icon: "📅",
      label: "Log your first OJT day",
      desc: "Start logging hours once your internship begins.",
      done: profile.has_logbook,
      locked: !profile.has_endorsement,
      action: () => window.location.href = "/logbook",
      cta: "Open Logbook →",
    },
  ];

  const done_count  = STEPS.filter(s => s.done).length;
  const total       = STEPS.length;
  const pct         = Math.round((done_count / total) * 100);
  const all_done    = done_count === total;
  const next_step   = STEPS.find(s => !s.done && !s.locked);

  // Auto-collapse once all done
  if (all_done && open) return (
    <div style={{ margin:"72px 16px 0", padding:"14px 20px", background:"linear-gradient(135deg,#0a6e4e,#0d8f66)",
      borderRadius:14, display:"flex", alignItems:"center", justifyContent:"space-between", gap:12 }}>
      <div style={{ display:"flex", alignItems:"center", gap:12 }}>
        <span style={{ fontSize:28 }}>🎉</span>
        <div>
          <div style={{ fontWeight:800, fontSize:15, color:"#fff" }}>Setup complete!</div>
          <div style={{ fontSize:12, color:"rgba(255,255,255,.75)" }}>You're fully set up and ready for OJT.</div>
        </div>
      </div>
      <button onClick={onDismiss} style={{ background:"rgba(255,255,255,.2)", border:"none", borderRadius:100,
        color:"#fff", fontSize:12, fontWeight:700, padding:"6px 14px", cursor:"pointer" }}>Dismiss</button>
    </div>
  );

  return (
    <div style={{ margin:"72px 16px 0", fontFamily:"inherit" }}>
      {/* ── Collapsed banner ── */}
      {!open && (
        <div onClick={()=>setOpen(true)} style={{ padding:"12px 20px", background:"#fff",
          border:`2px solid ${T.primary}`, borderRadius:14, cursor:"pointer",
          display:"flex", alignItems:"center", justifyContent:"space-between", gap:12,
          boxShadow:"0 2px 12px rgba(10,110,78,.08)" }}>
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            <div style={{ width:36, height:36, borderRadius:"50%",
              background:`conic-gradient(${T.primary} ${pct*3.6}deg, ${T.border} 0deg)`,
              display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
              <div style={{ width:26, height:26, borderRadius:"50%", background:"#fff",
                display:"flex", alignItems:"center", justifyContent:"center",
                fontSize:11, fontWeight:800, color:T.primary }}>{pct}%</div>
            </div>
            <div>
              <div style={{ fontWeight:700, fontSize:14, color:T.ink }}>Getting Started</div>
              <div style={{ fontSize:12, color:T.inkL }}>{done_count}/{total} steps done{next_step ? ` · Next: ${next_step.label}` : ""}</div>
            </div>
          </div>
          <span style={{ fontSize:18, color:T.inkL }}>▾</span>
        </div>
      )}

      {/* ── Expanded card ── */}
      {open && (
        <div style={{ background:"#fff", borderRadius:16, boxShadow:"0 4px 24px rgba(10,110,78,.10)",
          overflow:"hidden", border:`1px solid ${T.border}` }}>
          {/* Header */}
          <div style={{ padding:"18px 22px 14px", borderBottom:`1px solid ${T.border}`,
            display:"flex", alignItems:"center", justifyContent:"space-between", gap:12 }}>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8 }}>
                <div style={{ fontFamily:"'Fraunces',serif", fontSize:17, fontWeight:900, color:T.ink }}>
                  🚀 Getting Started
                </div>
                <div style={{ fontSize:13, fontWeight:700, color:T.primary }}>{done_count}/{total} done</div>
              </div>
              <div style={{ background:T.border, borderRadius:100, height:7, overflow:"hidden" }}>
                <div style={{ height:"100%", borderRadius:100,
                  background:`linear-gradient(90deg,${T.primary},#0d8f66)`,
                  width:`${pct}%`, transition:"width .6s ease" }} />
              </div>
            </div>
            <button onClick={()=>setOpen(false)} style={{ background:"none", border:"none", cursor:"pointer",
              color:T.inkL, fontSize:20, lineHeight:1, flexShrink:0, padding:"0 4px" }}>▴</button>
          </div>

          {/* Steps */}
          <div style={{ padding:"10px 0" }}>
            {STEPS.map((step, i) => (
              <div key={step.id} style={{
                display:"flex", alignItems:"flex-start", gap:14, padding:"12px 22px",
                opacity: step.locked ? .45 : 1,
                background: step.done ? "#fafffe" : "transparent",
                borderLeft: step.done ? `3px solid ${T.primary}` : "3px solid transparent",
                transition:"all .2s",
              }}>
                {/* Status dot */}
                <div style={{ flexShrink:0, marginTop:2 }}>
                  {step.done ? (
                    <div style={{ width:22, height:22, borderRadius:"50%", background:T.primary,
                      display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontSize:12 }}>✓</div>
                  ) : step.partial ? (
                    <div style={{ width:22, height:22, borderRadius:"50%", border:`2px solid ${T.amber}`,
                      background:"#fef3c7", display:"flex", alignItems:"center", justifyContent:"center",
                      fontSize:11, color:T.amber, fontWeight:800 }}>~</div>
                  ) : (
                    <div style={{ width:22, height:22, borderRadius:"50%", border:`2px solid ${T.border}`,
                      display:"flex", alignItems:"center", justifyContent:"center",
                      fontSize:12, color:T.inkL, fontWeight:700 }}>{i+1}</div>
                  )}
                </div>
                {/* Content */}
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:2 }}>
                    <span style={{ fontSize:15 }}>{step.icon}</span>
                    <span style={{ fontWeight:700, fontSize:13,
                      color: step.done ? T.primary : step.locked ? T.inkL : T.ink,
                      textDecoration: step.done ? "line-through" : "none" }}>
                      {step.label}
                    </span>
                    {step.locked && <span style={{ fontSize:10, color:T.inkL }}>🔒 Complete previous step first</span>}
                  </div>
                  {!step.done && !step.locked && (
                    <p style={{ fontSize:12, color:T.inkL, margin:"0 0 6px" }}>{step.desc}</p>
                  )}
                </div>
                {/* CTA */}
                {!step.done && !step.locked && (
                  <button onClick={step.action} style={{
                    flexShrink:0, padding:"6px 14px", borderRadius:100, border:`1.5px solid ${T.primary}`,
                    background:"transparent", color:T.primary, fontSize:12, fontWeight:700,
                    cursor:"pointer", whiteSpace:"nowrap", transition:"all .18s",
                  }}
                    onMouseEnter={e=>{ e.currentTarget.style.background=T.primary; e.currentTarget.style.color="#fff"; }}
                    onMouseLeave={e=>{ e.currentTarget.style.background="transparent"; e.currentTarget.style.color=T.primary; }}>
                    {step.cta}
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Footer */}
          <div style={{ padding:"12px 22px", borderTop:`1px solid ${T.border}`,
            display:"flex", alignItems:"center", justifyContent:"space-between" }}>
            <button onClick={onRefresh} style={{ background:"none", border:"none", cursor:"pointer",
              fontSize:12, color:T.inkL, fontWeight:600 }}>↻ Refresh</button>
            <button onClick={onDismiss} style={{ background:"none", border:"none", cursor:"pointer",
              fontSize:12, color:T.inkL, fontWeight:600 }}>Dismiss checklist</button>
          </div>
        </div>
      )}
    </div>
  );
}

function TopNav({ user, page, setPage, onLogin, onSignup, onLogout, isPremium, onUpgrade, notify }) {
  const [menuOpen, setMenuOpen] = useState(false);

  const go = (fn) => { fn(); setMenuOpen(false); };

  const navLinks = user?.role === "student" ? [
    { icon:"🏠", label:"Home",          fn:()=>setPage("home"),                   active: page==="home" },
    { icon:"💼", label:"OJT Jobs",     fn:()=>setPage("jobs"),                    active: page==="jobs" },
    { icon:"📄", label:"My Resume",    fn:()=>setPage("cv"),                      active: page==="cv" },
    { icon:"📨", label:"Applications", fn:()=>setPage("applications"),            active: page==="applications" },
    { icon:"👤", label:"My Profile",   fn:()=>setPage("profile"),                 active: page==="profile" },
    { icon:"📓", label:"Logbook",      fn:()=>{ window.location.href="/logbook"; }, active: false },
    { icon:"📈", label:"Progress",     fn:()=>{ window.location.href="/progress"; }, active: false },
    { icon:"📁", label:"Documents",    fn:()=>{ window.location.href="/documents"; }, active: false },
    { icon:"🤖", label:"AI Coach",     fn:()=>{ window.location.href="/coach"; },  active: false },
    { icon:"🎁", label:"Refer & Earn",   fn:()=>setPage("referral"),                active: page==="referral" },
  ] : user?.role === "employer" ? [
    { icon:"🏢", label:"Dashboard",   fn:()=>{ window.location.href="/employer"; }, active: false },
    { icon:"💼", label:"Browse Jobs", fn:()=>setPage("jobs"),                       active: page==="jobs" },
  ] : (user?.role === "coordinator" || user?.role === "department_head" || user?.role === "university_admin") ? [
    { icon:"🏫", label:"Dashboard",    fn:()=>{ window.location.href="/university"; },            active: false },
    { icon:"🎓", label:"Students",     fn:()=>{ window.location.href="/university#students"; },   active: false },
    { icon:"📜", label:"Endorsements", fn:()=>{ window.location.href="/university#endorsements"; }, active: false },
  ] : [
    { icon:"💼", label:"OJT Jobs",      fn:()=>setPage("jobs"),                      active: page==="jobs" },
    { icon:"🏢", label:"For Employers", fn:()=>{ window.location.href="/employer"; }, active: false },
  ];

  return (
    <>
      {/* ── Global nav styles ── */}
      <style>{`
        .tnav-link {
          display:inline-flex; align-items:center; gap:5px;
          font-size:13px; font-weight:500; color:${T.inkM};
          cursor:pointer; padding:6px 2px; white-space:nowrap;
          border-bottom:2.5px solid transparent; transition:all .18s;
          letter-spacing:0.01em; background:none; border-left:none; border-right:none; border-top:none;
          font-family:inherit;
        }
        .tnav-link.active { color:${T.primary}; font-weight:700; border-bottom-color:${T.primary}; }
        .tnav-link:hover  { color:${T.primary}; }
        .tnav-desktop { display:flex; align-items:center; gap:24px; flex:1; justify-content:center; margin:0 24px; }
        .tnav-hamburger { display:none; flex-direction:column; gap:5px; align-items:center; justify-content:center;
          background:none; border:none; cursor:pointer; padding:8px; margin-left:auto; margin-right:8px; }
        @media(max-width:768px) {
          .tnav-desktop   { display:none !important; }
          .tnav-hamburger { display:flex !important; }
        }
        @keyframes tnav-slide {
          from { transform:translateX(100%); opacity:0; }
          to   { transform:translateX(0);    opacity:1; }
        }
      `}</style>

      {/* ── Top bar ── */}
      <nav style={{ position:"fixed", top:0, left:0, right:0, zIndex:100,
        background:"#fff", borderBottom:`1px solid ${T.border}`,
        boxShadow:"0 1px 12px rgba(0,0,0,.06)" }}>
        <div style={{ maxWidth:1280, margin:"0 auto", padding:"0 16px",
          display:"flex", alignItems:"center", justifyContent:"space-between", height:66 }}>

          {/* Logo — click to go home/landing */}
          <div style={{ cursor:"pointer", flexShrink:0 }}
            onClick={()=>{ if(user) setPage("home"); else { window.location.href="/"; } }}>
            <img src="/OJT_logo.png" alt="OJT Hub" style={{ height:46, width:"auto", objectFit:"contain", display:"block" }} />
          </div>

          {/* Desktop nav */}
          <div className="tnav-desktop">
            {navLinks.map(l => (
              <button key={l.label} className={`tnav-link${l.active?" active":""}`} onClick={()=>go(l.fn)}>
                {l.label}
              </button>
            ))}
          </div>

          {/* Right side */}
          <div style={{ display:"flex", alignItems:"center", gap:8, flexShrink:0 }}>
            {user?.role === "student" && (isPremium
              ? <span style={{ fontSize:11, fontWeight:800, color:"#b45309", background:"#fef3c7",
                  padding:"4px 12px", borderRadius:100, border:"1.5px solid #f5a623",
                  whiteSpace:"nowrap" }}>⚡ Premium</span>
              : <button onClick={onUpgrade} className="hide-mobile"
                  style={{ fontSize:12, fontWeight:700, color:"#fff",
                    background:`linear-gradient(135deg,${T.primary},#0d8f66)`,
                    border:"none", borderRadius:100, padding:"6px 16px",
                    cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap",
                    boxShadow:"0 2px 8px rgba(10,110,78,.25)" }}>
                  ⚡ Go Premium
                </button>
            )}
            {user && <NotificationBell user={user} />}
            {!user && (
              <button className="btn btn-primary" style={{ fontSize:13, padding:"8px 18px" }}
                onClick={onLogin}>Sign In</button>
            )}
            {user && (
              <div className="hide-mobile" style={{ display:"flex", alignItems:"center", gap:8,
                background:T.surface, border:`1.5px solid ${T.border}`,
                borderRadius:100, padding:"5px 14px 5px 6px" }}>
                <div style={{ width:28, height:28, borderRadius:"50%",
                  background:`linear-gradient(135deg,${T.primary},#0d8f66)`,
                  color:"#fff", display:"flex", alignItems:"center", justifyContent:"center",
                  fontSize:13, fontWeight:800, flexShrink:0 }}>
                  {user.name?.[0] ?? "?"}
                </div>
                <span style={{ fontSize:13, fontWeight:600, color:T.ink }}>{user.name?.split(" ")[0]}</span>
                <button onClick={onLogout}
                  style={{ background:"none", border:"none", color:T.red, fontSize:12,
                    cursor:"pointer", fontFamily:"inherit", fontWeight:600, padding:0 }}>
                  Sign Out
                </button>
              </div>
            )}
          </div>

          {/* Hamburger — mobile only */}
          {user && (
            <button className="tnav-hamburger" onClick={() => setMenuOpen(o => !o)}>
              <span style={{ width:22, height:2, background:T.inkM, borderRadius:2, display:"block",
                transition:"all .25s",
                transform: menuOpen ? "rotate(45deg) translateY(7px)" : "none" }} />
              <span style={{ width:22, height:2, background:T.inkM, borderRadius:2, display:"block",
                transition:"all .25s", opacity: menuOpen ? 0 : 1 }} />
              <span style={{ width:22, height:2, background:T.inkM, borderRadius:2, display:"block",
                transition:"all .25s",
                transform: menuOpen ? "rotate(-45deg) translateY(-7px)" : "none" }} />
            </button>
          )}
        </div>
      </nav>

      {/* ── Mobile drawer ── */}
      {menuOpen && (
        <>
          <div onClick={() => setMenuOpen(false)}
            style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.45)",
              zIndex:200, backdropFilter:"blur(4px)" }} />
          <div style={{
            position:"fixed", top:0, right:0, bottom:0,
            width:"78%", maxWidth:300, background:"#fff",
            zIndex:201, overflowY:"auto",
            boxShadow:"-8px 0 40px rgba(0,0,0,.2)",
            animation:"tnav-slide .25s cubic-bezier(.22,1,.36,1)",
            display:"flex", flexDirection:"column",
          }}>
            {/* Header */}
            <div style={{ padding:"20px 20px 14px", borderBottom:`1px solid ${T.border}`,
              display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <div style={{ width:38, height:38, borderRadius:"50%",
                  background:`linear-gradient(135deg,${T.primary},#0d8f66)`,
                  color:"#fff", display:"flex", alignItems:"center", justifyContent:"center",
                  fontSize:16, fontWeight:800 }}>
                  {user?.name?.[0] ?? "?"}
                </div>
                <div>
                  <div style={{ fontWeight:700, fontSize:14, color:T.ink }}>{user?.name}</div>
                  <div style={{ fontSize:11, color:T.inkL, textTransform:"capitalize" }}>{user?.role}</div>
                </div>
              </div>
              <button onClick={() => setMenuOpen(false)}
                style={{ background:"none", border:"none", fontSize:24,
                  color:T.inkL, cursor:"pointer", lineHeight:1, padding:4 }}>×</button>
            </div>

            {/* Nav items */}
            <div style={{ flex:1, paddingTop:8 }}>
              {navLinks.map(l => (
                <div key={l.label} onClick={() => go(l.fn)}
                  style={{ display:"flex", alignItems:"center", gap:14,
                    padding:"14px 20px", cursor:"pointer",
                    background: l.active ? T.primaryL : "transparent",
                    borderLeft: l.active ? `3px solid ${T.primary}` : "3px solid transparent",
                    transition:"background .15s" }}>
                  <span style={{ fontSize:20, width:24, textAlign:"center" }}>{l.icon}</span>
                  <span style={{ fontSize:14, fontWeight: l.active ? 700 : 500,
                    color: l.active ? T.primary : T.ink }}>{l.label}</span>
                  {l.active && <span style={{ marginLeft:"auto", color:T.primary }}>›</span>}
                </div>
              ))}
            </div>

            {/* Footer */}
            <div style={{ padding:16, borderTop:`1px solid ${T.border}` }}>
              {user?.role === "student" && !isPremium && (
                <button onClick={() => go(onUpgrade)}
                  style={{ width:"100%", background:`linear-gradient(135deg,${T.primary},#0d8f66)`,
                    color:"#fff", border:"none", borderRadius:12, padding:"13px",
                    fontWeight:700, fontSize:14, cursor:"pointer",
                    fontFamily:"inherit", marginBottom:10 }}>
                  ⚡ Upgrade to Premium
                </button>
              )}
              <button onClick={() => go(onLogout)}
                style={{ width:"100%", background:"#fff", color:T.red,
                  border:`1.5px solid ${T.red}30`, borderRadius:12, padding:"12px",
                  fontWeight:700, fontSize:14, cursor:"pointer", fontFamily:"inherit" }}>
                Sign Out
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}


// ─── AUTH MODAL ───────────────────────────────────────────────────────────────

// ─── RESET PASSWORD MODAL ──────────────────────────────────────────────────────
// Shown when user lands back from the Supabase reset email link (?reset=1)
function ResetPasswordModal({ onClose, onDone }) {
  const [password, setPassword]   = useState("");
  const [confirm,  setConfirm]    = useState("");
  const [loading,  setLoading]    = useState(false);
  const [error,    setError]      = useState("");
  const [done,     setDone]       = useState(false);

  const strength = (p) => {
    let s = 0;
    if (p.length >= 8)          s++;
    if (/[A-Z]/.test(p))        s++;
    if (/[0-9]/.test(p))        s++;
    if (/[^A-Za-z0-9]/.test(p)) s++;
    return s; // 0–4
  };
  const pw_strength = strength(password);
  const STRENGTH_LABEL = ["Too short","Weak","Fair","Good","Strong"];
  const STRENGTH_COLOR = ["#dc2626","#f97316","#f59e0b","#16a34a","#0a6e4e"];

  const submit = async () => {
    setError("");
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setError("Passwords don't match."); return; }
    setLoading(true);
    const { error } = await supabase.auth.updateUser({ password });
    setLoading(false);
    if (error) { setError(error.message); return; }
    setDone(true);
    setTimeout(onDone, 2200);
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth:420 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:20 }}>
          <div>
            <Logo size={28} fontSize={16} />
            <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:22, fontWeight:800, marginTop:12, marginBottom:4 }}>
              Set New Password
            </h2>
            <p style={{ fontSize:13, color:T.inkL }}>Choose a strong password for your account.</p>
          </div>
          <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", color:T.inkL, fontSize:22 }}>×</button>
        </div>

        {done ? (
          <div style={{ textAlign:"center", padding:"20px 0" }}>
            <div style={{ fontSize:48, marginBottom:12 }}>✅</div>
            <div style={{ fontWeight:800, fontSize:16, marginBottom:6 }}>Password updated!</div>
            <p style={{ fontSize:13, color:T.inkL }}>Redirecting you to login…</p>
          </div>
        ) : (
          <>
            <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
              <div>
                <label>New Password *</label>
                <input className="input" type="password" placeholder="At least 8 characters"
                  value={password} onChange={e=>setPassword(e.target.value)} />
                {password && (
                  <div style={{ marginTop:6 }}>
                    <div style={{ display:"flex", gap:4, marginBottom:4 }}>
                      {[1,2,3,4].map(i => (
                        <div key={i} style={{ flex:1, height:4, borderRadius:100,
                          background: pw_strength >= i ? STRENGTH_COLOR[pw_strength] : T.border,
                          transition:"background .3s" }} />
                      ))}
                    </div>
                    <div style={{ fontSize:11, color:STRENGTH_COLOR[pw_strength], fontWeight:700 }}>
                      {STRENGTH_LABEL[pw_strength]}
                    </div>
                  </div>
                )}
              </div>
              <div>
                <label>Confirm Password *</label>
                <input className="input" type="password" placeholder="Repeat your password"
                  value={confirm} onChange={e=>setConfirm(e.target.value)}
                  onKeyDown={e=>e.key==="Enter"&&submit()}
                  style={{ borderColor: confirm && confirm!==password ? T.red : undefined }} />
                {confirm && confirm !== password && (
                  <p style={{ fontSize:11, color:T.red, marginTop:4 }}>Passwords don't match</p>
                )}
              </div>
            </div>

            {error && <div style={{ marginTop:12, padding:"10px 14px", background:T.redL,
              border:`1px solid #fecaca`, borderRadius:8, fontSize:13, color:T.red }}>{error}</div>}

            <button className="btn btn-primary" style={{ width:"100%", marginTop:20, padding:"13px", fontSize:15 }}
              onClick={submit} disabled={loading || !password || !confirm}>
              {loading ? "Updating…" : "Update Password →"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export function AuthModal({ mode, setMode, onLogin, onClose }) {
  const [form, setForm]               = useState({ name:"",email:"",password:"",role:"student",company:"",school:"",university_id:"",custom_school:"" });
  const [agreed, setAgreed]           = useState(false);
  const [showGoogleTerms, setShowGoogleTerms] = useState(false);
  const [universities, setUniversities] = useState([]);
  const [uniSearch, setUniSearch]     = useState("");
  const [showPwd, setShowPwd]         = useState(false);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState("");
  const [resetSent, setResetSent]     = useState(false);
  const isLogin  = mode === "login";
  const isForgot = mode === "forgot";

  useEffect(() => {
    supabase.from("universities").select("id,name,short_name,city").order("name")
      .then(({ data }) => setUniversities(data || []));
  }, []);

  const filteredUnis = universities.filter(u =>
    u.name.toLowerCase().includes(uniSearch.toLowerCase()) ||
    (u.short_name||"").toLowerCase().includes(uniSearch.toLowerCase())
  ).slice(0, 8);

  const sendReset = async () => {
    if (!form.email) { setError("Enter your email address first."); return; }
    setLoading(true); setError("");
    const { error } = await supabase.auth.resetPasswordForEmail(form.email, {
      redirectTo: "https://philippineinternships.com/?reset=1",
    });
    setLoading(false);
    if (error) { setError(error.message); return; }
    setResetSent(true);
  };

  const submit = async () => {
    setError("");
    if (!form.email || !form.password) { setError("Please fill all required fields."); return; }
    if (!isLogin && !form.name) { setError("Full name is required."); return; }
    if (!isLogin && !agreed) { setError("Please agree to the Terms and Privacy Policy."); return; }
    setLoading(true);
    if (isLogin) {
      const { data, error } = await supabase.auth.signInWithPassword({ email: form.email, password: form.password });
      if (error) { setError(error.message); setLoading(false); return; }
      const u = data.user;
      const { data: prof } = await supabase.from("profiles").select("role, full_name").eq("id", u.id).single();
      onLogin({ id: u.id, name: prof?.full_name || u.user_metadata?.full_name || form.email.split("@")[0], email: u.email, role: prof?.role || u.user_metadata?.role || "student" });
    } else {
      const { data, error } = await supabase.auth.signUp({ email: form.email, password: form.password, options: { data: { full_name: form.name, role: form.role } } });
      if (error) {
        if (error.message?.includes("already registered")) {
          setError("This email is already registered. Please sign in instead.");
        } else {
          setError(error.message);
        }
        setLoading(false); return;
      }
      const u = data.user;
      if (!u) { setError("Account created — please check your email to confirm, then sign in."); setLoading(false); return; }
      await supabase.from("profiles").upsert({
        id: u.id, email: form.email, full_name: form.name, role: form.role,
        university_id: (form.university_id && form.university_id !== "other") ? form.university_id : null,
        company_name: form.company || null,
        terms_agreed_at: new Date().toISOString(),
        privacy_agreed_at: new Date().toISOString(),
        consent_method: "email_signup",
      });
      sendWelcomeEmail(supabase, { name: form.name, email: u.email, role: form.role });
      onLogin({ id: u.id, name: form.name, email: u.email, role: form.role, isNew: true });
    }
    setLoading(false);
  };

  const handleGoogleOAuth = async () => {
    const isCapacitor = window.Capacitor?.isNativePlatform?.() ||
      window.location.origin.includes("localhost") ||
      window.location.origin.includes("capacitor");
    const redirectTo = isCapacitor
      ? "https://philippineinternships.com/?auth=callback"
      : window.location.origin + "/?auth=callback";
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo, queryParams: { access_type:"offline", prompt:"select_account" } }
    });
    if (error) setError(error.message);
  };

  // ── Reusable styles ──────────────────────────────────────────────────────
  const inputStyle = {
    width:"100%", padding:"11px 14px",
    border:"1.5px solid #e2e8e5", borderRadius:10,
    fontSize:14, color:"#0f1923", background:"#fff",
    outline:"none", fontFamily:"inherit", boxSizing:"border-box",
    transition:"border-color .2s",
  };
  const labelStyle = {
    fontSize:12, fontWeight:700, color:"#6b7a8d",
    display:"block", marginBottom:5, textTransform:"uppercase",
    letterSpacing:".04em",
  };
  const btnPrimary = {
    width:"100%", padding:"13px",
    background:"linear-gradient(135deg,#0a6e4e,#084f38)",
    color:"#fff", border:"none", borderRadius:10,
    fontWeight:800, fontSize:14, cursor:"pointer",
    fontFamily:"inherit", display:"flex",
    alignItems:"center", justifyContent:"center",
    gap:8, transition:"opacity .2s",
  };

  // ── Left panel features by role ──────────────────────────────────────────
  const FEATURES = {
    student: [
      { icon:"🎯", title:"AI Job Matching",   desc:"Get matched to OJT positions based on your course and skills" },
      { icon:"📓", title:"Digital Logbook",   desc:"Log hours, get supervisor verification, track progress" },
      { icon:"📜", title:"OJT Certificate",   desc:"Download your official certificate when you complete OJT" },
      { icon:"🤖", title:"AI Career Coach",   desc:"Get resume tips and interview prep from Coach Pia" },
    ],
    employer: [
      { icon:"📋", title:"Post OJT Jobs",     desc:"Reach thousands of qualified students from top universities" },
      { icon:"🔍", title:"Talent Pool",       desc:"Browse and invite students directly based on skills and course" },
      { icon:"🤖", title:"AI Screening",      desc:"AI ranks applicants by fit score to save you time" },
      { icon:"✅", title:"Verified Platform", desc:"All students are enrolled in CHED-accredited universities" },
    ],
  };
  const features = FEATURES[form.role] || FEATURES.student;

  return (
    <div style={{
      position:"fixed", inset:0, zIndex:1000,
      display:"flex", fontFamily:"'DM Sans',sans-serif",
      background:"#0a6e4e",
    }}>
      <style>{`
        @media (max-width: 640px) {
          .auth-left-panel { display: none !important; }
          .auth-right-panel {
            padding: 24px 20px !important;
            justify-content: flex-start !important;
            padding-top: 48px !important;
          }
          .auth-right-panel .auth-inner { max-width: 100% !important; }
        }
      `}</style>
      {/* ── LEFT PANEL ─────────────────────────────────────────── */}
      <div className="auth-left-panel" style={{
        flex:"0 0 42%", minWidth:0,
        background:"linear-gradient(160deg,#063d2b 0%,#0a6e4e 50%,#0d8f66 100%)",
        padding:"40px 44px",
        display:"flex", flexDirection:"column",
        position:"relative", overflow:"hidden",
      }}>
        {/* Background rings */}
        {[400,280,160].map((s,i) => (
          <div key={i} style={{
            position:"absolute",
            bottom: -s/3, right: -s/4,
            width:s, height:s,
            borderRadius:"50%",
            border:`1px solid rgba(255,255,255,${0.04 + i*0.02})`,
            pointerEvents:"none",
          }}/>
        ))}

        {/* Logo */}
        <div style={{ marginBottom:48 }}>
          <img src="/OJT_logo.png" alt="OJT Hub"
            style={{ height:52, objectFit:"contain",
              filter:"brightness(0) invert(1)" }}/>
        </div>

        {/* Headline */}
        <div style={{ flex:1 }}>
          <h1 style={{
            fontFamily:"'Fraunces',serif",
            fontSize:"clamp(24px,2.5vw,34px)",
            fontWeight:900, color:"#fff",
            lineHeight:1.2, marginBottom:12,
          }}>
            {isLogin
              ? "Welcome back to OJT Hub"
              : form.role === "employer"
                ? "Start hiring OJT interns today"
                : "Your OJT journey starts here"}
          </h1>
          <p style={{ fontSize:15, color:"rgba(255,255,255,.65)", lineHeight:1.7, marginBottom:40 }}>
            {isLogin
              ? "Sign in to access your dashboard, track your progress, and manage your OJT."
              : form.role === "employer"
                ? "Post jobs, find top students, and manage your internship program."
                : "Find internship opportunities, track your hours, and build your career."}
          </p>

          {/* Features */}
          <div style={{ display:"flex", flexDirection:"column", gap:18 }}>
            {features.map((f, i) => (
              <div key={i} style={{ display:"flex", gap:14, alignItems:"flex-start" }}>
                <div style={{
                  width:40, height:40, borderRadius:12, flexShrink:0,
                  background:"rgba(255,255,255,.12)",
                  display:"flex", alignItems:"center",
                  justifyContent:"center", fontSize:20,
                }}>
                  {f.icon}
                </div>
                <div>
                  <div style={{ fontWeight:700, fontSize:14, color:"#fff", marginBottom:2 }}>
                    {f.title}
                  </div>
                  <div style={{ fontSize:12, color:"rgba(255,255,255,.55)", lineHeight:1.5 }}>
                    {f.desc}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom badge */}
        <div style={{
          marginTop:40, display:"flex", alignItems:"center", gap:10,
          padding:"12px 16px", background:"rgba(255,255,255,.08)",
          borderRadius:12, border:"1px solid rgba(255,255,255,.12)",
        }}>
          <span style={{ fontSize:22 }}>🇵🇭</span>
          <div style={{ fontSize:12, color:"rgba(255,255,255,.7)" }}>
            <strong style={{ color:"#fff" }}>Philippines' #1 OJT Platform</strong>
            <br/>Trusted by students from 60+ universities
          </div>
        </div>
      </div>

      {/* ── RIGHT PANEL ─────────────────────────────────────────── */}
      <div className="auth-right-panel" style={{
        flex:1, background:"#fff",
        display:"flex", flexDirection:"column",
        alignItems:"center", justifyContent:"center",
        padding:"40px clamp(24px,5vw,60px)",
        overflowY:"auto",
      }}>
        {/* Close button */}
        <button onClick={onClose} style={{
          position:"absolute", top:20, right:20,
          background:"rgba(0,0,0,.06)", border:"none",
          width:36, height:36, borderRadius:"50%",
          cursor:"pointer", fontSize:18, color:"#666",
          display:"flex", alignItems:"center", justifyContent:"center",
        }}>×</button>

        <div className="auth-inner" style={{ width:"100%", maxWidth:400 }}>

          {/* Header */}
          <div style={{ marginBottom:28 }}>
            <h2 style={{
              fontFamily:"'Fraunces',serif",
              fontSize:26, fontWeight:900, color:"#0f1923", marginBottom:6,
            }}>
              {isForgot ? "Reset your password"
                : isLogin ? "Sign in to your account"
                : "Create your account"}
            </h2>
            <p style={{ fontSize:13, color:"#6b7a8d" }}>
              {isForgot ? "Enter your email and we'll send a reset link"
                : isLogin ? "Welcome back! Enter your credentials below."
                : "Join OJT Hub — it's free for students."}
            </p>
          </div>

          {/* Forgot password */}
          {isForgot && (
            <div>
              {resetSent ? (
                <div style={{ background:"#f0fdf4", border:"1.5px solid #16a34a",
                  borderRadius:12, padding:20, textAlign:"center" }}>
                  <div style={{ fontSize:32, marginBottom:8 }}>📧</div>
                  <div style={{ fontWeight:700, color:"#16a34a", marginBottom:4 }}>Reset link sent!</div>
                  <div style={{ fontSize:13, color:"#15803d" }}>Check your email inbox and spam folder.</div>
                </div>
              ) : (
                <>
                  {error && <div style={{ background:"#fee2e2", color:"#dc2626", padding:"10px 14px", borderRadius:9, fontSize:13, marginBottom:14 }}>{error}</div>}
                  <label style={{ fontSize:12, fontWeight:700, color:"#6b7a8d", display:"block", marginBottom:5 }}>Email Address</label>
                  <input style={inputStyle} type="email" placeholder="you@email.com"
                    value={form.email} onChange={e=>setForm({...form,email:e.target.value})}/>
                  <button onClick={sendReset} disabled={loading} style={btnPrimary}>
                    {loading ? "Sending…" : "Send Reset Link →"}
                  </button>
                </>
              )}
              <button onClick={() => setMode("login")}
                style={{ background:"none", border:"none", color:"#0a6e4e",
                  fontSize:13, fontWeight:700, cursor:"pointer",
                  marginTop:16, fontFamily:"inherit" }}>
                ← Back to Sign In
              </button>
            </div>
          )}

          {/* Main form */}
          {!isForgot && (
            <>
              {/* Role selector — signup only */}
              {!isLogin && (
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:20 }}>
                  {["student","employer"].map(r => (
                    <div key={r} onClick={() => setForm({...form,role:r})}
                      style={{
                        border:`2px solid ${form.role===r?"#0a6e4e":"#e2e8e5"}`,
                        borderRadius:12, padding:"14px 16px", cursor:"pointer",
                        background:form.role===r?"#e8f5f0":"#fff",
                        transition:"all .18s",
                      }}>
                      <div style={{ fontSize:22, marginBottom:4 }}>{r==="student"?"🎓":"🏢"}</div>
                      <div style={{ fontWeight:700, fontSize:13,
                        color:form.role===r?"#0a6e4e":"#0f1923" }}>
                        {r==="student"?"OJT Student":"Employer"}
                      </div>
                      <div style={{ fontSize:11, color:"#6b7a8d", marginTop:2 }}>
                        {r==="student"?"Find internships":"Post jobs & hire"}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Error */}
              {error && (
                <div style={{ background:"#fee2e2", color:"#dc2626",
                  padding:"10px 14px", borderRadius:9, fontSize:13,
                  marginBottom:14 }}>
                  {error}
                </div>
              )}

              {/* Google OAuth */}
              {showGoogleTerms && (
                <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.5)",
                  zIndex:9999, display:"flex", alignItems:"center",
                  justifyContent:"center", padding:20 }}>
                  <div style={{ background:"#fff", borderRadius:16, padding:28,
                    maxWidth:420, width:"100%", boxShadow:"0 24px 64px rgba(0,0,0,.2)" }}>
                    <h3 style={{ fontFamily:"'Fraunces',serif", fontSize:18,
                      fontWeight:900, textAlign:"center", marginBottom:16 }}>
                      Before you continue
                    </h3>
                    <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:20 }}>
                      {[
                        { icon:"📋", text:<>I agree to the <a href="/terms" target="_blank" rel="noreferrer" style={{color:"#0a6e4e",fontWeight:700}}>Terms of Service</a></> },
                        { icon:"🔒", text:<>I agree to the <a href="/privacy" target="_blank" rel="noreferrer" style={{color:"#0a6e4e",fontWeight:700}}>Privacy Policy</a></> },
                        { icon:"🇵🇭", text:"I consent to data processing under the Philippine Data Privacy Act (RA 10173)" },
                      ].map((item,i) => (
                        <div key={i} style={{ display:"flex", gap:10, alignItems:"flex-start",
                          padding:"10px 12px", background:"#f7f9f8",
                          borderRadius:9, fontSize:13, color:"#2d3d4f" }}>
                          <span style={{ fontSize:16, flexShrink:0 }}>{item.icon}</span>
                          <span>{item.text}</span>
                        </div>
                      ))}
                    </div>
                    <div style={{ display:"flex", gap:10 }}>
                      <button onClick={() => setShowGoogleTerms(false)}
                        style={{ flex:1, padding:"11px", background:"#fff",
                          border:"1.5px solid #e2e8e5", borderRadius:10,
                          fontWeight:700, fontSize:13, cursor:"pointer", fontFamily:"inherit" }}>
                        Cancel
                      </button>
                      <button onClick={() => { setShowGoogleTerms(false); handleGoogleOAuth(); }}
                        style={{ flex:2, padding:"11px",
                          background:"linear-gradient(135deg,#0a6e4e,#084f38)",
                          color:"#fff", border:"none", borderRadius:10,
                          fontWeight:800, fontSize:13, cursor:"pointer", fontFamily:"inherit" }}>
                        I Agree — Continue →
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <button
                onClick={() => isLogin ? handleGoogleOAuth() : setShowGoogleTerms(true)}
                style={{ width:"100%", padding:"11px 16px",
                  border:"1.5px solid #e2e8e5", borderRadius:10,
                  background:"#fff", cursor:"pointer", fontFamily:"inherit",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  gap:10, fontSize:14, fontWeight:600, color:"#0f1923",
                  marginBottom:16, transition:"border-color .15s" }}
                onMouseEnter={e=>e.currentTarget.style.borderColor="#0a6e4e"}
                onMouseLeave={e=>e.currentTarget.style.borderColor="#e2e8e5"}
              >
                <svg width="18" height="18" viewBox="0 0 48 48">
                  <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.2l6.8-6.8C35.6 2.2 30.1 0 24 0 14.7 0 6.7 5.5 2.9 13.5l7.9 6.1C12.6 13.3 17.9 9.5 24 9.5z"/>
                  <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v8.5h12.7c-.6 3-2.3 5.5-4.8 7.2l7.5 5.8c4.4-4 7.1-10 7.1-17z"/>
                  <path fill="#FBBC05" d="M10.8 28.4A14.4 14.4 0 0 1 9.5 24c0-1.5.3-3 .7-4.4L2.3 13.5A23.9 23.9 0 0 0 0 24c0 3.9.9 7.5 2.9 10.5l7.9-6.1z"/>
                  <path fill="#34A853" d="M24 48c6.1 0 11.2-2 14.9-5.4l-7.5-5.8c-2 1.4-4.6 2.2-7.4 2.2-6.1 0-11.3-3.8-13.2-9.1l-7.9 6.1C6.7 42.5 14.7 48 24 48z"/>
                </svg>
                {isLogin ? "Continue with Google" : "Sign up with Google"}
              </button>

              {/* Divider */}
              <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:16 }}>
                <div style={{ flex:1, height:1, background:"#e2e8e5" }}/>
                <span style={{ fontSize:12, color:"#6b7a8d" }}>or with email</span>
                <div style={{ flex:1, height:1, background:"#e2e8e5" }}/>
              </div>

              {/* Name (signup) */}
              {!isLogin && (
                <div style={{ marginBottom:14 }}>
                  <label style={labelStyle}>Full Name *</label>
                  <input style={inputStyle} placeholder="Juan dela Cruz"
                    value={form.name} onChange={e=>setForm({...form,name:e.target.value})}/>
                </div>
              )}

              {/* University (student signup) */}
              {!isLogin && form.role === "student" && (
                <div style={{ marginBottom:14 }}>
                  <label style={labelStyle}>University / School</label>
                  <input style={inputStyle}
                    placeholder="Search your university…"
                    value={uniSearch}
                    onChange={e => { setUniSearch(e.target.value); if (form.university_id) setForm(f=>({...f,university_id:""})); }}
                  />
                  {uniSearch && !form.university_id && filteredUnis.length > 0 && (
                    <div style={{ border:"1.5px solid #e2e8e5", borderTop:"none",
                      borderRadius:"0 0 10px 10px", background:"#fff",
                      maxHeight:160, overflowY:"auto" }}>
                      {filteredUnis.map(u => (
                        <div key={u.id}
                          onClick={() => { setForm(f=>({...f,university_id:u.id})); setUniSearch(u.name); }}
                          style={{ padding:"9px 14px", cursor:"pointer", fontSize:13,
                            borderBottom:"1px solid #f0f0f0" }}
                          onMouseEnter={e=>e.currentTarget.style.background="#f0faf6"}
                          onMouseLeave={e=>e.currentTarget.style.background="#fff"}>
                          <strong>{u.short_name||u.name}</strong>
                          {u.city && <span style={{color:"#6b7a8d",fontSize:12}}> · {u.city}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Company (employer signup) */}
              {!isLogin && form.role === "employer" && (
                <div style={{ marginBottom:14 }}>
                  <label style={labelStyle}>Company Name *</label>
                  <input style={inputStyle} placeholder="e.g. Accenture Philippines"
                    value={form.company} onChange={e=>setForm({...form,company:e.target.value})}/>
                </div>
              )}

              {/* Email */}
              <div style={{ marginBottom:14 }}>
                <label style={labelStyle}>Email Address *</label>
                <input style={inputStyle} type="email" placeholder="you@email.com"
                  value={form.email} onChange={e=>setForm({...form,email:e.target.value})}/>
              </div>

              {/* Password */}
              <div style={{ marginBottom: isLogin ? 8 : 16 }}>
                <label style={labelStyle}>Password *</label>
                <div style={{ position:"relative" }}>
                  <input style={{...inputStyle, paddingRight:44}}
                    type={showPwd?"text":"password"}
                    placeholder={isLogin?"••••••••":"Min. 8 characters"}
                    value={form.password}
                    onChange={e=>setForm({...form,password:e.target.value})}
                    onKeyDown={e=>e.key==="Enter"&&submit()}/>
                  <button onClick={()=>setShowPwd(p=>!p)}
                    style={{ position:"absolute", right:12, top:"50%",
                      transform:"translateY(-50%)", background:"none",
                      border:"none", cursor:"pointer", fontSize:16,
                      color:"#6b7a8d" }}>
                    {showPwd?"🙈":"👁"}
                  </button>
                </div>
              </div>

              {/* Forgot password link */}
              {isLogin && (
                <div style={{ textAlign:"right", marginBottom:16 }}>
                  <button onClick={() => setMode("forgot")}
                    style={{ background:"none", border:"none", color:"#0a6e4e",
                      fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                    Forgot password?
                  </button>
                </div>
              )}

              {/* Terms consent (signup) */}
              {!isLogin && (
                <div onClick={() => setAgreed(p=>!p)}
                  style={{ display:"flex", gap:12, alignItems:"flex-start",
                    padding:"12px 14px", borderRadius:10, cursor:"pointer", marginBottom:16,
                    background:agreed?"#e8f5f0":"#fffbeb",
                    border:`1.5px solid ${agreed?"#0a6e4e":"#f5a623"}`,
                    transition:"all .2s" }}>
                  <div style={{ width:20, height:20, borderRadius:5, flexShrink:0, marginTop:1,
                    border:`2px solid ${agreed?"#0a6e4e":"#f5a623"}`,
                    background:agreed?"#0a6e4e":"#fff",
                    display:"flex", alignItems:"center", justifyContent:"center" }}>
                    {agreed && <span style={{ color:"#fff", fontSize:12, fontWeight:900 }}>✓</span>}
                  </div>
                  <span style={{ fontSize:12, color:"#2d3d4f", lineHeight:1.5 }}>
                    I agree to the{" "}
                    <a href="/terms" target="_blank" rel="noreferrer"
                      onClick={e=>e.stopPropagation()}
                      style={{ color:"#0a6e4e", fontWeight:700 }}>Terms of Service</a>
                    {" "}and{" "}
                    <a href="/privacy" target="_blank" rel="noreferrer"
                      onClick={e=>e.stopPropagation()}
                      style={{ color:"#0a6e4e", fontWeight:700 }}>Privacy Policy</a>
                    , and consent to data processing under the Philippine Data Privacy Act (RA 10173).
                  </span>
                </div>
              )}

              {/* Submit */}
              <button onClick={submit}
                disabled={loading || (!isLogin && !agreed)}
                style={{...btnPrimary,
                  opacity:loading||(!isLogin&&!agreed)?0.55:1,
                  cursor:loading||(!isLogin&&!agreed)?"not-allowed":"pointer",
                }}>
                {loading
                  ? <><span style={{ width:16,height:16,border:"2px solid rgba(255,255,255,.4)",borderTopColor:"#fff",borderRadius:"50%",display:"inline-block",animation:"spin .7s linear infinite",marginRight:8 }}/>Please wait…</>
                  : isLogin ? "Sign In →" : "Create Account →"
                }
              </button>

              {/* Switch mode */}
              <div style={{ textAlign:"center", marginTop:20, fontSize:13, color:"#6b7a8d" }}>
                {isLogin ? (
                  <>Don't have an account?{" "}
                    <button onClick={() => setMode("signup")}
                      style={{ background:"none", border:"none", color:"#0a6e4e",
                        fontWeight:700, cursor:"pointer", fontFamily:"inherit", fontSize:13 }}>
                      Sign up free
                    </button>
                  </>
                ) : (
                  <>Already have an account?{" "}
                    <button onClick={() => setMode("login")}
                      style={{ background:"none", border:"none", color:"#0a6e4e",
                        fontWeight:700, cursor:"pointer", fontFamily:"inherit", fontSize:13 }}>
                      Sign in
                    </button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin{to{transform:rotate(360deg)}}
        @media(max-width:640px){
          [data-auth-left]{display:none!important}
          [data-auth-right]{padding:24px!important}
        }
      `}</style>
    </div>
  );
}
function LandingPage({ setPage, onSignup }) {
  return (
    <div style={{ paddingTop:64 }}>

      {/* ══════════ HERO ══════════ */}
      <section style={{ position:"relative", minHeight:"94vh", display:"flex", alignItems:"center", padding:"60px clamp(16px,5vw,72px)", overflow:"hidden", background:"#f7f9f8" }}>
        {/* Mesh background */}
        <div style={{ position:"absolute", inset:0, background:`radial-gradient(ellipse 70% 60% at 8% 55%, rgba(10,110,78,.08) 0%, transparent 65%), radial-gradient(ellipse 55% 65% at 88% 12%, rgba(245,166,35,.07) 0%, transparent 65%), radial-gradient(ellipse 45% 45% at 55% 90%, rgba(10,110,78,.04) 0%, transparent 60%)`, pointerEvents:"none" }} />
        {/* Grid texture */}
        <div style={{ position:"absolute", inset:0, backgroundImage:`linear-gradient(rgba(10,110,78,.025) 1px, transparent 1px), linear-gradient(90deg, rgba(10,110,78,.025) 1px, transparent 1px)`, backgroundSize:"40px 40px", pointerEvents:"none" }} />
        {/* Decorative rings */}
        <div style={{ position:"absolute", top:80, right:"4%", width:420, height:420, borderRadius:"50%", border:`1px solid rgba(10,110,78,.09)`, pointerEvents:"none" }} />
        <div style={{ position:"absolute", top:150, right:"9%", width:270, height:270, borderRadius:"50%", border:`1px solid rgba(10,110,78,.06)`, pointerEvents:"none" }} />
        <div style={{ position:"absolute", bottom:60, left:"2%", width:180, height:180, borderRadius:"50%", border:`1px solid rgba(245,166,35,.1)`, pointerEvents:"none" }} />

        {/* Floating job card */}
        <div className="float-a" style={{ position:"absolute", top:"12%", right:"3%", zIndex:2 }}>
          <div className="card" style={{ padding:"14px 16px", boxShadow:T.shadowLg, maxWidth:230 }}>
            <div style={{ display:"flex", gap:10, alignItems:"center", marginBottom:10 }}>
              <div style={{ width:34, height:34, background:T.primaryL, borderRadius:9, display:"flex", alignItems:"center", justifyContent:"center", fontSize:16 }}>💻</div>
              <div><div style={{ fontSize:12, fontWeight:700 }}>SWE Intern</div><div style={{ fontSize:11, color:T.inkL }}>Accenture PH · BGC</div></div>
            </div>
            <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
              <span style={{ fontSize:11, background:T.accentL, color:T.accentD, fontWeight:700, padding:"2px 9px", borderRadius:100 }}>₱18k/mo</span>
              <span style={{ fontSize:11, color:T.green, fontWeight:700 }}>● Live</span>
            </div>
          </div>
        </div>

        {/* Floating match card */}
        <div className="float-b" style={{ position:"absolute", bottom:"22%", right:"1%", zIndex:2 }}>
          <div className="card" style={{ padding:"12px 16px", boxShadow:T.shadow, maxWidth:210 }}>
            <div style={{ fontSize:10, color:T.inkL, marginBottom:3 }}>🤖 AI Match Score</div>
            <div style={{ fontWeight:800, fontSize:20, color:T.primary }}>94% match</div>
            <div style={{ fontSize:11, color:T.inkL, marginTop:2 }}>Data Science OJT · Globe</div>
          </div>
        </div>

        {/* Floating logbook card */}
        <div className="float-c" style={{ position:"absolute", top:"50%", right:"2%", zIndex:2, display:"none" }}>
          <div className="card" style={{ padding:"12px 16px", boxShadow:T.shadow, maxWidth:200 }}>
            <div style={{ fontSize:10, color:T.green, fontWeight:700, marginBottom:3 }}>✅ Hours Approved</div>
            <div style={{ fontWeight:800, fontSize:17 }}>320 / 486 hrs</div>
            <div style={{ background:T.border, borderRadius:100, height:5, marginTop:6, overflow:"hidden" }}>
              <div style={{ width:"66%", height:"100%", background:`linear-gradient(90deg,${T.primary},#0d8f66)`, borderRadius:100 }} />
            </div>
          </div>
        </div>

        <div style={{ maxWidth:1200, margin:"0 auto", width:"100%", position:"relative", zIndex:1 }}>
          <div style={{ maxWidth:680 }}>
            {/* Badge */}
            <div className="fade-up" style={{ display:"inline-flex", alignItems:"center", gap:8, background:"#fff", border:`1.5px solid ${T.border}`, borderRadius:100, padding:"8px 18px", marginBottom:26, fontSize:13, fontWeight:600, color:T.inkL, boxShadow:T.shadow }}>
              <span className="pulse-dot" style={{ width:8, height:8, background:T.green, borderRadius:"50%", display:"inline-block" }} />
              🇵🇭 Philippines' #1 OJT & Internship Portal
            </div>

            <h1 className="fade-up" style={{ fontFamily:"'Fraunces',serif", fontSize:"clamp(42px,6.5vw,78px)", fontWeight:900, lineHeight:1.05, letterSpacing:"-0.035em", marginBottom:22, color:T.ink, animationDelay:".08s" }}>
              Find Your OJT.<br />
              <span style={{ color:T.primary }}>Launch Your</span><br />
              <span style={{ fontStyle:"italic", color:T.accent }}>Career.</span>
            </h1>

            <p className="fade-up" style={{ fontSize:"clamp(15px,1.8vw,18px)", color:T.inkL, lineHeight:1.75, marginBottom:32, maxWidth:520, animationDelay:".16s" }}>
              The smartest way for Filipino students to find OJT, track hours, get endorsed, and land jobs — all in one platform.
            </p>

            <div className="fade-up" style={{ display:"flex", gap:12, marginBottom:36, flexWrap:"wrap", animationDelay:".24s" }}>
              <button className="btn btn-primary" style={{ fontSize:15, padding:"14px 34px", borderRadius:12 }} onClick={()=>setPage("jobs")}>Browse OJT Jobs →</button>
              <button className="btn btn-ghost" style={{ fontSize:15, padding:"14px 34px", borderRadius:12 }} onClick={onSignup}>Create Free Account</button>
            </div>

            {/* Search bar */}
            <div className="fade-up" style={{ background:"#fff", border:`1.5px solid ${T.border}`, borderRadius:14, padding:8, display:"flex", gap:8, boxShadow:T.shadowLg, maxWidth:560, animationDelay:".3s" }}>
              <div style={{ flex:1, position:"relative", display:"flex", alignItems:"center" }}>
                <svg style={{ position:"absolute", left:12 }} width="15" height="15" fill="none" stroke={T.inkL} strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
                <input className="input" style={{ paddingLeft:36, border:"none", borderRadius:8, fontSize:14, background:T.surface }} placeholder="Job title, skill, company…" onKeyDown={e=>e.key==="Enter"&&setPage("jobs")} />
              </div>
              <select className="input" style={{ border:"none", background:T.surface, borderRadius:8, fontSize:14, width:"auto", minWidth:130, cursor:"pointer" }}>
                <option>🌏 All Regions</option><option>🇵🇭 Philippines</option><option>🇺🇸 USA</option><option>🇸🇬 Singapore</option>
              </select>
              <button className="btn btn-primary" style={{ borderRadius:8, padding:"10px 18px", fontSize:14, whiteSpace:"nowrap" }} onClick={()=>setPage("jobs")}>Search</button>
            </div>

            <div style={{ display:"flex", gap:8, marginTop:14, flexWrap:"wrap" }}>
              <span style={{ fontSize:12, color:T.inkL, lineHeight:"26px" }}>Popular:</span>
              {["Software","IT","Data Analytics","Marketing","Finance","Design","HR","Accounting"].map(t=>(
                <span key={t} onClick={()=>setPage("jobs")} style={{ fontSize:12, background:"#fff", color:T.inkM, padding:"4px 14px", borderRadius:100, cursor:"pointer", fontWeight:600, border:`1px solid ${T.border}`, transition:"all .18s" }}
                  onMouseEnter={e=>{e.target.style.background=T.primaryL;e.target.style.borderColor=T.primary;e.target.style.color=T.primary;}}
                  onMouseLeave={e=>{e.target.style.background="#fff";e.target.style.borderColor=T.border;e.target.style.color=T.inkM;}}>{t}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══════════ TICKER ══════════ */}
      <div style={{ background:T.primary, padding:"12px 0", overflow:"hidden" }}>
        <div style={{ overflow:"hidden", whiteSpace:"nowrap" }}>
          <div className="ticker-inner">
            {[...Array(2)].map((_,ri)=>(
              <span key={ri}>
                {["SWE Intern · Accenture PH · ₱18k/mo","Data OJT · Globe · ₱16k/mo","UX OJT · Canva Manila · ₱15k/mo","Marketing OJT · Jollibee · ₱12k/mo","Finance OJT · BDO · ₱14k/mo","SWE Intern · Google · $40/hr","Cloud OJT · AWS · $35/hr","Biz Dev · Grab PH · ₱13k/mo","IT OJT · PLDT · ₱11k/mo","Design OJT · Lazada · ₱13k/mo"].map((item,i)=>(
                  <span key={i} style={{ fontSize:13, color:"rgba(255,255,255,.85)", fontWeight:500, padding:"0 28px" }}>
                    <span style={{ color:T.accent, marginRight:10 }}>✦</span>{item}
                  </span>
                ))}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* ══════════ STATS ══════════ */}
      <section style={{ padding:"72px clamp(16px,5vw,64px)", background:"#fff" }}>
        <div style={{ maxWidth:1100, margin:"0 auto" }}>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(175px,1fr))", gap:16 }}>
            {[
              {v:"2,400+",l:"Active OJT Listings",i:"📋",c:T.primary},
              {v:"380+",l:"Partner Companies",i:"🏢",c:T.accent},
              {v:"52+",l:"Partner Universities",i:"🎓",c:"#7c3aed"},
              {v:"94%",l:"Placement Rate",i:"🎯",c:T.green},
              {v:"12,000+",l:"Students Placed",i:"🇵🇭",c:T.blue},
            ].map((s,i)=>(
              <div key={i} className="stat-box fade-up" style={{ textAlign:"center", animationDelay:`${i*60}ms` }}>
                <div style={{ width:52, height:52, borderRadius:"50%", background:s.c+"15", display:"flex", alignItems:"center", justifyContent:"center", fontSize:24, margin:"0 auto 12px" }}>{s.i}</div>
                <div style={{ fontFamily:"'Fraunces',serif", fontSize:36, fontWeight:900, letterSpacing:"-0.03em", color:T.ink }}>{s.v}</div>
                <div style={{ fontSize:13, color:T.inkL, marginTop:4 }}>{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════ HOW IT WORKS — STUDENTS ══════════ */}
      <section style={{ padding:"80px clamp(16px,5vw,64px)", background:T.surface }}>
        <div style={{ maxWidth:1040, margin:"0 auto" }}>
          <div style={{ textAlign:"center", marginBottom:52 }}>
            <div style={{ fontSize:12, fontWeight:700, color:T.primary, letterSpacing:".1em", textTransform:"uppercase", marginBottom:10 }}>For Students</div>
            <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:"clamp(26px,4vw,42px)", fontWeight:900, letterSpacing:"-0.025em", marginBottom:14 }}>Land your OJT in 4 steps</h2>
            <p style={{ color:T.inkL, fontSize:15, maxWidth:500, margin:"0 auto" }}>Everything you need from search to signed certificate — in one platform.</p>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))", gap:24 }}>
            {[
              {n:"01",i:"📝",t:"Build Your Resume",d:"Smart OJT CV builder tailored for Filipino students. Add skills, education, and experience in minutes.",c:T.primary},
              {n:"02",i:"🔍",t:"Discover OJT Jobs",d:"Browse 2,400+ listings from local and global companies, filtered by course, location, and allowance.",c:T.accent},
              {n:"03",i:"📓",t:"Track Your Hours",d:"Log daily OJT hours, get employer sign-off, and have your coordinator approve entries — all digital.",c:"#7c3aed"},
              {n:"04",i:"🎓",t:"Get Your Certificate",d:"Auto-generate an official completion certificate the moment your coordinator approves your hours.",c:T.green},
            ].map((s,i)=>(
              <div key={i} className="card fade-up" style={{ padding:28, textAlign:"left", position:"relative", overflow:"hidden", animationDelay:`${i*80}ms` }}>
                <div style={{ fontFamily:"'Fraunces',serif", fontSize:64, fontWeight:900, color:T.border, position:"absolute", top:-10, right:10, lineHeight:1, userSelect:"none" }}>{s.n}</div>
                <div style={{ width:52, height:52, background:s.c+"18", border:`1.5px solid ${s.c}28`, borderRadius:14, display:"flex", alignItems:"center", justifyContent:"center", fontSize:24, marginBottom:18 }}>{s.i}</div>
                <h3 style={{ fontSize:16, fontWeight:800, marginBottom:10 }}>{s.t}</h3>
                <p style={{ fontSize:13, color:T.inkL, lineHeight:1.75 }}>{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════ FEATURES DEEP-DIVE ══════════ */}
      <section style={{ padding:"80px clamp(16px,5vw,64px)", background:"#fff" }}>
        <div style={{ maxWidth:1100, margin:"0 auto" }}>
          <div style={{ textAlign:"center", marginBottom:56 }}>
            <div style={{ fontSize:12, fontWeight:700, color:T.primary, letterSpacing:".1em", textTransform:"uppercase", marginBottom:10 }}>Full Platform</div>
            <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:"clamp(26px,4vw,42px)", fontWeight:900, letterSpacing:"-0.025em" }}>Everything in one place</h2>
          </div>

          {/* Feature rows */}
          {[
            {
              icon:"🤖", tag:"AI-Powered",
              title:"Claude AI Career Coach",
              desc:"Get personalized OJT advice, resume feedback, and interview prep from Coach Pia — our built-in AI career coach powered by Claude. Available 24/7, always free for students.",
              highlights:["Resume review & scoring","Interview question practice","OJT readiness check","Career path guidance"],
              color:T.primary, dark:false,
            },
            {
              icon:"📓", tag:"3-Way Workflow",
              title:"Digital OJT Logbook",
              desc:"Replace paper timesheets forever. Students log daily hours, employers verify entries, and university coordinators approve — creating a tamper-proof digital trail.",
              highlights:["Daily entry with mood & tasks","Employer sign-off per entry","Coordinator batch approval","Auto-generated weekly reports"],
              color:"#7c3aed", dark:false,
            },
            {
              icon:"🎓", tag:"For Universities",
              title:"University & Coordinator Portal",
              desc:"A dedicated dashboard for OJT coordinators and department heads to endorse students, monitor logbook progress, and approve hours — all without email back-and-forth.",
              highlights:["Student roster with progress bars","Endorsement management","Logbook approval queue","Departmental reporting"],
              color:T.accent, dark:false,
            },
          ].map((f,i)=>(
            <div key={i} className="fade-up" style={{ display:"grid", gridTemplateColumns:i%2===0?"1fr 1fr":"1fr 1fr", gap:56, alignItems:"center", marginBottom:72, direction:i%2===1?"rtl":"ltr" }}>
              <div style={{ direction:"ltr" }}>
                <div style={{ display:"inline-flex", alignItems:"center", gap:8, background:f.color+"15", border:`1px solid ${f.color}25`, borderRadius:100, padding:"5px 14px", fontSize:12, fontWeight:700, color:f.color, marginBottom:16 }}>
                  {f.icon} {f.tag}
                </div>
                <h3 style={{ fontFamily:"'Fraunces',serif", fontSize:"clamp(22px,3vw,32px)", fontWeight:900, letterSpacing:"-0.02em", marginBottom:14, lineHeight:1.2 }}>{f.title}</h3>
                <p style={{ fontSize:15, color:T.inkL, lineHeight:1.8, marginBottom:22 }}>{f.desc}</p>
                <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                  {f.highlights.map((h,hi)=>(
                    <div key={hi} style={{ display:"flex", gap:10, alignItems:"center" }}>
                      <div style={{ width:22, height:22, borderRadius:"50%", background:f.color+"20", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                        <span style={{ fontSize:11, color:f.color, fontWeight:900 }}>✓</span>
                      </div>
                      <span style={{ fontSize:14, color:T.inkM, fontWeight:500 }}>{h}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ direction:"ltr" }}>
                <div style={{ background:`linear-gradient(135deg, ${f.color}12, ${f.color}06)`, border:`1.5px solid ${f.color}20`, borderRadius:20, padding:32, minHeight:240, display:"flex", flexDirection:"column", justifyContent:"center", gap:14 }}>
                  <div style={{ fontSize:52, textAlign:"center", marginBottom:8 }}>{f.icon}</div>
                  {f.highlights.map((h,hi)=>(
                    <div key={hi} style={{ display:"flex", gap:12, alignItems:"center", background:"#fff", borderRadius:10, padding:"12px 16px", boxShadow:"0 2px 8px rgba(0,0,0,.06)" }}>
                      <div style={{ width:28, height:28, borderRadius:7, background:f.color+"20", display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, flexShrink:0 }}>
                        {["📝","✅","👁","📊"][hi]||"✓"}
                      </div>
                      <span style={{ fontSize:13, fontWeight:600, color:T.inkM }}>{h}</span>
                      <span style={{ marginLeft:"auto", fontSize:11, color:f.color, fontWeight:700 }}>✓</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ══════════ UNIVERSITY PARTNER LOGOS ══════════ */}
      <section style={{ padding:"56px clamp(16px,5vw,64px)", background:T.surface, borderTop:`1px solid ${T.border}` }}>
        <div style={{ maxWidth:1100, margin:"0 auto" }}>
          <div style={{ textAlign:"center", marginBottom:36 }}>
            <div style={{ fontSize:12, fontWeight:700, color:T.inkL, letterSpacing:".1em", textTransform:"uppercase", marginBottom:8 }}>Trusted by students from</div>
            <h3 style={{ fontFamily:"'Fraunces',serif", fontSize:24, fontWeight:800, color:T.ink }}>52+ Philippine Universities</h3>
          </div>
          <div style={{ display:"flex", gap:16, flexWrap:"wrap", justifyContent:"center", alignItems:"center" }}>
            {["De La Salle University","University of the Philippines","Ateneo de Manila","UST","FEU","Mapúa University","Adamson University","PUP","DLSU-D","Lyceum PH","San Beda","APC"].map((u,i)=>(
              <div key={i} style={{ background:"#fff", border:`1.5px solid ${T.border}`, borderRadius:10, padding:"10px 20px", fontSize:13, fontWeight:600, color:T.inkM, whiteSpace:"nowrap" }}>{u}</div>
            ))}
            <div style={{ background:T.primaryL, border:`1.5px solid ${T.primary}30`, borderRadius:10, padding:"10px 20px", fontSize:13, fontWeight:700, color:T.primary }}>+40 more →</div>
          </div>
        </div>
      </section>

      {/* ══════════ TESTIMONIALS ══════════ */}
      <section style={{ padding:"80px clamp(16px,5vw,64px)", background:"#fff" }}>
        <div style={{ maxWidth:1100, margin:"0 auto" }}>
          <div style={{ textAlign:"center", marginBottom:52 }}>
            <div style={{ fontSize:12, fontWeight:700, color:T.primary, letterSpacing:".1em", textTransform:"uppercase", marginBottom:10 }}>Student Stories</div>
            <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:"clamp(26px,4vw,42px)", fontWeight:900, letterSpacing:"-0.025em" }}>Real students. Real results.</h2>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(300px,1fr))", gap:20 }}>
            {[
              {name:"Mikhaela Santos",role:"BS Computer Science · DLSU",company:"Placed at Accenture PH",avatar:"MS",color:"#0a6e4e",
               quote:"I found my OJT at Accenture through OJT Hub in literally one week. The AI match score told me I was 91% compatible before I even applied. My coordinator loves how she can approve my logbook without printing anything."},
              {name:"Carlo Reyes",role:"BS Accountancy · UST",company:"Placed at BDO Unibank",avatar:"CR",color:"#7c3aed",
               quote:"The digital logbook is a game-changer. No more paper timesheets! My supervisor verifies my hours on his phone, and I got my completion certificate automatically. 10/10 would recommend."},
              {name:"Aisha Mangondato",role:"BS Marketing · Ateneo",company:"Placed at Jollibee Corporate",avatar:"AM",color:"#d97706",
               quote:"Coach Pia helped me fix my resume and prep for my OJT interview. I got accepted on my first application. The university endorsement flow is also super smooth — my coordinator approved my placement in 2 days."},
              {name:"Rafael Dela Cruz",role:"BS IT · PUP",company:"Placed at Globe Telecom",avatar:"RC",color:"#1d6fcc",
               quote:"As an OJT coordinator, this platform cut my admin work by 70%. I can see all my students' logbooks in one dashboard, approve hours in bulk, and the endorsement tracking is excellent."},
              {name:"Jasmine Ocampo",role:"BS HRM · FEU",company:"Placed at Shangri-La Hotel",avatar:"JO",color:"#16a34a",
               quote:"I was stressed about finding an OJT but OJT Hub showed me roles I never would have found on my own. The salary transparency feature is amazing — I found a ₱15k/mo hospitality OJT!"},
              {name:"Tristan Villanueva",role:"BS Engineering · Mapúa",company:"Placed at AECOM Philippines",avatar:"TV",color:"#dc2626",
               quote:"The job matching AI is scary accurate. It scored me 88% for an engineering OJT and I got the offer. The whole process from application to certificate was completely paperless."},
            ].map((t,i)=>(
              <div key={i} className="card fade-up" style={{ padding:24, animationDelay:`${i*60}ms` }}>
                <div style={{ display:"flex", gap:4, marginBottom:14 }}>
                  {[...Array(5)].map((_,si)=><span key={si} style={{ color:T.accent, fontSize:14 }}>★</span>)}
                </div>
                <p style={{ fontSize:13.5, color:T.inkM, lineHeight:1.75, marginBottom:18, fontStyle:"italic" }}>"{t.quote}"</p>
                <div style={{ display:"flex", alignItems:"center", gap:12, paddingTop:14, borderTop:`1px solid ${T.border}` }}>
                  <div style={{ width:38, height:38, borderRadius:"50%", background:t.color, color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:800, fontSize:13, flexShrink:0 }}>{t.avatar}</div>
                  <div>
                    <div style={{ fontWeight:700, fontSize:13 }}>{t.name}</div>
                    <div style={{ fontSize:11, color:T.inkL }}>{t.role}</div>
                    <div style={{ fontSize:11, color:t.color, fontWeight:700 }}>{t.company}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════ EMPLOYER CTA ══════════ */}
      <section style={{ padding:"80px clamp(16px,5vw,64px)", background:T.ink, position:"relative", overflow:"hidden" }}>
        <div style={{ position:"absolute", top:-80, right:-60, width:500, height:500, borderRadius:"50%", background:"rgba(10,110,78,.12)", pointerEvents:"none" }} />
        <div style={{ position:"absolute", bottom:-80, left:-60, width:380, height:380, borderRadius:"50%", background:"rgba(245,166,35,.07)", pointerEvents:"none" }} />
        <div style={{ maxWidth:1100, margin:"0 auto", display:"grid", gridTemplateColumns:"1fr 1fr", gap:60, alignItems:"center" }}>
          <div>
            <div style={{ fontSize:12, fontWeight:700, color:T.accent, letterSpacing:".1em", textTransform:"uppercase", marginBottom:14 }}>For Employers</div>
            <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:"clamp(28px,4vw,46px)", fontWeight:900, color:"#f7f9f8", letterSpacing:"-0.025em", lineHeight:1.15, marginBottom:18 }}>
              Hire top Filipino<br />OJT talent.
            </h2>
            <p style={{ fontSize:15, color:"rgba(247,249,248,.6)", lineHeight:1.8, marginBottom:32 }}>
              Post OJT listings and connect with qualified students from 52+ Philippine universities. Full applicant tracking, AI-powered screening, and verified logbook management included.
            </p>
            <div style={{ display:"flex", gap:12, flexWrap:"wrap", marginBottom:32 }}>
              <button className="btn btn-accent" style={{ fontSize:15, padding:"14px 32px", borderRadius:12 }} onClick={onSignup}>Post an OJT Job</button>
              <a href="/pricing" style={{ background:"transparent", color:"rgba(247,249,248,.8)", border:"1.5px solid rgba(247,249,248,.2)", padding:"13px 28px", borderRadius:12, fontSize:15, fontWeight:600, cursor:"pointer", fontFamily:"inherit", transition:"all .2s", textDecoration:"none", display:"inline-flex", alignItems:"center" }}>View Pricing</a>
            </div>
            {/* Social proof */}
            <div style={{ display:"flex", gap:24, flexWrap:"wrap" }}>
              {[["380+","Partner companies"],["3 min","To post a listing"],["48 hrs","Avg. first applicant"]].map(([v,l],i)=>(
                <div key={i}>
                  <div style={{ fontFamily:"'Fraunces',serif", fontSize:22, fontWeight:900, color:T.accent }}>{v}</div>
                  <div style={{ fontSize:12, color:"rgba(247,249,248,.45)" }}>{l}</div>
                </div>
              ))}
            </div>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            {[
              {i:"📊",t:"Applicant Tracking",d:"Manage all OJT applications in one place"},
              {i:"🤖",t:"AI Screening",d:"Claude-powered candidate match scoring"},
              {i:"📓",t:"Logbook Verification",d:"Sign off on student hours digitally"},
              {i:"🎓",t:"Endorsement System",d:"Work directly with university coordinators"},
              {i:"📈",t:"Analytics Dashboard",d:"Track post views, applications, hires"},
              {i:"✅",t:"Verification Badge",d:"Build trust with verified employer status"},
            ].map((f,i)=>(
              <div key={i} style={{ background:"rgba(255,255,255,.05)", border:"1px solid rgba(255,255,255,.08)", borderRadius:14, padding:18, transition:"background .2s", cursor:"default" }}
                onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,.1)"}
                onMouseLeave={e=>e.currentTarget.style.background="rgba(255,255,255,.05)"}>
                <div style={{ fontSize:22, marginBottom:8 }}>{f.i}</div>
                <div style={{ fontSize:13, fontWeight:700, color:"#e0f0ea", marginBottom:4 }}>{f.t}</div>
                <div style={{ fontSize:11, color:"rgba(255,255,255,.38)", lineHeight:1.5 }}>{f.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════ PRICING ══════════ */}
      <section style={{ padding:"80px clamp(16px,5vw,64px)", background:"#fff" }}>
        <div style={{ maxWidth:980, margin:"0 auto" }}>
          <div style={{ textAlign:"center", marginBottom:52 }}>
            <div style={{ fontSize:12, fontWeight:700, color:T.primary, letterSpacing:".1em", textTransform:"uppercase", marginBottom:10 }}>Employer Pricing</div>
            <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:"clamp(26px,4vw,42px)", fontWeight:900, letterSpacing:"-0.025em", marginBottom:12 }}>Simple, transparent plans</h2>
            <p style={{ color:T.inkL, fontSize:15 }}>Students always get full access for free. Cancel employer plans anytime.</p>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(240px,1fr))", gap:20 }}>
            {[
              {
                name:"Starter", price:"₱2,500", per:"/month", highlight:"5 active listings",
                features:["30-day post duration","Applicant management","Email notifications","Basic analytics"],
                featured:false, cta:"Get Started"
              },
              {
                name:"Growth", price:"₱4,999", per:"/month", highlight:"20 active listings",
                features:["60-day post duration","Featured placement","Resume database access","AI candidate scoring","Priority support"],
                featured:true, cta:"Most Popular"
              },
              {
                name:"Enterprise", price:"₱9,999", per:"/month", highlight:"Unlimited listings",
                features:["90-day post duration","Homepage banner slot","Full API access","Dedicated account manager","Custom branding","University partnerships"],
                featured:false, cta:"Contact Sales"
              },
            ].map((p,i)=>(
              <div key={i} className={`plan-card ${p.featured?"featured":""} fade-up`} style={{ position:"relative", animationDelay:`${i*80}ms` }}>
                {p.featured && <div style={{ position:"absolute", top:-14, left:"50%", transform:"translateX(-50%)", background:T.primary, color:"#fff", fontSize:11, fontWeight:800, padding:"4px 16px", borderRadius:100, letterSpacing:".06em", textTransform:"uppercase", whiteSpace:"nowrap" }}>Most Popular</div>}
                <div style={{ fontWeight:700, fontSize:17, marginBottom:4 }}>{p.name}</div>
                <div style={{ display:"flex", alignItems:"baseline", gap:2, marginBottom:6 }}>
                  <span style={{ fontFamily:"'Fraunces',serif", fontSize:38, fontWeight:900, letterSpacing:"-0.03em", color:T.ink }}>{p.price}</span>
                  <span style={{ fontSize:13, color:T.inkL }}>{p.per}</span>
                </div>
                <div style={{ fontSize:13, color:T.primary, fontWeight:700, marginBottom:20, paddingBottom:16, borderBottom:`1px solid ${T.border}` }}>{p.highlight}</div>
                {p.features.map((f,fi)=>(
                  <div key={fi} style={{ display:"flex", gap:10, marginBottom:10 }}>
                    <span style={{ color:T.green, flexShrink:0, fontWeight:700 }}>✓</span>
                    <span style={{ fontSize:13, color:T.inkL }}>{f}</span>
                  </div>
                ))}
                <button className={`btn btn-sm ${p.featured?"btn-primary":"btn-outline"}`}
                  style={{ width:"100%", marginTop:22, padding:"12px", borderRadius:10, fontSize:14, ...(p.featured?{}:{border:`1.5px solid ${T.border}`, background:"transparent", color:T.inkM}) }}
                  onClick={onSignup}>{p.featured?"Get Started →":p.cta}</button>
              </div>
            ))}
          </div>
          {/* Students free note */}
          <div style={{ textAlign:"center", marginTop:28, padding:"16px 24px", background:T.greenL, borderRadius:12, border:`1px solid #86efac`, fontSize:14, color:T.green, fontWeight:600 }}>
            🎓 <strong>Students always get full access for free</strong> — job browsing, AI coach, logbook, certificate, and more.
          </div>
        </div>
      </section>

      {/* ══════════ FAQ ══════════ */}
      <section style={{ padding:"80px clamp(16px,5vw,64px)", background:T.surface }}>
        <div style={{ maxWidth:720, margin:"0 auto" }}>
          <div style={{ textAlign:"center", marginBottom:48 }}>
            <div style={{ fontSize:12, fontWeight:700, color:T.primary, letterSpacing:".1em", textTransform:"uppercase", marginBottom:10 }}>FAQ</div>
            <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:"clamp(26px,4vw,40px)", fontWeight:900, letterSpacing:"-0.025em" }}>Common questions</h2>
          </div>
          {[
            {q:"Is OJT Hub free for students?",a:"Yes — 100% free for students, always. You get full access to job listings, the AI career coach, digital logbook, endorsement tracking, and completion certificates at no cost."},
            {q:"What universities are supported?",a:"We support 52+ Philippine universities. Coordinators and department heads can register their institution through our University Portal. Student enrollment is immediate after university registration."},
            {q:"How does the digital logbook work?",a:"Students log daily OJT hours and tasks. Their employer (supervisor) verifies each entry digitally. The university OJT coordinator then reviews and approves verified entries. Once the required hours are reached and approved, a completion certificate is auto-generated."},
            {q:"How does the AI match score work?",a:"Our algorithm scores each job against your profile with three weighted factors: skills match (50%), course/job title relevance (30%), and location preference (20%). Scores above 75% are flagged as strong matches."},
            {q:"Can employers post international OJT positions?",a:"Yes. We support listings from the Philippines, USA, Singapore, UAE, and Australia. International positions are clearly flagged and filtered separately in student search."},
            {q:"How does employer verification work?",a:"Employers submit a business license or SEC certificate during signup. Our admin team reviews and approves within 24–48 hours. Verified employers get a badge that increases student trust and application rates."},
          ].map((faq,i)=>(
            <FAQItem key={i} q={faq.q} a={faq.a} />
          ))}
        </div>
      </section>

      {/* ══════════ FINAL CTA ══════════ */}
      <section style={{ padding:"80px clamp(16px,5vw,64px)", background:`linear-gradient(135deg, ${T.primary} 0%, #084f38 100%)`, position:"relative", overflow:"hidden", textAlign:"center" }}>
        <div style={{ position:"absolute", top:-100, left:"50%", transform:"translateX(-50%)", width:600, height:600, borderRadius:"50%", background:"rgba(255,255,255,.04)", pointerEvents:"none" }} />
        <div style={{ position:"absolute", bottom:-80, right:-80, width:300, height:300, borderRadius:"50%", background:"rgba(245,166,35,.1)", pointerEvents:"none" }} />
        <div style={{ maxWidth:620, margin:"0 auto", position:"relative", zIndex:1 }}>
          <div style={{ fontSize:56, marginBottom:16 }}>🇵🇭</div>
          <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:"clamp(30px,5vw,52px)", fontWeight:900, color:"#fff", letterSpacing:"-0.03em", lineHeight:1.12, marginBottom:16 }}>
            Start your OJT journey<br />today.
          </h2>
          <p style={{ fontSize:16, color:"rgba(255,255,255,.65)", lineHeight:1.8, marginBottom:36 }}>
            Join 12,000+ Filipino students who found their OJT placement through philippineinternships.com — powered by TransCareers.
          </p>
          <div style={{ display:"flex", gap:14, justifyContent:"center", flexWrap:"wrap" }}>
            <button className="btn" style={{ fontSize:15, padding:"15px 38px", borderRadius:12, background:"#fff", color:T.primary, fontWeight:800 }} onClick={onSignup}>Create Free Account →</button>
            <button className="btn" style={{ fontSize:15, padding:"14px 38px", borderRadius:12, background:"transparent", border:"1.5px solid rgba(255,255,255,.35)", color:"rgba(255,255,255,.9)", fontWeight:600 }} onClick={()=>setPage("jobs")}>Browse OJT Jobs</button>
          </div>
          <p style={{ fontSize:12, color:"rgba(255,255,255,.35)", marginTop:20 }}>No credit card required · Free for students forever</p>
        </div>
      </section>

      {/* ══════════ FOOTER ══════════ */}
      <footer style={{ background:T.ink, padding:"52px clamp(16px,5vw,64px) 28px" }}>
        <div style={{ maxWidth:1200, margin:"0 auto" }}>
          <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr 1fr 1fr", gap:40, marginBottom:44, flexWrap:"wrap" }}>
            <div>
              <Logo size={32} fontSize={18} />
              <p style={{ fontSize:13, color:"rgba(247,249,248,.42)", lineHeight:1.75, maxWidth:260, marginTop:14 }}>Philippines' #1 platform for OJT and internship opportunities. Connecting students with top companies locally and abroad.</p>
              <div style={{ marginTop:18, fontSize:12, color:"rgba(247,249,248,.28)" }}>
                <div>📍 philippineinternships.com</div>
                <div style={{ marginTop:4 }}>Powered by <strong style={{ color:T.accent }}>TransCareers</strong></div>
              </div>
            </div>
            {[
              {h:"Students",links:["Browse Jobs","AI Career Coach","OJT Logbook","My Applications","Get Certificate"]},
              {h:"Employers",links:["Post OJT Job","Applicant Tracking","Pricing","Employer Verification","API Access"]},
              {h:"Universities",links:["University Portal","Coordinator Login","Endorsements","Student Monitoring","Partner With Us"]},
            ].map((col,i)=>(
              <div key={i}>
                <div style={{ fontSize:12, fontWeight:700, color:"rgba(247,249,248,.35)", textTransform:"uppercase", letterSpacing:".08em", marginBottom:16 }}>{col.h}</div>
                {col.links.map(l=>(
                  <div key={l} style={{ fontSize:13, color:"rgba(247,249,248,.5)", marginBottom:10, cursor:"pointer", transition:"color .18s" }}
                    onMouseEnter={e=>e.target.style.color="rgba(247,249,248,.9)"}
                    onMouseLeave={e=>e.target.style.color="rgba(247,249,248,.5)"}>{l}</div>
                ))}
              </div>
            ))}
          </div>
          <div style={{ paddingTop:24, borderTop:"1px solid rgba(247,249,248,.08)", display:"flex", justifyContent:"space-between", flexWrap:"wrap", gap:12 }}>
            <div style={{ fontSize:12, color:"rgba(247,249,248,.25)" }}>© 2025 TransCareers / philippineinternships.com · All rights reserved</div>
            <div style={{ display:"flex", gap:20 }}>
              {["Privacy Policy","Terms of Service","Cookie Policy"].map(l=>(
                <span key={l} style={{ fontSize:12, color:"rgba(247,249,248,.3)", cursor:"pointer", transition:"color .18s" }}
                  onMouseEnter={e=>e.target.style.color="rgba(247,249,248,.7)"}
                  onMouseLeave={e=>e.target.style.color="rgba(247,249,248,.3)"}>{l}</span>
              ))}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ─── FAQ ACCORDION ITEM ────────────────────────────────────────────────────────
function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom:`1px solid ${T.border}`, paddingBottom:0 }}>
      <button onClick={()=>setOpen(p=>!p)} style={{ width:"100%", display:"flex", justifyContent:"space-between", alignItems:"center", padding:"20px 0", background:"none", border:"none", cursor:"pointer", fontFamily:"inherit", textAlign:"left" }}>
        <span style={{ fontWeight:700, fontSize:15, color:T.ink, paddingRight:16 }}>{q}</span>
        <span style={{ fontSize:20, color:T.inkL, flexShrink:0, transform:open?"rotate(45deg)":"none", transition:"transform .2s" }}>+</span>
      </button>
      {open && (
        <div style={{ fontSize:14, color:T.inkL, lineHeight:1.8, paddingBottom:20, animation:"fadeUp .25s ease" }}>{a}</div>
      )}
    </div>
  );
}


// ─── JOBS PAGE ────────────────────────────────────────────────────────────────

// ─── OJT MATCH SCORE ALGORITHM ───────────────────────────────────────────────
// Weights: Skills 50% | Course/Title 30% | Location 20%


function computeMatchScore(job, profile) {
  return computeKeywordScore(job, profile);
}

function JobsPage({ user, cvProfile, onApply, onAlerts, savedJobs, setSavedJobs, onLoginPrompt, notify }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [country, setCountry] = useState("ALL");
  const [source, setSource] = useState("ALL");
  const SOURCES = [
    { val:"ALL",      label:"All Jobs" },
    { val:"direct",   label:"Direct",   color: T.primary },
    { val:"jobstreet",label:"JobStreet",color:"#e8523a" },
    { val:"indeed",   label:"Indeed",   color:"#003a9b" },
    { val:"linkedin", label:"LinkedIn", color:"#0a66c2" },
    { val:"company",  label:"Company",  color:"#6b7a8d" },
  ];
  const [sortBy, setSortBy] = useState("default");
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobDetailTab, setJobDetailTab] = useState("details"); // "details" | "reviews"
  const [fetchingSource, setFetchingSource] = useState("");
  const [aiSummary, setAiSummary] = useState("");
  const [aiLoading, setAiLoading] = useState(false);

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      // 1. Fetch direct employer jobs from Supabase
      const { data: dbJobs } = await supabase
        .from("job_listings")
        .select("*, companies(name, logo_url, verified)")
        .eq("is_active", true)
        .eq("source", "direct")
        .order("is_featured", { ascending: false })
        .order("created_at", { ascending: false });

      const directJobs = (dbJobs || []).map(j => ({
        id: j.id,
        title: j.title,
        company: j.companies?.name || j.company_name || "—",
        location: j.location || "Philippines",
        country: j.country || "PH",
        salary: j.salary || "Allowance TBD",
        tags: j.tags || [],
        source: "direct",
        sourceLabel: "OJT Hub",
        sourceColor: T.primary,
        flag: "🇵🇭",
        accentColor: T.primary,
        posted: new Date(j.created_at).toLocaleDateString("en-PH"),
        type: "OJT / Internship",
        desc: j.description || "",
        applyUrl: null,
        isFeatured: j.is_featured,
        verified: j.companies?.verified,
        isExternal: false,
      }));

      // 2. Fetch curated external jobs from Supabase
      const { data: extJobs } = await supabase
        .from("external_jobs")
        .select("*")
        .eq("is_active", true)
        .gt("expires_at", new Date().toISOString())
        .order("is_featured", { ascending: false })
        .order("created_at", { ascending: false });

      const SOURCE_META = {
        jobstreet: { label: "JobStreet", color: "#e8523a" },
        indeed:    { label: "Indeed",    color: "#003a9b" },
        linkedin:  { label: "LinkedIn",  color: "#0a66c2" },
        company:   { label: "Company",   color: "#6b7a8d" },
        kalibrr:   { label: "Kalibrr",   color: "#7c3aed" },
      };

      const externalJobs = (extJobs || []).map(j => {
        const meta = SOURCE_META[j.source] || { label: j.source, color: "#6b7a8d" };
        return {
          id: "ext-" + j.id,
          title: j.title,
          company: j.company,
          location: j.location || "Philippines",
          country: "PH",
          salary: j.salary || "Allowance TBD",
          tags: j.tags || [],
          source: j.source,
          sourceLabel: meta.label,
          sourceColor: meta.color,
          flag: "🇵🇭",
          accentColor: meta.color,
          posted: new Date(j.created_at).toLocaleDateString("en-PH"),
          type: j.job_type || "OJT / Internship",
          duration: j.duration || "3-6 months",
          desc: j.description || "",
          applyUrl: j.source_url,
          isFeatured: j.is_featured,
          verified: false,
          isExternal: true,
          courses: j.courses || [],
          slots: j.slots || 1,
        };
      });

      // 3. Fetch JSearch live jobs
      setFetchingSource("Philippines 🇵🇭");
      const phJobs = country === "US" ? [] : await fetchRealJobs("PH", search);
      setFetchingSource("USA 🇺🇸");
      const usJobs = country === "PH" ? [] : await fetchRealJobs("US", search);
      setFetchingSource("");

      // Direct first, then external (featured), then JSearch
      const featuredExt = externalJobs.filter(j => j.isFeatured);
      const regularExt  = externalJobs.filter(j => !j.isFeatured);
      const all = [...directJobs, ...featuredExt, ...regularExt, ...phJobs, ...usJobs];
      setJobs(all);
    } catch (err) {
      console.error("fetchJobs error:", err);
      setJobs([]);
    }
    setLoading(false);
  }, [country, search]);

  useEffect(() => { fetchJobs(); }, [country]);

  const scored = jobs
    .filter(j => {
      if (source === "ALL") return true;
      if (source === "direct") return j.source === "direct";
      if (source === "external") return j.isExternal === true;
      return j.source === source;
    })
    .map(j => ({ ...j, match: computeMatchScore(j, cvProfile) }));

  const filtered = [...scored].sort((a,b) => {
    if (sortBy === "match") return (b.match?.score||0) - (a.match?.score||0);
    if (sortBy === "salary") {
      const sal = s => parseInt((s||"0").replace(/[^0-9]/g,""))||0;
      return sal(b.salary) - sal(a.salary);
    }
    // default: featured first
    if (a.isFeatured && !b.isFeatured) return -1;
    if (!a.isFeatured && b.isFeatured) return 1;
    return 0;
  });

  const openJob = async (job) => {
    setSelectedJob(job); setAiSummary(""); setAiLoading(true);
    try {
      const r = await claudeAPI(
        "You are a career advisor for Filipino students seeking OJT and internship placements. Given a job description, write 2 encouraging sentences about why it is a great OJT opportunity, then list 3 bullet points on what skills the student should highlight in their application. Be concise.",
        `OJT Role: ${job.title} at ${job.company} (${job.location}). Description: ${job.desc}. Skills needed: ${job.tags.join(", ")}.`
      );
      setAiSummary(r);
    } catch { setAiSummary("This looks like an excellent OJT opportunity! Make sure your resume highlights relevant skills and experience before applying."); }
    setAiLoading(false);
  };

  return (
    <div style={{ paddingTop:64,minHeight:"100vh",background:T.surface }}>
      {/* Search header */}
      <div style={{ background:"#fff",borderBottom:`1px solid ${T.border}`,padding:"20px clamp(16px,5vw,56px)" }}>
        <div style={{ maxWidth:1200,margin:"0 auto" }}>
          <h1 style={{ fontFamily:"'Fraunces',serif",fontSize:26,fontWeight:800,marginBottom:4 }}>OJT &amp; Internship Jobs</h1>
          <p style={{ fontSize:13,color:T.inkL,marginBottom:16 }}>Live listings from Indeed, ZipRecruiter &amp; Glassdoor</p>
          <div style={{ display:"flex",gap:10,flexWrap:"wrap" }}>
            <div style={{ flex:1,minWidth:220,position:"relative" }}>
              <svg style={{ position:"absolute",left:13,top:"50%",transform:"translateY(-50%)" }} width="14" height="14" fill="none" stroke={T.inkL} strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
              <input className="input" style={{ paddingLeft:36 }} placeholder="Title, skill, company…" value={search} onChange={e=>setSearch(e.target.value)} onKeyDown={e=>e.key==="Enter"&&fetchJobs()} />
            </div>
            <select className="input" style={{ width:"auto",minWidth:160 }} value={country} onChange={e=>setCountry(e.target.value)}>
              <option value="ALL">🌏 All Countries</option>
              <option value="PH">🇵🇭 Philippines</option>
              <option value="US">🇺🇸 United States</option>
            </select>
            <select className="input" style={{ width:"auto",minWidth:150 }} value={source} onChange={e=>setSource(e.target.value)}>
              <option value="ALL">All Sources</option>
              <option value="Indeed">Indeed</option>
              <option value="ZipRecruiter">ZipRecruiter</option>
              <option value="Glassdoor">Glassdoor</option>
            </select>
            <button className="btn btn-primary" onClick={fetchJobs} disabled={loading}>{loading?"Fetching…":"Search"}</button>
            {user?.role==="student" && <button className="btn btn-ghost" onClick={onAlerts} style={{ whiteSpace:"nowrap" }}>🔔 Job Alerts</button>}
            <select className="input" style={{ width:"auto",minWidth:150 }} value={sortBy} onChange={e=>setSortBy(e.target.value)}>
              <option value="default">Sort: Default</option>
              <option value="match">⭐ Best Match</option>
              <option value="salary">💰 Highest Pay</option>
            </select>
          </div>
          <div style={{ display:"flex",gap:14,marginTop:12,flexWrap:"wrap" }}>
            {[{n:"Indeed",c:"#2557a7"},{n:"ZipRecruiter",c:"#0078a3"},{n:"Glassdoor",c:"#0caa41"}].map(s=>(
              <div key={s.n} style={{ display:"flex",alignItems:"center",gap:6,fontSize:12,fontWeight:600 }}>
                <span style={{ width:7,height:7,borderRadius:"50%",background:fetchingSource===s.n?T.accent:T.green,display:"inline-block",transition:"background .3s" }} />
                <span style={{ color:s.c }}>{s.n}</span>
                <span style={{ color:T.inkL }}>{fetchingSource===s.n?"fetching…":"live"}</span>
              </div>
            ))}
            {!loading && <span style={{ marginLeft:"auto",fontSize:12,color:T.inkL }}>{filtered.length} results</span>}
          </div>
        </div>
      </div>

      {/* Match prompt banner */}
      {sortBy === "match" && !cvProfile?.skills && (
        <div style={{ background:T.amberL, borderBottom:`1px solid ${T.accent}40`, padding:"12px clamp(16px,5vw,56px)" }}>
          <div style={{ maxWidth:1200, margin:"0 auto", display:"flex", alignItems:"center", justifyContent:"space-between", gap:12, flexWrap:"wrap" }}>
            <div style={{ fontSize:13, color:T.amber, fontWeight:700 }}>⭐ Fill in your CV Builder profile to see personalized match scores on every job!</div>
          </div>
        </div>
      )}
      {/* Grid + Sidebar */}
      <div style={{ maxWidth:1200,margin:"0 auto",padding:"28px clamp(16px,5vw,56px)",display:"grid",gridTemplateColumns:"1fr minmax(0,300px)",gap:24,alignItems:"start" }}>
        {/* Jobs column */}
        <div>
          {loading ? (
            <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))",gap:16 }}>
              {Array(8).fill(0).map((_,i)=>(
                <div key={i} className="card" style={{ padding:20 }}>
                  {[70,110,60,40].map((w,j)=><div key={j} className="shimmer" style={{ height:13,width:`${w}%`,marginBottom:10 }} />)}
                </div>
              ))}
            </div>
          ) : filtered.length===0 ? (
            <div style={{ textAlign:"center",padding:80,color:T.inkL }}>
              <div style={{ fontSize:48,marginBottom:12 }}>🔍</div>
              <div style={{ fontSize:16,fontWeight:600 }}>No results. Try different filters.</div>
            </div>
          ) : (
            <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))",gap:16 }}>
              {filtered.map((job,i)=>(
                <div key={job.id} className="job-card fade-up" style={{ "--ac":job.accentColor,animationDelay:`${i*45}ms` }}>
                  <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:14 }}>
                    <div style={{ width:44,height:44,borderRadius:12,background:job.accentColor+"18",display:"flex",alignItems:"center",justifyContent:"center",fontSize:20 }}>{job.flag}</div>
                    <div style={{ display:"flex",gap:6,alignItems:"center",flexWrap:"wrap",justifyContent:"flex-end" }}>
                      <span style={{ fontSize:11,fontWeight:700,color:job.sourceColor,background:job.sourceColor+"12",padding:"2px 9px",borderRadius:100 }}>{job.source}</span>
                      {job.match && <MatchBadge score={job.match.score} isAI={job.match.isAI} />}
                      <button onClick={e=>{e.stopPropagation();if(!user){onLoginPrompt();return;}setSavedJobs(p=>p.includes(job.id)?p.filter(id=>id!==job.id):[...p,job.id]);}}
                        style={{ background:"none",border:"none",cursor:"pointer",fontSize:16,color:savedJobs.includes(job.id)?T.primary:T.border,transition:"color .18s" }}>
                        {savedJobs.includes(job.id)?"♥":"♡"}
                      </button>
                    </div>
                  </div>
                  <div style={{ fontWeight:700,fontSize:15,marginBottom:3 }}>{job.title}</div>
                  <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:8 }}>
                    <span style={{ fontSize:13,color:T.inkL }}>{job.company} · {job.location}</span>
                    {job.isExternal && (
                      <span style={{ fontSize:11,fontWeight:700,padding:"2px 8px",borderRadius:20,background:job.sourceColor+"22",color:job.sourceColor }}>
                        {job.sourceLabel}
                      </span>
                    )}
                    {!job.isExternal && job.source==="direct" && (
                      <span style={{ fontSize:11,fontWeight:700,padding:"2px 8px",borderRadius:20,background:T.primary+"22",color:T.primary }}>
                        Direct
                      </span>
                    )}
                  </div>
                  <div style={{ display:"flex",gap:6,flexWrap:"wrap",marginBottom:14 }}>
                    {job.tags.map((t,ti)=><span key={ti} className="tag">{t}</span>)}
                  </div>
                  <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",paddingTop:12,borderTop:`1px solid ${T.border}` }}>
                    <span style={{ fontWeight:700,fontSize:15,color:job.accentColor }}>{job.salary}</span>
                    <div style={{ display:"flex",gap:8 }}>
                      <button className="btn btn-ghost btn-sm" onClick={()=>openJob(job)}>View</button>
                      {(!user || user?.role==="student") && (
                        job.isExternal
                          ? <a href={job.applyUrl} target="_blank" rel="noopener noreferrer"
                              style={{ display:"flex",alignItems:"center",gap:4,padding:"6px 12px",background:job.sourceColor||T.primary,color:"#fff",borderRadius:8,fontSize:13,fontWeight:700,textDecoration:"none" }}>
                              Apply on {job.sourceLabel} ↗
                            </a>
                          : <button className="btn btn-primary btn-sm" onClick={()=>openJob(job)}>Apply</button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {/* Salary Benchmark Sidebar */}
        <div style={{ position:"sticky",top:80 }}>
          <div className="card" style={{ padding:16 }}>
            <SalaryBenchmark supabase={supabase} T={T} />
          </div>
        </div>
      </div>

      {/* Job modal */}
      {selectedJob && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setSelectedJob(null)}>
          <div className="modal" style={{ maxWidth:580, maxHeight:"90vh", overflowY:"auto" }}>
            {/* Header */}
            <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16 }}>
              <div style={{ display:"flex",gap:14,alignItems:"center" }}>
                <div style={{ width:52,height:52,borderRadius:14,background:selectedJob.accentColor+"18",display:"flex",alignItems:"center",justifyContent:"center",fontSize:26 }}>{selectedJob.flag}</div>
                <div>
                  <h2 style={{ fontFamily:"'Fraunces',serif",fontSize:20,fontWeight:800 }}>{selectedJob.title}</h2>
                  <div style={{ fontSize:13,color:T.inkL }}>{selectedJob.company} · {selectedJob.location}</div>
                </div>
              </div>
              <button onClick={()=>setSelectedJob(null)} style={{ background:"none",border:"none",cursor:"pointer",color:T.inkL,fontSize:24 }}>×</button>
            </div>
            {/* Tags */}
            <div style={{ display:"flex",gap:8,flexWrap:"wrap",marginBottom:16 }}>
              <span style={{ fontSize:11,fontWeight:700,color:selectedJob.sourceColor,background:selectedJob.sourceColor+"15",padding:"3px 10px",borderRadius:100 }}>{selectedJob.source}</span>
              <span className="tag">{selectedJob.type}</span>
              <span className="tag">{selectedJob.duration}</span>
              <span className="tag">{selectedJob.applicants} applicants</span>
              <span style={{ marginLeft:"auto",fontWeight:800,fontSize:16,color:selectedJob.accentColor }}>{selectedJob.salary}</span>
            </div>
            {/* Tabs */}
            <div style={{ display:"flex",gap:0,borderBottom:`2px solid ${T.border}`,marginBottom:18 }}>
              {[["details","📋 Details"],["reviews","⭐ Reviews"]].map(([id,label])=>(
                <button key={id} onClick={()=>setJobDetailTab(id)}
                  style={{ padding:"8px 18px",fontSize:13,fontWeight:700,border:"none",background:"none",cursor:"pointer",
                    color:jobDetailTab===id?T.primary:T.inkL,
                    borderBottom:jobDetailTab===id?`2px solid ${T.primary}`:"2px solid transparent",
                    marginBottom:-2,transition:"all .15s" }}>
                  {label}
                </button>
              ))}
            </div>

            {jobDetailTab === "details" && (<>
              <p style={{ fontSize:14,color:T.inkL,lineHeight:1.78,marginBottom:18 }}>{selectedJob.desc}</p>
              <div style={{ marginBottom:18 }}>
                <div style={{ fontSize:11,fontWeight:700,color:T.inkL,textTransform:"uppercase",letterSpacing:".08em",marginBottom:8 }}>Required Skills</div>
                <div style={{ display:"flex",gap:6,flexWrap:"wrap" }}>{selectedJob.tags.map((t,i)=><span key={i} className="tag" style={{ fontSize:12 }}>{t}</span>)}</div>
              </div>
              <div style={{ background:T.primaryL,border:`1px solid ${T.primary}25`,borderRadius:12,padding:16,marginBottom:20 }}>
                <div style={{ fontSize:12,fontWeight:700,color:T.primary,marginBottom:8 }}>✨ AI OJT Advisor — powered by TransCareers</div>
                {aiLoading ? (
                  <div style={{ display:"flex",gap:8,alignItems:"center" }}>
                    <div className="spin" style={{ width:14,height:14,border:"2px solid rgba(10,110,78,.3)",borderTopColor:T.primary,borderRadius:"50%" }} />
                    <span style={{ fontSize:13,color:T.primary }}>Analyzing this opportunity…</span>
                  </div>
                ) : <p style={{ fontSize:13,color:T.primaryD,lineHeight:1.75,whiteSpace:"pre-wrap" }}>{aiSummary}</p>}
              </div>
              {cvProfile && (
                <div style={{ marginBottom:16 }}>
                  <MatchBreakdown job={selectedJob} profile={cvProfile} studentId={user?.id} />
                </div>
              )}
            </>)}

            {jobDetailTab === "reviews" && (
              <CompanyReviewsDisplay
                companyId={selectedJob.companyId || selectedJob.id}
                companyName={selectedJob.company}
                user={user}
                notify={notify}
              />
            )}

            <div style={{ display:"flex",gap:10,marginTop:16 }}>
              <button className="btn btn-ghost" style={{ flex:1 }} onClick={()=>setSelectedJob(null)}>Close</button>
              {(!user || user?.role==="student") && (
                selectedJob.isExternal
                  ? <a href={selectedJob.applyUrl} target="_blank" rel="noopener noreferrer"
                      style={{ flex:2,display:"flex",alignItems:"center",justifyContent:"center",gap:6,padding:"12px",background:selectedJob.sourceColor||T.primary,color:"#fff",borderRadius:10,fontSize:15,fontWeight:800,textDecoration:"none" }}>
                      Apply on {selectedJob.sourceLabel} ↗
                    </a>
                  : <button className="btn btn-primary" style={{ flex:2 }} onClick={()=>{onApply(selectedJob);setSelectedJob(null);}}>Apply to this OJT →</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── CV BUILDER ───────────────────────────────────────────────────────────────
function CVBuilder({ user, cvProfile, setCvProfile, notify, onLoginPrompt }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(cvProfile||{ name:"",email:"",phone:"",location:"",school:"",course:"",gpa:"",yearLevel:"",summary:"",skills:"",experience:"",education:"",certifications:"",linkedin:"" });
  const [aiLoading, setAiLoading] = useState(false);
  const [aiTips, setAiTips] = useState("");
  const u = (k,v) => setForm(p=>({...p,[k]:v}));

  useEffect(() => {
    if (!user?.id) return;
    supabase.from("profiles").select("*").eq("id", user.id).single().then(({ data }) => {
      if (!data) return;
      setForm(p => ({
        ...p,
        name: data.full_name || p.name,
        email: data.email || p.email,
        phone: data.phone || p.phone,
        course: data.course || p.course,
        yearLevel: data.year_level || p.yearLevel,
        gpa: data.gpa || p.gpa,
        summary: data.summary || p.summary,
        skills: data.skills || p.skills,
        experience: data.experience || p.experience,
        school: (() => {
            // If education was previously saved as "School — Date", extract school back out
            const ed = data.education || "";
            if (ed.includes(" — ")) return ed.split(" — ")[0];
            return data.full_name ? "" : p.school; // don't overwrite typed school
          })(),
        education: (() => {
            const ed = data.education || p.education || "";
            // Strip leading school name repetitions: keep only the last segment after " — "
            if (ed.includes(" — ")) {
              const parts = ed.split(" — ").map(s => s.trim()).filter(Boolean);
              // If all parts before last look like a school name (repeated), return last part
              const last = parts[parts.length - 1];
              return last;
            }
            return ed;
          })(),
        certifications: data.certifications || p.certifications,
        linkedin: data.linkedin || p.linkedin,
      }));
    });
  }, [user?.id]);
  const save = async () => {
    setCvProfile(form);
    if (user?.id) {
      await supabase.from("profiles").update({
        full_name: form.name,
        email: form.email,
        phone: form.phone,
        linkedin: form.linkedin,
        course: form.course,
        year_level: form.yearLevel,
        gpa: form.gpa,
        summary: form.summary,
        skills: form.skills,
        experience: form.experience,
        education: form.education || "",
        certifications: form.certifications,
      }).eq("id", user.id);
    }
    notify("Resume saved! ✓");
  };

  if (!user) return (
    <div style={{ paddingTop:64,minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:T.surface }}>
      <div style={{ textAlign:"center" }}>
        <div style={{ fontSize:48,marginBottom:16 }}>📝</div>
        <h2 style={{ fontFamily:"'Fraunces',serif",fontSize:24,fontWeight:800,marginBottom:10 }}>Build Your OJT Resume</h2>
        <p style={{ color:T.inkL,marginBottom:24 }}>Log in to create and save your professional OJT resume.</p>
        <button className="btn btn-primary" onClick={onLoginPrompt}>Log In to Continue</button>
      </div>
    </div>
  );

  const STEPS = [{ label:"Personal",icon:"👤" },{ label:"Skills",icon:"🛠" },{ label:"Education",icon:"🎓" },{ label:"Preview",icon:"✨" }];

  const getAITips = async () => {
    setAiLoading(true); setAiTips("");
    try {
      const r = await claudeAPI(
        "You are an OJT resume coach for Filipino college students. Review the resume and give 3 specific, actionable tips to improve it for OJT applications in the Philippines. Be direct, practical, and encouraging. Format as numbered list.",
        `Student: ${form.name}. Course: ${form.course} at ${form.school}. GPA: ${form.gpa}. Skills: ${form.skills}. Experience: ${form.experience}. Summary: ${form.summary}.`
      );
      setAiTips(r);
    } catch { setAiTips("1. Add specific technical skills relevant to your target OJT role\n2. Include extracurricular activities and org involvement\n3. Quantify any achievements with numbers where possible"); }
    setAiLoading(false);
  };

  return (
    <div style={{ paddingTop:64,minHeight:"100vh",background:T.surface }}>
      <div style={{ maxWidth:800,margin:"0 auto",padding:"36px clamp(16px,5vw,40px)" }}>
        <h1 style={{ fontFamily:"'Fraunces',serif",fontSize:28,fontWeight:800,marginBottom:6 }}>OJT Resume Builder</h1>
        <p style={{ color:T.inkL,marginBottom:28,fontSize:14 }}>Build a professional resume designed for OJT and internship applications.</p>

        {/* Stepper */}
        <div style={{ display:"flex",background:"#fff",border:`1.5px solid ${T.border}`,borderRadius:12,overflow:"hidden",marginBottom:28 }}>
          {STEPS.map((s,i)=>(
            <div key={i} onClick={()=>setStep(i)} style={{ flex:1,padding:"12px 6px",textAlign:"center",fontSize:12,fontWeight:600,cursor:"pointer",transition:"all .2s",
              background:step===i?T.primary:"transparent",color:step===i?"#fff":T.inkL,borderRight:i<STEPS.length-1?`1px solid ${T.border}`:"none" }}>
              <span style={{ display:"block",fontSize:18,marginBottom:2 }}>{s.icon}</span>{s.label}
            </div>
          ))}
        </div>

        <div className="slide-in">
          {step===0 && (
            <div className="card" style={{ padding:28 }}>
              <h3 style={{ fontWeight:700,marginBottom:20 }}>Personal Information</h3>
              <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:16 }}>
                <div><label>Full Name *</label><input className="input" value={form.name} onChange={e=>u("name",e.target.value)} placeholder="Juan dela Cruz" /></div>
                <div><label>Email *</label><input className="input" value={form.email} onChange={e=>u("email",e.target.value)} placeholder="juan@email.com" /></div>
                <div><label>Phone</label><input className="input" value={form.phone} onChange={e=>u("phone",e.target.value)} placeholder="+63 912 345 6789" /></div>
                <div><label>Location</label><input className="input" value={form.location} onChange={e=>u("location",e.target.value)} placeholder="Quezon City, Philippines" /></div>
                <div><label>LinkedIn</label><input className="input" value={form.linkedin} onChange={e=>u("linkedin",e.target.value)} placeholder="linkedin.com/in/juandelacruz" /></div>
                <div><label>Year Level</label>
                  <select className="input" value={form.yearLevel} onChange={e=>u("yearLevel",e.target.value)}>
                    <option value="">Select…</option>
                    <option>2nd Year</option><option>3rd Year</option><option>4th Year</option><option>Graduate Student</option>
                  </select>
                </div>
                <div style={{ gridColumn:"1/-1" }}><label>Professional Summary</label><textarea className="input" rows={3} value={form.summary} onChange={e=>u("summary",e.target.value)} placeholder="Motivated 4th year BS IT student seeking software engineering OJT…" /></div>
              </div>
              <button className="btn btn-primary" style={{ marginTop:20 }} onClick={()=>form.name&&form.email?setStep(1):notify("Fill required fields","error")}>Next →</button>
            </div>
          )}

          {step===1 && (
            <div className="card" style={{ padding:28 }}>
              <h3 style={{ fontWeight:700,marginBottom:20 }}>Skills &amp; Experience</h3>
              <div style={{ display:"flex",flexDirection:"column",gap:16 }}>
                <div><label>Technical &amp; Soft Skills (comma-separated)</label><input className="input" value={form.skills} onChange={e=>u("skills",e.target.value)} placeholder="React, Python, Figma, MS Office, Communication, Teamwork…" /></div>
                <div><label>Work, OJT &amp; Org Experience</label><textarea className="input" rows={5} value={form.experience} onChange={e=>u("experience",e.target.value)} placeholder="• VP Technology, DLSU CS Society (2024) — Led 8-person dev team&#10;• Freelance Web Developer (2023-present) — Built 5+ client websites" /></div>
                <div><label>Certifications &amp; Awards</label><textarea className="input" rows={3} value={form.certifications} onChange={e=>u("certifications",e.target.value)} placeholder="AWS Cloud Practitioner (2024), Dean's List AY 2022-2025…" /></div>
              </div>
              <div style={{ display:"flex",gap:10,marginTop:20 }}>
                <button className="btn btn-ghost" onClick={()=>setStep(0)}>← Back</button>
                <button className="btn btn-primary" onClick={()=>setStep(2)}>Next →</button>
              </div>
            </div>
          )}

          {step===2 && (
            <div className="card" style={{ padding:28 }}>
              <h3 id="cv-education-section" style={{ fontWeight:700,marginBottom:20 }}>Education</h3>
              <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:16 }}>
                <div><label>School / University *</label><input className="input" value={form.school} onChange={e=>u("school",e.target.value)} placeholder="De La Salle University" /></div>
                <div><label>Course / Degree *</label><input className="input" value={form.course} onChange={e=>u("course",e.target.value)} placeholder="BS Computer Science" /></div>
                <div><label>GPA (optional)</label><input className="input" value={form.gpa} onChange={e=>u("gpa",e.target.value)} placeholder="3.8 / 4.0" /></div>
                <div><label>Expected Graduation</label><input className="input" value={form.education} onChange={e=>u("education",e.target.value)} placeholder="April 2026" /></div>
              </div>
              <div style={{ display:"flex",gap:10,marginTop:20 }}>
                <button className="btn btn-ghost" onClick={()=>setStep(1)}>← Back</button>
                <button className="btn btn-primary" onClick={()=>{if(form.school&&form.course){save();setStep(3);}else notify("Fill school and course","error");}}>Preview →</button>
              </div>
            </div>
          )}

          {step===3 && (
            <>
              {/* Resume preview */}
              <div className="card" style={{ padding:32,marginBottom:20 }}>
                <div style={{ borderBottom:`3px solid ${T.primary}`,paddingBottom:18,marginBottom:20 }}>
                  <h2 style={{ fontFamily:"'Fraunces',serif",fontSize:26,fontWeight:900,color:T.ink }}>{form.name||"Your Name"}</h2>
                  {form.course && <div style={{ fontSize:14,color:T.primary,fontWeight:600,marginTop:2 }}>{form.course}{form.school?` · ${form.school}`:""}{form.yearLevel?` · ${form.yearLevel}`:""}</div>}
                  <div style={{ display:"flex",gap:16,flexWrap:"wrap",marginTop:8 }}>
                    {form.email && <span style={{ fontSize:13,color:T.inkL }}>✉ {form.email}</span>}
                    {form.phone && <span style={{ fontSize:13,color:T.inkL }}>📱 {form.phone}</span>}
                    {form.location && <span style={{ fontSize:13,color:T.inkL }}>📍 {form.location}</span>}
                    {form.gpa && <span style={{ fontSize:13,color:T.green,fontWeight:700 }}>GPA: {form.gpa}</span>}
                  </div>
                </div>
                {form.summary && <div style={{ marginBottom:16 }}><div style={{ fontSize:10,fontWeight:800,letterSpacing:".1em",textTransform:"uppercase",color:T.primary,marginBottom:6 }}>SUMMARY</div><p style={{ fontSize:13,color:T.inkL,lineHeight:1.75 }}>{form.summary}</p></div>}
                {form.skills && <div style={{ marginBottom:16 }}><div style={{ fontSize:10,fontWeight:800,letterSpacing:".1em",textTransform:"uppercase",color:T.primary,marginBottom:6 }}>SKILLS</div><div style={{ display:"flex",flexWrap:"wrap",gap:6 }}>{form.skills.split(",").map((s,i)=><span key={i} className="tag">{s.trim()}</span>)}</div></div>}
                {form.experience && <div style={{ marginBottom:16 }}><div style={{ fontSize:10,fontWeight:800,letterSpacing:".1em",textTransform:"uppercase",color:T.primary,marginBottom:6 }}>EXPERIENCE</div><p style={{ fontSize:13,color:T.inkL,lineHeight:1.75,whiteSpace:"pre-line" }}>{form.experience}</p></div>}
                {(form.school||form.education) && <div style={{ marginBottom:16 }}><div style={{ fontSize:10,fontWeight:800,letterSpacing:".1em",textTransform:"uppercase",color:T.primary,marginBottom:6 }}>EDUCATION</div><p style={{ fontSize:13,color:T.inkL,lineHeight:1.75 }}>{form.course} · {form.school}{form.education?` · Expected ${form.education}`:""}</p></div>}
                {form.certifications && <div><div style={{ fontSize:10,fontWeight:800,letterSpacing:".1em",textTransform:"uppercase",color:T.primary,marginBottom:6 }}>CERTIFICATIONS</div><p style={{ fontSize:13,color:T.inkL,lineHeight:1.75,whiteSpace:"pre-line" }}>{form.certifications}</p></div>}
              </div>

              {/* AI tips */}
              <div style={{ background:T.primaryL,border:`1.5px solid ${T.primary}20`,borderRadius:14,padding:20,marginBottom:20 }}>
                <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10 }}>
                  <div style={{ fontSize:13,fontWeight:700,color:T.primary }}>✨ AI Resume Coach — TransCareers</div>
                  <button className="btn btn-primary btn-sm" onClick={getAITips} disabled={aiLoading}>{aiLoading?"Analyzing…":"Get AI Tips"}</button>
                </div>
                {aiTips ? <p style={{ fontSize:13,color:T.primaryD,lineHeight:1.8,whiteSpace:"pre-wrap" }}>{aiTips}</p>
                  : <p style={{ fontSize:13,color:T.primary,opacity:.6 }}>Click "Get AI Tips" for personalized OJT resume feedback powered by Claude AI.</p>}
              </div>

              <div style={{ display:"flex",gap:10 }}>
                <button className="btn btn-ghost" onClick={()=>setStep(0)}>Edit</button>
                <button className="btn btn-primary" onClick={save}>Save to Profile ✓</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── MY APPLICATIONS ──────────────────────────────────────────────────────────
function MyApplications({ user, isPremium, onUpgrade, onLoginPrompt }) {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [filter, setFilter]             = useState("ALL");
  const [view, setView]                 = useState("applications"); // applications | analytics | evaluations
  const [appTab, setAppTab]             = useState({}); // { [appId]: "info"|"messages"|"interview" }
  const [expanded, setExpanded]         = useState(null);
  const [reviewTarget, setReviewTarget] = useState(null);
  const [withdrawing, setWithdrawing]   = useState(null);
  const [toast, setToast]               = useState(null);
  const [myEvals, setMyEvals]           = useState([]);

  const notify = (msg, ok=true) => { setToast({msg,ok}); setTimeout(()=>setToast(null),3000); };

  const load = () => {
    if (!user) { setLoading(false); return; }
    supabase
      .from("applications")
      .select("*")
      .eq("student_id", user.id)
      .order("applied_at", { ascending: false })
      .then(async ({ data: apps }) => {
        // Merge job_listings data with directly stored job_title/company_name fallback
        const enriched = (apps || []).map(a => ({
          ...a,
          // Use join data if available, fall back to stored columns
          _title:   a.job_listings?.title    || a.job_title    || "Internship Position",
          _company: a.job_listings?.company_name || a.company_name || "Company",
          _location:a.job_listings?.location  || a.job_location || "",
        }));
        setApplications(enriched);
        // Load evaluations for this student
        if (apps?.length) {
          const appIds = apps.map(a => a.id);
          const { data: evals } = await supabase
            .from("intern_evaluations")
            .select("*, applications(job_listings(title, company_name))")
            .in("application_id", appIds)
            .order("submitted_at", { ascending: false });
          setMyEvals(evals || []);
        }
        setLoading(false);
      });
  };

  useEffect(() => { load(); }, [user]);

  const withdraw = async (appId) => {
    setWithdrawing(appId);
    const { error } = await supabase.from("applications").update({ status:"withdrawn" }).eq("id", appId);
    if (!error) { setApplications(p => p.map(a => a.id===appId ? {...a, status:"withdrawn"} : a)); notify("Application withdrawn."); }
    else notify("Failed to withdraw.", false);
    setWithdrawing(null);
  };

  if (!user) return (
    <div style={{ paddingTop:64, minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:T.surface }}>
      <div style={{ textAlign:"center" }}>
        <div style={{ fontSize:48, marginBottom:16 }}>📋</div>
        <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:24, fontWeight:800, marginBottom:10 }}>My OJT Applications</h2>
        <p style={{ color:T.inkL, marginBottom:24 }}>Log in to track your applications.</p>
        <button className="btn btn-primary" onClick={onLoginPrompt}>Log In</button>
      </div>
    </div>
  );

  const SC = {
    pending:   { bg:T.surface, color:T.inkL,    dot:"#94a3b8", label:"Pending",   icon:"⏳", next:"Waiting for employer to review your application." },
    reviewing: { bg:T.blueL,   color:T.blue,    dot:T.blue,    label:"Reviewing", icon:"🔍", next:"Employer is reviewing your profile. Make sure your CV is up to date!" },
    interview: { bg:"#f5f3ff", color:"#7c3aed", dot:"#7c3aed", label:"Interview", icon:"🎤", next:"You've been shortlisted! Prepare for your interview — review the job description and practice common OJT interview questions." },
    accepted:  { bg:T.greenL,  color:T.green,   dot:T.green,   label:"Accepted",  icon:"✅", next:"Congratulations! Coordinate with your OJT coordinator to get your endorsement letter processed." },
    rejected:  { bg:T.redL,    color:T.red,     dot:T.red,     label:"Rejected",  icon:"❌", next:"Don't give up — keep applying! Use Coach Pia to strengthen your CV and cover letter." },
    withdrawn: { bg:T.surface, color:T.inkL,    dot:"#94a3b8", label:"Withdrawn", icon:"↩️", next:"You withdrew this application." },
  };

  // Pipeline stages for progress bar
  const STAGES = ["pending","reviewing","interview","accepted"];
  const stageIdx = (status) => STAGES.indexOf(status);

  const counts = {
    ALL:       applications.length,
    pending:   applications.filter(a=>a.status==="pending").length,
    reviewing: applications.filter(a=>a.status==="reviewing").length,
    interview: applications.filter(a=>a.status==="interview").length,
    accepted:  applications.filter(a=>a.status==="accepted").length,
    rejected:  applications.filter(a=>a.status==="rejected").length,
  };

  const visible = filter==="ALL" ? applications : applications.filter(a=>a.status===filter);
  const active  = applications.filter(a=>!["accepted","rejected","withdrawn"].includes(a.status)).length;

  return (
    <div style={{ paddingTop:64, minHeight:"100vh", background:T.surface }}>
      {toast && <div style={{ position:"fixed", top:80, right:20, zIndex:999, background:toast.ok?T.primary:T.red, color:"#fff", padding:"11px 20px", borderRadius:12, fontWeight:700, fontSize:13, boxShadow:"0 8px 24px rgba(0,0,0,.15)" }}>{toast.msg}</div>}
      <div style={{ maxWidth:900, margin:"0 auto", padding:"36px clamp(16px,5vw,40px)" }}>

        {/* Header */}
        <div style={{ marginBottom:28 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", flexWrap:"wrap", gap:12 }}>
            <div>
              <h1 style={{ fontFamily:"'Fraunces',serif", fontSize:28, fontWeight:900, color:T.ink, marginBottom:4 }}>My OJT Applications 📋</h1>
              <p style={{ fontSize:13, color:T.inkL }}>Track every application from submission to acceptance</p>
            </div>
            <div style={{ display:"flex", gap:6 }}>
              {[["applications","📋 Applications",false],["analytics","📊 Analytics",true],["evaluations","⭐ Evaluations",true]].map(([v,l,premiumOnly])=>{
                const locked = premiumOnly && !isPremium;
                return (
                  <button key={v} onClick={()=>{ if(locked){onUpgrade&&onUpgrade();return;} setView(v); }}
                    title={locked?"Premium feature — upgrade to unlock":""}
                    style={{ padding:"8px 16px", borderRadius:100, border:"1.5px solid", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit",
                      background:view===v?T.primary:"#fff", color:view===v?"#fff":locked?T.inkL:T.inkM,
                      borderColor:view===v?T.primary:T.border, transition:"all .18s", opacity:locked?.75:1 }}>
                    {locked?"🔒 ":""}{l}{v==="evaluations"&&myEvals.length>0?` (${myEvals.length})`:""}{locked?" ⚡":""}
                  </button>
                );
              })}
            </div>
          </div>
        </div>


        {/* ── ANALYTICS VIEW ─────────────────────────────────────────────── */}
        {view === "analytics" && (() => {
          const total      = applications.length;
          const accepted   = applications.filter(a=>a.status==="accepted").length;
          const rejected   = applications.filter(a=>a.status==="rejected").length;
          const interviews = applications.filter(a=>["interview","accepted"].includes(a.status)).length;
          const responded  = applications.filter(a=>a.status!=="pending").length;
          const responseRate = total ? Math.round((responded/total)*100) : 0;
          const interviewRate = responded ? Math.round((interviews/responded)*100) : 0;
          const offerRate = interviews ? Math.round((accepted/interviews)*100) : 0;

          // Applications by week (last 8 weeks)
          const now = new Date();
          const weeks = Array.from({length:8},(_,i)=>{
            const d = new Date(now); d.setDate(d.getDate() - (7*(7-i)));
            return { label:`W${8-i}`, count:0, start:d };
          });
          applications.forEach(a => {
            const d = new Date(a.applied_at);
            const wi = weeks.findIndex((w,i) => {
              const next = weeks[i+1];
              return d >= w.start && (!next || d < next.start);
            });
            if (wi >= 0) weeks[wi].count++;
          });
          const maxW = Math.max(...weeks.map(w=>w.count), 1);

          // Status breakdown for donut chart substitute
          const breakdown = [
            {label:"Accepted", v:accepted,   c:"#16a34a"},
            {label:"Interview",v:applications.filter(a=>a.status==="interview").length, c:"#7c3aed"},
            {label:"Reviewing",v:applications.filter(a=>a.status==="reviewing").length, c:T.blue},
            {label:"Pending",  v:applications.filter(a=>a.status==="pending").length,   c:"#94a3b8"},
            {label:"Rejected", v:rejected,   c:T.red},
          ].filter(b=>b.v>0);

          return (
            <div className="fade-in">
              {/* Funnel metrics */}
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))", gap:12, marginBottom:24 }}>
                {[
                  {l:"Applied",       v:total,         c:T.blue,    icon:"📤"},
                  {l:"Response Rate", v:responseRate+"%", c:T.amber, icon:"📬"},
                  {l:"Interview Rate",v:interviewRate+"%",c:"#7c3aed",icon:"🎤"},
                  {l:"Offer Rate",    v:offerRate+"%",  c:T.green,  icon:"✅"},
                ].map(s=>(
                  <div key={s.l} style={{ background:"#fff", border:`1.5px solid ${s.c}20`, borderRadius:14, padding:"16px", textAlign:"center" }}>
                    <div style={{ fontSize:22, marginBottom:6 }}>{s.icon}</div>
                    <div style={{ fontFamily:"'Fraunces',serif", fontSize:28, fontWeight:900, color:s.c }}>{s.v}</div>
                    <div style={{ fontSize:11, color:T.inkL, fontWeight:600 }}>{s.l}</div>
                  </div>
                ))}
              </div>

              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:20 }}>
                {/* Weekly activity chart */}
                <div className="card" style={{ padding:20 }}>
                  <div style={{ fontWeight:800, fontSize:14, marginBottom:16 }}>📅 Applications by Week</div>
                  <div style={{ display:"flex", alignItems:"flex-end", gap:6, height:100 }}>
                    {weeks.map((w,i)=>(
                      <div key={i} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
                        <div style={{ fontSize:10, color:T.inkL, fontWeight:700 }}>{w.count||""}</div>
                        <div style={{ width:"100%", background:w.count?T.primary:T.border, borderRadius:"4px 4px 0 0", height:w.count?`${Math.max(8,(w.count/maxW)*80)}px`:"4px", transition:"height .4s" }} />
                        <div style={{ fontSize:9, color:T.inkL }}>{w.label}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Status breakdown */}
                <div className="card" style={{ padding:20 }}>
                  <div style={{ fontWeight:800, fontSize:14, marginBottom:16 }}>📊 Status Breakdown</div>
                  {breakdown.map((b,i)=>(
                    <div key={i} style={{ marginBottom:10 }}>
                      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                        <span style={{ fontSize:12, fontWeight:600 }}>{b.label}</span>
                        <span style={{ fontSize:12, color:T.inkL }}>{b.v} ({total?Math.round((b.v/total)*100):0}%)</span>
                      </div>
                      <div style={{ height:6, background:T.border, borderRadius:100, overflow:"hidden" }}>
                        <div style={{ height:"100%", width:`${total?(b.v/total)*100:0}%`, background:b.c, borderRadius:100, transition:"width .6s" }} />
                      </div>
                    </div>
                  ))}
                  {!breakdown.length && <div style={{ color:T.inkL, fontSize:13, textAlign:"center", padding:"20px 0" }}>No data yet</div>}
                </div>
              </div>

              {/* Application funnel */}
              <div className="card" style={{ padding:20 }}>
                <div style={{ fontWeight:800, fontSize:14, marginBottom:16 }}>🔽 Conversion Funnel</div>
                <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                  {[
                    {stage:"Applied",        n:total,        c:"#1d6fcc"},
                    {stage:"Got Response",   n:responded,    c:"#f5a623"},
                    {stage:"Got Interview",  n:interviews,   c:"#7c3aed"},
                    {stage:"Got Offer",      n:accepted,     c:"#16a34a"},
                  ].map((s,i)=>(
                    <div key={i} style={{ display:"flex", alignItems:"center", gap:12 }}>
                      <div style={{ width:110, fontSize:12, fontWeight:600, color:T.inkM, textAlign:"right", flexShrink:0 }}>{s.stage}</div>
                      <div style={{ flex:1, height:28, background:T.border, borderRadius:6, overflow:"hidden", position:"relative" }}>
                        <div style={{ height:"100%", width:`${total?(s.n/total)*100:0}%`, background:s.c, borderRadius:6, transition:"width .8s", minWidth: s.n>0?"28px":0 }}>
                          {s.n > 0 && <span style={{ position:"absolute", left:8, top:"50%", transform:"translateY(-50%)", color:"#fff", fontSize:11, fontWeight:800 }}>{s.n}</span>}
                        </div>
                      </div>
                      <div style={{ width:40, fontSize:12, fontWeight:700, color:s.c }}>{total?Math.round((s.n/total)*100):0}%</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tips based on data */}
              {total > 0 && responseRate < 30 && (
                <div style={{ background:"#fef3c7", border:"1px solid #fcd34d", borderRadius:12, padding:"14px 18px", marginTop:16 }}>
                  <div style={{ fontWeight:700, fontSize:13, color:"#92400e", marginBottom:4 }}>💡 Application Tip</div>
                  <div style={{ fontSize:13, color:"#78350f" }}>Your response rate is {responseRate}%. Try tailoring your cover letter to each role, and use the AI Match Score to only apply to jobs where you score above 60%.</div>
                </div>
              )}
            </div>
          );
        })()}

        {/* ── EVALUATIONS VIEW ──────────────────────────────────────────── */}
        {view === "evaluations" && (
          <div className="fade-in">
            {myEvals.length === 0 ? (
              <div className="card" style={{ padding:"60px 40px", textAlign:"center" }}>
                <div style={{ fontSize:48, marginBottom:12 }}>⭐</div>
                <div style={{ fontWeight:700, fontSize:16, marginBottom:6 }}>No evaluations yet</div>
                <div style={{ fontSize:13, color:T.inkL }}>Evaluations from your employer will appear here once your internship is underway.</div>
              </div>
            ) : (
              <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
                {myEvals.map((ev,i) => {
                  const STARS = [1,2,3,4,5];
                  const Star = ({v, rating}) => <span style={{ fontSize:14, color: v<=rating?"#f5a623":"#e2e8e5" }}>★</span>;
                  const COMPS = [
                    {key:"rating_attendance",   label:"Attendance"},
                    {key:"rating_attitude",      label:"Attitude"},
                    {key:"rating_quality",       label:"Work Quality"},
                    {key:"rating_initiative",    label:"Initiative"},
                    {key:"rating_teamwork",      label:"Teamwork"},
                    {key:"rating_communication", label:"Communication"},
                    {key:"rating_technical",     label:"Technical Skills"},
                    {key:"rating_professionalism",label:"Professionalism"},
                  ];
                  const jobTitle = ev.applications?.job_listings?.title || "OJT Position";
                  const company  = ev.applications?.job_listings?.company_name || "Employer";
                  return (
                    <div key={ev.id} className="card fade-up" style={{ overflow:"hidden", animationDelay:`${i*50}ms` }}>
                      <div style={{ padding:"16px 20px", background: ev.eval_type==="final"?T.primaryL:T.blueL, borderBottom:`1px solid ${T.border}` }}>
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:8 }}>
                          <div>
                            <div style={{ fontWeight:800, fontSize:15 }}>{ev.eval_type==="final"?"🏁 Final":"📋 Mid-term"} Evaluation</div>
                            <div style={{ fontSize:12, color:T.inkL }}>{jobTitle} · {company}</div>
                          </div>
                          <div style={{ textAlign:"right" }}>
                            <div style={{ fontFamily:"'Fraunces',serif", fontSize:28, fontWeight:900, color:T.primary }}>{ev.overall_rating ? Number(ev.overall_rating).toFixed(1) : "—"}<span style={{ fontSize:14, color:T.inkL }}>/5</span></div>
                            <div style={{ fontSize:11, color:T.inkL }}>Overall</div>
                          </div>
                        </div>
                        {/* Stars */}
                        <div style={{ marginTop:8 }}>{STARS.map(v=><Star key={v} v={v} rating={Math.round(ev.overall_rating||0)} />)}</div>
                      </div>
                      <div style={{ padding:"16px 20px" }}>
                        {/* Competency grid */}
                        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:16 }}>
                          {COMPS.filter(c=>ev[c.key]).map(comp=>(
                            <div key={comp.key} style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                              <span style={{ fontSize:12, color:T.inkM }}>{comp.label}</span>
                              <div>{STARS.map(v=><Star key={v} v={v} rating={ev[comp.key]||0} />)}</div>
                            </div>
                          ))}
                        </div>
                        {ev.strengths && (
                          <div style={{ marginBottom:10 }}>
                            <div style={{ fontSize:11, fontWeight:700, color:T.green, textTransform:"uppercase", letterSpacing:".04em", marginBottom:4 }}>✅ Strengths</div>
                            <div style={{ fontSize:13, color:T.inkM, lineHeight:1.6 }}>{ev.strengths}</div>
                          </div>
                        )}
                        {ev.improvements && (
                          <div style={{ marginBottom:10 }}>
                            <div style={{ fontSize:11, fontWeight:700, color:T.amber, textTransform:"uppercase", letterSpacing:".04em", marginBottom:4 }}>💡 Areas to Improve</div>
                            <div style={{ fontSize:13, color:T.inkM, lineHeight:1.6 }}>{ev.improvements}</div>
                          </div>
                        )}
                        {ev.recommendation && (
                          <div style={{ display:"inline-flex", alignItems:"center", gap:6, padding:"4px 14px", borderRadius:100, fontSize:12, fontWeight:700,
                            background: ev.recommendation==="hire"?T.greenL:ev.recommendation==="extend"?T.blueL:T.redL,
                            color: ev.recommendation==="hire"?T.green:ev.recommendation==="extend"?T.blue:T.red }}>
                            {ev.recommendation==="hire"?"✅ Recommended for hire":ev.recommendation==="extend"?"🔄 Extend internship":"⚠️ "+ev.recommendation}
                          </div>
                        )}
                        {ev.submitted_at && <div style={{ fontSize:11, color:T.inkL, marginTop:10 }}>Submitted {new Date(ev.submitted_at).toLocaleDateString("en-PH",{year:"numeric",month:"long",day:"numeric"})}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── APPLICATIONS VIEW ─────────────────────────────────────────── */}
        {view === "applications" && (<>
        {/* KPI strip */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))", gap:12, marginBottom:24 }}>
          {[
            { l:"Total Applied", v:counts.ALL,      c:T.blue,   bg:T.blueL   },
            { l:"In Progress",   v:active,           c:T.amber,  bg:T.amberL  },
            { l:"Interview",     v:counts.interview, c:"#7c3aed",bg:"#f5f3ff" },
            { l:"Accepted",      v:counts.accepted,  c:T.green,  bg:T.greenL  },
            { l:"Rejected",      v:counts.rejected,  c:T.red,    bg:T.redL    },
          ].map(s => (
            <div key={s.l} className="stat-box" style={{ background:s.bg, border:`1.5px solid ${s.c}20`, textAlign:"center", padding:"14px 10px", borderRadius:12 }}>
              <div style={{ fontFamily:"'Fraunces',serif", fontSize:30, fontWeight:900, color:s.c }}>{s.v}</div>
              <div style={{ fontSize:11, color:T.inkM, fontWeight:600, marginTop:3 }}>{s.l}</div>
            </div>
          ))}
        </div>

        {/* Filter tabs */}
        <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:20 }}>
          {[["ALL","All"],["pending","Pending"],["reviewing","Reviewing"],["interview","Interview 🎤"],["accepted","Accepted ✅"],["rejected","Rejected"]].map(([key,label]) => (
            <button key={key} onClick={()=>setFilter(key)} style={{
              padding:"7px 14px", borderRadius:100, border:"1.5px solid", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit",
              background: filter===key ? T.primary : "#fff",
              color: filter===key ? "#fff" : T.inkM,
              borderColor: filter===key ? T.primary : T.border,
              transition:"all .18s"
            }}>
              {label} {counts[key]!==undefined && counts[key]>0 ? `(${counts[key]})` : ""}
            </button>
          ))}
        </div>

        {/* List */}
        {loading ? (
          <div style={{ textAlign:"center", padding:60, color:T.inkL }}>Loading…</div>
        ) : visible.length===0 ? (
          <div className="card" style={{ padding:"60px 40px", textAlign:"center" }}>
            <div style={{ fontSize:40, marginBottom:12 }}>{filter==="ALL" ? "📭" : "🔍"}</div>
            <div style={{ fontSize:15, fontWeight:700, marginBottom:6 }}>{filter==="ALL" ? <EmptyState icon="📨" title="No applications yet" desc="Start applying to internship listings and track your progress here." /> : `No ${filter} applications`}</div>
            <div style={{ fontSize:13, color:T.inkL }}>{filter==="ALL" ? "Browse OJT jobs and hit Apply to get started!" : "Try a different filter above."}</div>
          </div>
        ) : (
          <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
            {visible.map((app, i) => {
              const sm   = SC[app.status] || SC.pending;
              const isEx = expanded === app.id;
              const si   = stageIdx(app.status);
              const canWithdraw = ["pending","reviewing"].includes(app.status);

              return (
                <div key={app.id} className="card fade-up" style={{ overflow:"hidden", animationDelay:`${i*40}ms`, cursor:"pointer" }}
                  onClick={() => setExpanded(isEx ? null : app.id)}>

                  {/* Main row */}
                  <div style={{ display:"flex", alignItems:"center", gap:14, padding:"16px 20px" }}>
                    <div style={{ width:44, height:44, borderRadius:12, background:sm.bg, display:"flex", alignItems:"center", justifyContent:"center", fontSize:22, flexShrink:0 }}>{sm.icon}</div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontWeight:700, fontSize:15, marginBottom:2, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                        {app._title || app.job_listings?.title || "—"}
                      </div>
                      <div style={{ fontSize:13, color:T.inkL }}>
                        {app._company || app.job_listings?.company_name || "—"}
                        {app._location || (app.job_listings?.location ? ` · ${app.job_listings.location}` : "")}
                        {" · Applied "}{new Date(app.applied_at).toLocaleDateString("en-PH",{month:"short",day:"numeric",year:"numeric"})}
                      </div>
                      {app.employer_notes && (
                        <div style={{ fontSize:12, color:T.primary, marginTop:4, fontWeight:600 }}>
                          💬 Employer: "{app.employer_notes}"
                        </div>
                      )}
                    </div>
                    <div style={{ display:"flex", alignItems:"center", gap:10, flexShrink:0 }}>
                      <span style={{ background:sm.bg, color:sm.color, fontSize:12, fontWeight:700, padding:"4px 12px", borderRadius:100, display:"flex", alignItems:"center", gap:5 }}>
                        <span style={{ width:6, height:6, borderRadius:"50%", background:sm.dot, display:"inline-block" }} />
                        {sm.label}
                      </span>
                      <span style={{ color:T.inkL, fontSize:16 }}>{isEx ? "▲" : "▼"}</span>
                    </div>
                  </div>

                  {/* Progress pipeline — only for active stages */}
                  {si >= 0 && app.status !== "rejected" && app.status !== "withdrawn" && (
                    <div style={{ padding:"0 20px 14px" }}>
                      <div style={{ display:"flex", alignItems:"center", gap:0 }}>
                        {STAGES.map((stage, idx) => {
                          const done    = idx < si;
                          const current = idx === si;
                          const sc      = SC[stage];
                          return (
                            <div key={stage} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center" }}>
                              <div style={{ display:"flex", alignItems:"center", width:"100%" }}>
                                {idx > 0 && <div style={{ flex:1, height:3, background:done||current?T.primary:T.border, transition:"background .4s" }} />}
                                <div style={{ width:20, height:20, borderRadius:"50%", background:done?T.primary:current?T.primary:"#fff",
                                  border:`2.5px solid ${done||current?T.primary:T.border}`, display:"flex", alignItems:"center", justifyContent:"center",
                                  fontSize:9, color:"#fff", fontWeight:800, flexShrink:0, transition:"all .4s" }}>
                                  {done ? "✓" : current ? "●" : ""}
                                </div>
                                {idx < STAGES.length-1 && <div style={{ flex:1, height:3, background:done?T.primary:T.border, transition:"background .4s" }} />}
                              </div>
                              <div style={{ fontSize:10, fontWeight:600, color:done||current?T.primary:T.inkL, marginTop:4, textAlign:"center", textTransform:"capitalize" }}>{stage}</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Expanded detail */}
                  {isEx && (
                    <div style={{ borderTop:`1.5px solid ${T.border}`, padding:"16px 20px", background:T.surface }}>
                      {/* Next step tip */}
                      <div style={{ background:sm.bg, border:`1.5px solid ${sm.dot}30`, borderRadius:10, padding:"12px 14px", marginBottom:14, fontSize:13, color:sm.color, fontWeight:600 }}>
                        💡 {sm.next}
                      </div>

                      {/* Job details */}
                      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))", gap:"6px 20px", marginBottom:14 }}>
                        {[
                          ["📍 Location",   app._location || app.job_listings?.location || "—"],
                          ["💰 Allowance",  app.job_listings?.salary || "Negotiable"],
                          ["🕒 Type",       app.job_source || app.job_listings?.type || "—"],
                          ["📅 Duration",   app.job_listings?.duration  || "—"],
                        ].map(([k,v]) => (
                          <div key={k} style={{ padding:"8px 0", borderBottom:`1px solid ${T.border}` }}>
                            <div style={{ fontSize:11, fontWeight:700, color:T.inkL, marginBottom:2 }}>{k}</div>
                            <div style={{ fontSize:13, color:T.ink }}>{v}</div>
                          </div>
                        ))}
                      </div>

                      {/* Sub-tabs: Info / Messages / Interview */}
                      <div style={{ display:"flex", gap:4, marginBottom:14, borderBottom:`1px solid ${T.border}`, paddingBottom:10 }}
                        onClick={e => e.stopPropagation()}>
                        {[["info","📋 Info"],["messages","💬 Messages"],["interview","🗓️ Interview"]].map(([t,l])=>(
                          <button key={t} onClick={()=>setAppTab(p=>({...p,[app.id]:t}))}
                            style={{ padding:"5px 12px", borderRadius:100, border:"1.5px solid", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit",
                              background:(appTab[app.id]||"info")===t?T.primary:"#fff",
                              color:(appTab[app.id]||"info")===t?"#fff":T.inkM,
                              borderColor:(appTab[app.id]||"info")===t?T.primary:T.border }}>
                            {l}
                          </button>
                        ))}
                      </div>

                      {/* Info tab */}
                      {(appTab[app.id]||"info")==="info" && (<>
                        {app.cover_note && (
                          <div style={{ marginBottom:14 }}>
                            <div style={{ fontSize:11, fontWeight:700, color:T.inkL, textTransform:"uppercase", letterSpacing:".05em", marginBottom:6 }}>Your Cover Note</div>
                            <div style={{ fontSize:13, color:T.inkM, background:"#fff", borderRadius:8, padding:"10px 13px", border:`1px solid ${T.border}`, fontStyle:"italic" }}>
                              "{app.cover_note}"
                            </div>
                          </div>
                        )}
                        <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
                          {app.status==="accepted" && (
                            <a href="/documents" className="btn btn-primary" style={{ textDecoration:"none", fontSize:13, padding:"8px 16px" }}>📁 Submit Documents</a>
                          )}
                          {app.status==="accepted" && (
                            <button className="btn btn-ghost" style={{ fontSize:13, padding:"8px 16px", color:T.amber, borderColor:T.amber }}
                              onClick={e=>{ e.stopPropagation(); setReviewTarget({ companyId:app.company_id||app.job_id, companyName:app.company, applicationId:app.id }); }}>
                              ⭐ Review Company
                            </button>
                          )}
                          {app.status==="interview" && (
                            <a href="/coach" className="btn btn-primary" style={{ textDecoration:"none", fontSize:13, padding:"8px 16px" }}>🤖 Prep with Coach Pia</a>
                          )}
                          {canWithdraw && (
                            <button className="btn btn-ghost" style={{ fontSize:12, padding:"7px 14px", color:T.red, borderColor:T.red }}
                              onClick={e=>{ e.stopPropagation(); if(window.confirm("Withdraw this application?")) withdraw(app.id); }}
                              disabled={withdrawing===app.id}>
                              {withdrawing===app.id ? "Withdrawing…" : "↩ Withdraw"}
                            </button>
                          )}
                        </div>
                      </>)}

                      {/* Messages tab */}
                      {(appTab[app.id]||"info")==="messages" && (
                        <div style={{ height:360, border:`1px solid ${T.border}`, borderRadius:12, overflow:"hidden" }}
                          onClick={e=>e.stopPropagation()}>
                          <MessagingPanel
                            applicationId={app.id}
                            currentUser={user}
                            otherParty={{ id: app.job_listings?.employer_id || app.employerId, name: app.job_listings?.company_name || "Employer", role: "employer" }}
                            supabase={supabase}
                            T={T}
                          />
                        </div>
                      )}

                      {/* Interview tab */}
                      {(appTab[app.id]||"info")==="interview" && (
                        <div onClick={e=>e.stopPropagation()}>
                          <InterviewSchedulerStudent
                            applicationId={app.id}
                            currentUser={user}
                            supabase={supabase}
                            T={T}
                            notify={notify}
                          />
                        </div>
                      )}
                    </div>
                  )}

                </div>
              );
            })}
          </div>
        )}
        </>)}
      </div>
      {reviewTarget && (
        <WriteReviewModal
          companyId={reviewTarget.companyId}
          companyName={reviewTarget.companyName}
          applicationId={reviewTarget.applicationId}
          user={user}
          onClose={() => setReviewTarget(null)}
          notify={notify}
        />
      )}
    </div>
  );
}
// ─── EMPLOYER DASHBOARD ───────────────────────────────────────────────────────
function EmployerDashboard({ user, applications, jobs, onUpdateStatus, notify, onLoginPrompt }) {
  const [tab, setTab] = useState("overview");
  const [selectedApp, setSelectedApp] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiScore, setAiScore] = useState("");
  const [filterJob, setFilterJob] = useState("ALL");
  const [filterStatus, setFilterStatus] = useState("ALL");
  const [search, setSearch] = useState("");

  if (!user) return (
    <div style={{ paddingTop:64,minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:T.surface }}>
      <div style={{ textAlign:"center" }}>
        <div style={{ fontSize:48,marginBottom:16 }}>🏢</div>
        <h2 style={{ fontFamily:"'Fraunces',serif",fontSize:24,fontWeight:800,marginBottom:10 }}>Employer Dashboard</h2>
        <p style={{ color:T.inkL,marginBottom:8 }}>Log in as an employer to manage your OJT listings.</p>
        <p style={{ fontSize:12,color:T.inkL,marginBottom:24 }}>💡 Demo: use <strong>employer@demo.com</strong> with any password</p>
        <button className="btn btn-primary" onClick={onLoginPrompt}>Log In as Employer</button>
      </div>
    </div>
  );

  const filtered = applications.filter(a=>{
    const mj=filterJob==="ALL"||a.jobId===filterJob;
    const ms=filterStatus==="ALL"||a.status===filterStatus;
    const mq=!search||a.name.toLowerCase().includes(search.toLowerCase())||a.email.toLowerCase().includes(search.toLowerCase());
    return mj&&ms&&mq;
  });

  const counts = {
    total:applications.length,
    new:applications.filter(a=>a.status==="new").length,
    shortlisted:applications.filter(a=>a.status==="shortlisted").length,
    hired:applications.filter(a=>a.status==="hired").length,
  };

  const openApp = async (app) => {
    setSelectedApp(app); setAiScore(""); setAiLoading(true);
    try {
      const r = await claudeAPI(
        "You are an HR assistant helping Philippine employers screen OJT applicants. Given an applicant profile, provide: a suitability score out of 10, 2 strengths, and 1 area to probe in interview. Keep it under 100 words. Format: Score: X/10, then bullet points.",
        `OJT Role: ${app.jobTitle}. Applicant: ${app.name}. School: ${app.school||"N/A"}. Skills: ${app.skills}. Education: ${app.education}. Experience: ${app.experience}. GPA: ${app.gpa||"N/A"}.`
      );
      setAiScore(r);
    } catch { setAiScore("Score: 7/10\n• Strong academic background and relevant skills\n• Good extracurricular involvement\n• Interview focus: practical project experience"); }
    setAiLoading(false);
  };

  const TABS = [
    { id:"overview",label:"Overview",icon:"📊" },
    { id:"applicants",label:"Applicants",icon:"👥",badge:counts.new },
    { id:"jobs",label:"Job Listings",icon:"📋" },
    { id:"settings",label:"Settings",icon:"⚙️" },
  ];

  return (
    <div style={{ paddingTop:64,minHeight:"100vh",background:T.surface,display:"flex" }}>
      {/* Sidebar */}
      <div style={{ width:220,flexShrink:0,background:"#fff",borderRight:`1px solid ${T.border}`,padding:"24px 14px",display:"flex",flexDirection:"column",gap:4,position:"sticky",top:64,height:"calc(100vh - 64px)",overflowY:"auto" }}>
        <div style={{ padding:"8px 12px",marginBottom:14 }}>
          <div style={{ fontWeight:800,fontSize:14,color:T.ink }}>{user.company||user.name}</div>
          <div style={{ fontSize:12,color:T.inkL,marginTop:2 }}>Employer · OJT Hub</div>
          <div style={{ fontSize:11,color:T.primary,marginTop:2,fontWeight:600 }}>TransCareers Partner</div>
        </div>
        {TABS.map(t=>(
          <div key={t.id} className={`sidebar-item ${tab===t.id?"active":""}`} onClick={()=>setTab(t.id)}>
            <span style={{ fontSize:16 }}>{t.icon}</span>
            <span>{t.label}</span>
            {t.badge>0 && <span style={{ marginLeft:"auto",background:T.primary,color:"#fff",borderRadius:"50%",width:18,height:18,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:800 }}>{t.badge}</span>}
          </div>
        ))}
        <div className="divider" />
        <div style={{ padding:"8px 12px",fontSize:11,color:T.inkL }}>
          <div>philippineinternships.com</div>
          <div style={{ marginTop:4,color:T.primary,fontWeight:600 }}>by TransCareers</div>
        </div>
      </div>

      {/* Main content */}
      <div style={{ flex:1,padding:"28px clamp(16px,3vw,40px)",overflow:"auto" }}>

        {/* OVERVIEW TAB */}
        {tab==="overview" && (
          <div className="fade-in">
            <h1 style={{ fontFamily:"'Fraunces',serif",fontSize:26,fontWeight:800,marginBottom:6 }}>Dashboard</h1>
            <p style={{ color:T.inkL,fontSize:14,marginBottom:24 }}>Welcome, {user.name.split(" ")[0]}. Here's your OJT Hub activity.</p>

            <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:14,marginBottom:28 }}>
              {[{l:"Total Applicants",v:counts.total,i:"👥",c:T.blue},{l:"New",v:counts.new,i:"🆕",c:T.primary},{l:"Shortlisted",v:counts.shortlisted,i:"⭐",c:T.purple},{l:"Hired",v:counts.hired,i:"✅",c:T.green}].map((s,i)=>(
                <div key={i} className="stat-box fade-up" style={{ animationDelay:`${i*60}ms`,textAlign:"center" }}>
                  <div style={{ fontSize:22,marginBottom:6 }}>{s.i}</div>
                  <div style={{ fontFamily:"'Fraunces',serif",fontSize:30,fontWeight:900,color:s.c }}>{s.v}</div>
                  <div style={{ fontSize:12,color:T.inkL,marginTop:4 }}>{s.l}</div>
                </div>
              ))}
            </div>

            <h2 style={{ fontWeight:800,fontSize:16,marginBottom:14 }}>Active OJT Listings</h2>
            <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:14,marginBottom:28 }}>
              {jobs.filter(j=>j.country==="PH").slice(0,4).map((j,i)=>(
                <div key={j.id} className="card fade-up" style={{ padding:18,animationDelay:`${i*55}ms` }}>
                  <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10 }}>
                    <div style={{ fontWeight:700,fontSize:14,lineHeight:1.3 }}>{j.title}</div>
                    <span style={{ fontSize:11,background:T.greenL,color:T.green,fontWeight:700,padding:"2px 8px",borderRadius:100,whiteSpace:"nowrap",flexShrink:0,marginLeft:8 }}>● Live</span>
                  </div>
                  <div style={{ fontSize:12,color:T.inkL,marginBottom:10 }}>{j.location} · {j.type}</div>
                  <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center" }}>
                    <span style={{ fontWeight:700,color:j.accentColor,fontSize:14 }}>{j.salary}</span>
                    <span style={{ fontSize:12,color:T.inkL }}>{applications.filter(a=>a.jobId===j.id).length} applicants</span>
                  </div>
                </div>
              ))}
            </div>

            <h2 style={{ fontWeight:800,fontSize:16,marginBottom:14 }}>Recent Applications</h2>
            <div className="card" style={{ overflow:"hidden" }}>
              <table style={{ width:"100%",borderCollapse:"collapse" }}>
                <thead>
                  <tr style={{ background:T.surface }}>
                    {["Applicant","School","Position","Applied","Status","Action"].map(h=>(
                      <th key={h} style={{ padding:"11px 16px",textAlign:"left",fontSize:11,fontWeight:700,color:T.inkL,textTransform:"uppercase",letterSpacing:".06em",borderBottom:`1px solid ${T.border}` }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {applications.slice(0,6).map((app,i)=>{
                    const sm=STATUS_META[app.status];
                    return (
                      <tr key={app.id} style={{ borderBottom:`1px solid ${T.border}`,transition:"background .15s" }}
                        onMouseEnter={e=>e.currentTarget.style.background=T.surface}
                        onMouseLeave={e=>e.currentTarget.style.background=""}>
                        <td style={{ padding:"12px 16px" }}>
                          <div style={{ display:"flex",gap:10,alignItems:"center" }}>
                            <div style={{ width:32,height:32,borderRadius:"50%",background:`linear-gradient(135deg,${T.primary},#0d8f66)`,color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,fontSize:12,flexShrink:0 }}>{app.name[0]}</div>
                            <div><div style={{ fontSize:13,fontWeight:600 }}>{app.name}</div><div style={{ fontSize:11,color:T.inkL }}>{app.email}</div></div>
                          </div>
                        </td>
                        <td style={{ padding:"12px 16px",fontSize:12,color:T.inkL }}>{app.school||"—"}</td>
                        <td style={{ padding:"12px 16px",fontSize:13,color:T.inkM }}>{app.jobTitle}</td>
                        <td style={{ padding:"12px 16px",fontSize:12,color:T.inkL }}>{app.appliedAt}</td>
                        <td style={{ padding:"12px 16px" }}><span className="badge" style={{ background:sm.bg,color:sm.color }}><span style={{ width:6,height:6,borderRadius:"50%",background:sm.dot }} />{sm.label}</span></td>
                        <td style={{ padding:"12px 16px" }}><button className="btn btn-ghost btn-xs" onClick={()=>openApp(app)}>Review</button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* APPLICANTS TAB */}
        {tab==="applicants" && (
          <div className="fade-in">
            <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20,flexWrap:"wrap",gap:12 }}>
              <h1 style={{ fontFamily:"'Fraunces',serif",fontSize:26,fontWeight:800 }}>All Applicants</h1>
              <div style={{ display:"flex",gap:10,flexWrap:"wrap" }}>
                <input className="input" style={{ width:190 }} placeholder="Search name, email…" value={search} onChange={e=>setSearch(e.target.value)} />
                <select className="input" style={{ width:"auto",minWidth:170 }} value={filterJob} onChange={e=>setFilterJob(e.target.value)}>
                  <option value="ALL">All Positions</option>
                  {jobs.map(j=><option key={j.id} value={j.id}>{j.title}</option>)}
                </select>
                <select className="input" style={{ width:"auto",minWidth:130 }} value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}>
                  <option value="ALL">All Status</option>
                  {Object.entries(STATUS_META).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
            </div>
            <div style={{ fontSize:13,color:T.inkL,marginBottom:14 }}>{filtered.length} applicants</div>
            <div className="card" style={{ overflow:"hidden" }}>
              <table style={{ width:"100%",borderCollapse:"collapse" }}>
                <thead>
                  <tr style={{ background:T.surface }}>
                    {["Applicant","School","GPA","Position","Applied","Status","Action"].map(h=>(
                      <th key={h} style={{ padding:"11px 14px",textAlign:"left",fontSize:11,fontWeight:700,color:T.inkL,textTransform:"uppercase",letterSpacing:".06em",borderBottom:`1px solid ${T.border}` }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((app,i)=>{
                    const sm=STATUS_META[app.status];
                    return (
                      <tr key={app.id} style={{ borderBottom:`1px solid ${T.border}`,transition:"background .15s" }}
                        onMouseEnter={e=>e.currentTarget.style.background=T.surface}
                        onMouseLeave={e=>e.currentTarget.style.background=""}>
                        <td style={{ padding:"12px 14px" }}>
                          <div style={{ display:"flex",gap:10,alignItems:"center" }}>
                            <div style={{ width:32,height:32,borderRadius:"50%",background:`linear-gradient(135deg,${T.primary},#0d8f66)`,color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,fontSize:12,flexShrink:0 }}>{app.name[0]}</div>
                            <div><div style={{ fontSize:13,fontWeight:600 }}>{app.name}</div><div style={{ fontSize:11,color:T.inkL }}>{app.email}</div></div>
                          </div>
                        </td>
                        <td style={{ padding:"12px 14px",fontSize:12,color:T.inkL,maxWidth:140 }}><div style={{ overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{app.school||"—"}</div></td>
                        <td style={{ padding:"12px 14px",fontSize:13,fontWeight:600,color:parseFloat(app.gpa)>=3.7?T.green:T.inkM }}>{app.gpa||"—"}</td>
                        <td style={{ padding:"12px 14px",fontSize:13,color:T.inkM }}>{app.jobTitle}</td>
                        <td style={{ padding:"12px 14px",fontSize:12,color:T.inkL }}>{app.appliedAt}</td>
                        <td style={{ padding:"12px 14px" }}>
                          <select style={{ border:`1px solid ${T.border}`,borderRadius:8,padding:"4px 10px",fontSize:12,fontWeight:600,color:sm.color,background:sm.bg,cursor:"pointer",fontFamily:"inherit",outline:"none" }}
                            value={app.status} onChange={e=>{onUpdateStatus(app.id,e.target.value);notify(`Updated to ${STATUS_META[e.target.value].label}`);}}>
                            {Object.entries(STATUS_META).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
                          </select>
                        </td>
                        <td style={{ padding:"12px 14px" }}><button className="btn btn-primary btn-xs" onClick={()=>openApp(app)}>AI Review</button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {filtered.length===0 && <div style={{ padding:48,textAlign:"center",color:T.inkL,fontSize:14 }}>No applicants match your filters.</div>}
            </div>
          </div>
        )}

        {/* JOBS TAB */}
        {tab==="jobs" && (
          <div className="fade-in">
            <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:24 }}>
              <h1 style={{ fontFamily:"'Fraunces',serif",fontSize:26,fontWeight:800 }}>OJT Listings</h1>
              <button className="btn btn-primary" onClick={()=>notify("Connect your backend to enable job creation!")}>+ Post New OJT Job</button>
            </div>
            <div style={{ display:"flex",flexDirection:"column",gap:12 }}>
              {jobs.map((job,i)=>{
                const appCount=applications.filter(a=>a.jobId===job.id).length;
                return (
                  <div key={job.id} className="card fade-up" style={{ padding:"18px 22px",display:"flex",alignItems:"center",gap:16,animationDelay:`${i*50}ms` }}>
                    <div style={{ width:46,height:46,borderRadius:12,background:job.accentColor+"18",display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,flexShrink:0 }}>{job.flag}</div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:700,fontSize:15,marginBottom:2 }}>{job.title}</div>
                      <div style={{ fontSize:13,color:T.inkL }}>{job.company} · {job.location} · {job.type}</div>
                    </div>
                    <div style={{ textAlign:"center",padding:"0 20px" }}>
                      <div style={{ fontFamily:"'Fraunces',serif",fontSize:26,fontWeight:900,color:T.ink }}>{appCount}</div>
                      <div style={{ fontSize:11,color:T.inkL }}>applicants</div>
                    </div>
                    <div style={{ textAlign:"right" }}>
                      <div style={{ fontWeight:700,color:job.accentColor,marginBottom:4,fontSize:14 }}>{job.salary}</div>
                      <span style={{ fontSize:11,background:T.greenL,color:T.green,fontWeight:700,padding:"2px 8px",borderRadius:100 }}>● Live</span>
                    </div>
                    <button className="btn btn-ghost btn-sm" onClick={()=>{setFilterJob(job.id);setTab("applicants");}}>View Applicants →</button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* SETTINGS TAB */}
        {tab==="settings" && (
          <div className="fade-in">
            <h1 style={{ fontFamily:"'Fraunces',serif",fontSize:26,fontWeight:800,marginBottom:24 }}>Account Settings</h1>
            <div className="card" style={{ padding:28,maxWidth:500 }}>
              <h3 style={{ fontWeight:700,marginBottom:18 }}>Company Profile</h3>
              <div style={{ display:"flex",flexDirection:"column",gap:14 }}>
                <div><label>Company Name</label><input className="input" defaultValue={user.company||"My Company"} /></div>
                <div><label>Contact Email</label><input className="input" defaultValue={user.email} /></div>
                <div><label>Industry</label>
                  <select className="input"><option>Technology</option><option>Finance</option><option>Marketing</option><option>Healthcare</option><option>Other</option></select>
                </div>
                <div><label>Subscription Plan</label>
                  <div style={{ padding:"12px 14px",background:T.primaryL,border:`1.5px solid ${T.primary}30`,borderRadius:10,fontSize:14,fontWeight:600,color:T.primary }}>Growth Plan · ₱4,999/month</div>
                </div>
              </div>
              <button className="btn btn-primary" style={{ marginTop:20 }} onClick={()=>notify("Settings saved!")}>Save Changes</button>
            </div>
          </div>
        )}
      </div>

      {/* Applicant detail modal */}
      {selectedApp && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setSelectedApp(null)}>
          <div className="modal" style={{ maxWidth:520 }}>
            <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20 }}>
              <div style={{ display:"flex",gap:12,alignItems:"center" }}>
                <div style={{ width:50,height:50,borderRadius:"50%",background:`linear-gradient(135deg,${T.primary},#0d8f66)`,color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:900,fontSize:20 }}>{selectedApp.name[0]}</div>
                <div>
                  <h2 style={{ fontFamily:"'Fraunces',serif",fontSize:20,fontWeight:800 }}>{selectedApp.name}</h2>
                  <div style={{ fontSize:13,color:T.inkL }}>{selectedApp.email}</div>
                </div>
              </div>
              <button onClick={()=>setSelectedApp(null)} style={{ background:"none",border:"none",cursor:"pointer",color:T.inkL,fontSize:24 }}>×</button>
            </div>

            <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:18 }}>
              {[{l:"Position",v:selectedApp.jobTitle},{l:"School",v:selectedApp.school||"—"},{l:"GPA",v:selectedApp.gpa||"—"},{l:"Applied",v:selectedApp.appliedAt}].map((f,i)=>(
                <div key={i} style={{ background:T.surface,border:`1px solid ${T.border}`,borderRadius:10,padding:"10px 14px" }}>
                  <div style={{ fontSize:10,fontWeight:700,color:T.inkL,textTransform:"uppercase",letterSpacing:".07em",marginBottom:4 }}>{f.l}</div>
                  <div style={{ fontSize:14,fontWeight:600,color:T.ink }}>{f.v}</div>
                </div>
              ))}
            </div>

            {selectedApp.skills && <div style={{ marginBottom:14 }}><label>Skills</label><div style={{ display:"flex",flexWrap:"wrap",gap:6 }}>{selectedApp.skills.split(",").map((s,i)=><span key={i} className="tag">{s.trim()}</span>)}</div></div>}
            {selectedApp.education && <div style={{ marginBottom:14 }}><label>Education</label><p style={{ fontSize:13,color:T.inkL }}>{selectedApp.education}</p></div>}
            {selectedApp.experience && <div style={{ marginBottom:18 }}><label>Experience</label><p style={{ fontSize:13,color:T.inkL,lineHeight:1.7,whiteSpace:"pre-line" }}>{selectedApp.experience}</p></div>}

            {/* AI Score */}
            <div style={{ background:T.primaryL,border:`1.5px solid ${T.primary}20`,borderRadius:12,padding:16,marginBottom:20 }}>
              <div style={{ fontSize:13,fontWeight:700,color:T.primary,marginBottom:10 }}>🤖 AI Screening — TransCareers</div>
              {aiLoading ? (
                <div style={{ display:"flex",gap:8,alignItems:"center" }}>
                  <div className="spin" style={{ width:14,height:14,border:"2px solid rgba(10,110,78,.3)",borderTopColor:T.primary,borderRadius:"50%" }} />
                  <span style={{ fontSize:13,color:T.primary }}>Analyzing candidate profile…</span>
                </div>
              ) : <p style={{ fontSize:13,color:T.primaryD,lineHeight:1.8,whiteSpace:"pre-wrap" }}>{aiScore}</p>}
            </div>

            {/* Status buttons */}
            <div style={{ marginBottom:20 }}>
              <label>Update Status</label>
              <div style={{ display:"flex",gap:8,flexWrap:"wrap" }}>
                {Object.entries(STATUS_META).map(([k,v])=>(
                  <button key={k} onClick={()=>{onUpdateStatus(selectedApp.id,k);setSelectedApp({...selectedApp,status:k});notify(`Marked as ${v.label}`);}}
                    className="btn btn-xs" style={{ background:selectedApp.status===k?v.color:"transparent",color:selectedApp.status===k?"#fff":v.color,border:`1.5px solid ${v.color}`,borderRadius:100,padding:"6px 14px",fontSize:12,fontWeight:700 }}>
                    {v.label}
                  </button>
                ))}
              </div>
            </div>

            <button className="btn btn-primary" style={{ width:"100%",padding:"12px" }} onClick={()=>setSelectedApp(null)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
